/**
 * NewJourneys — the unified Journey designer (route `newjourneys`, rail
 * "New journeys"). The UX is the Gold Master prototype `New Journeys.dc.html`
 * (phases 2–5) shipped as designed: journeys list (stats, search, filters,
 * sort, list/cards, bulk bar), the Pages + Flow designer, the Experience
 * Designer, the template picker and the page-type registry picker. Every style
 * literal below is the prototype's — the operator ruled this surface does NOT
 * use the design system.
 *
 * What is REAL here (2026-08-19 drop):
 *  • List rows are DERIVED from the tenant's own objects — queues (listQueues),
 *    events (useEvents) and the appointment catalogue (getServiceCatalogue).
 *    Opening a row edits that object's journey; the row links back to the
 *    object's own screen.
 *  • Attachments are PICKED from the real libraries: question groups
 *    (useQuestions), surveys (listSurveys), messages (useMessaging), coupons
 *    (useDiscounts), ticket groups (useTicketGroups). Every picker also links
 *    to the canonical editor (plan ruling ④).
 *  • Appointment slots come from the journey availability op
 *    (useJourneyAvailability) — never invented; an empty range says so.
 *  • Edits persist in two places, by ruling: the fields the OWNING object really
 *    has go onto that object (a queue takes name / hero image / services through
 *    `updateQueue`, so a designer rename shows up in the queue console), and
 *    everything with no column anywhere — pages, blocks, flow, attachments,
 *    experiences — goes through `newjourneys-api`, the single seam for the six
 *    ops requested in CD-API-REQUEST-newjourneys-documents-2026-08-19.md. Until
 *    that contract lands the seam is a per-tenant local stub, and the surface
 *    says which half went where (see nj-writeback).
 *
 * Legacy queue / event / appointment editors and the old Journeys rail item are
 * untouched (ruling ⑤).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Screen } from "../../shell/chrome";
import {
  useEditorHistory, EditorHistoryButtons, useCanvasWidth, CanvasColumn, CanvasWidthBar, CanvasEdge,
  SavedBadge, useDragAutoScroll,
} from "../shared/editor-shell";
import { OverlayPortal } from "../../shell/Overlay";
import { RowActions } from "../../shell/composites";
import { useFolderLibrary, FolderShellFrame, FolderShellBars, FolderShellOverlays } from "../shared/library-shell";
import { journeysSectionTabs } from "../../shell/list-composites";
import { useNav } from "../../shell/router";
import { useTenant, getUser } from "../../shell/access";
import { useApprovals } from "../approvals/approvals-store";
import { getServiceCatalogue, type ServiceCatalogue } from "../workforce/wf-api";
import { listQueues } from "../queues/queues-api";
import { useEvents } from "../events/useEvents";
import { useQuestions } from "../questions/useQuestions";
import { useMessaging } from "../messaging/useMessaging";
import { useFlows, flowStats } from "../messaging/useFlows";
import { useDiscounts } from "../promos/useDiscounts";
import { useStaff } from "../staff/useStaff";
import { useResources } from "../resources/useResources";
import { useServices, ServicesStore } from "../services/services-data";
import {
  listQueueTypes, listMessageBoards, getQueueBoard, sendQueueMessage, broadcastQueueMessage, mintBoardDisplayUrl,
  type QueueType, type QueueSummary, type MessageBoard, type QueueEntry, type QueueBoard as QueueBoardState,
} from "../queues/queues-api";
import { Q_DAYS, TIME_OPTS, hmLabel12, qHoursLabel } from "../queues/queue-hours";
import { BOARD_BLOCKS, BoardQueueCard, boardColors, boardPublicUrl, boardBlockUid, defaultBoardBlocks, useBoardPager, type BoardBlockRec, type BoardNameFormat } from "../queues/board-blocks";
import { CatalogPicker, resolveSelection, describeSelection, type PickerItem, type PickerGroup, type PickerSel } from "./catalog-picker";
import { publicUrl, useTenantPublicConfig } from "../../lib/public-url";
import { copyText } from "../../lib/copy-text";
import { QrCode, downloadQrPng } from "../../shell/QrCode";
import { NumberField } from "../../lib/NumberField";
import { useTicketGroups, tgMoney } from "../tickets/ticket-groups-store";
import { useTicketTypes } from "../tickets/ticket-types-store";
import { listSurveys, validateAddress, suggestAddresses, listGuests, listGuestSegments, messageGuest } from "@v0y4ge/api-client";
import { useLocations } from "../locations/useLocations";
import { studioRequestOptions } from "../../lib/studio-config";
import { tzShortLabel } from "../../lib/datetime";
import { REPEAT_LABELS_ALL } from "../events/event-config-types";
import { useJourneyAvailability } from "./availability-data";
import { listJourneyStore, putJourneyDoc, deleteJourneyDoc, putExperience, deleteExperience, NJ_STUBBED } from "./newjourneys-api";
import { writeBackDoc, WRITES_BACK, OWNER_NOUN } from "./nj-writeback";
import { stampSave, createdCell, modifiedCell, type AuditCell } from "./nj-audit";
import { attachJourney, needsAttach, attachBlockers, ATTACH_NOUN, publishJourneyPages, type AttachResult } from "./nj-attach";
import {
  loadEventDoc, eventReadiness, patchEventBasics, patchEventDetails, patchEventSchedule,
  patchEventTickets, patchEventRegistration, patchEventGuestList, patchEventMessaging, publishEventRecord,
  takeEventRecordOffline, cancelEventRecord, attachEventTicketGroupIds,
  patchQueueConfig, setQueueLifecycle, queueReadiness, saveBoardConfig, deleteBoardConfig, boardReadiness,
  type GuestEntry,
} from "./nj-binding";
import { Input, Toggle, Dropdown, ChoiceTile, DatePicker, TimeZonePicker, Icon } from "@v0y4ge/jrni-ui";
import { JourneyFlowBuilder } from "./flow/JourneyFlowBuilder";
import { OSRail, OSCanvas, osLabel, osSeedConfig } from "./onestop-canvas";
import { BlockCanvas, BLOCK_ADD_DND, BLOCK_MOVE_DND } from "./block-canvas";
import { OneStopInspector, type OneStopLibs } from "./onestop-inspector";
import { oneStopSkeletonBlocks } from "./onestop-seed";
import { mintBlockId, slugFromTitle } from "./onestop-page-api";
import { withPreviewData } from "./onestop-preview-data";
import { tzContext } from "../../lib/datetime";
import {
  qfVocabFor, qfFlatten, qfMapAll,
  qfMkPage, qfMkComms, qfMkSurvey, qfMkWait, qfMkDecision, qfMkExit,
  type QFFlow, type QFStep,
} from "./flow/jf-model";
import { journeyPages } from "./flow/journey-pages";
import { blockVariant } from "../shared/block-registry";
import {
  ApptSetupPanel, apptReadiness, APPT_SETUP_LABEL, APPT_SETUP_DEFAULTS,
  type ApptView, type ApptLibs,
} from "./appt-setup";
import { njFlowOf } from "./flow/nj-flow-migrate";
import {
  REG, regOf, INV, blocksFor, CDEF, NETS, BLOCK_DEF, BLOCK_CFG, CORE_DEF, CORE_CFG,
  mapContent, findContentBlock, addToCell, blockCells,
  ATTACH_TYPES, LIB_META, TYPE_TINT, TYPE_GLYPH, HUB_VERB, docType, njUid, mkPage, hoistHero,
  docFromQueue, docFromEvent, docFromApptGroup, docFromBoard, templateDoc,
  stepNavFor, COMMIT_LABEL_BY_TYPE,
  type Doc, type NJPage, type Block, type Exp, type CfgRow, type EventLike,
  type QueueConfigLike, type BoardConfigLike,
} from "./nj-model";
/* The journey END PAGE (stage 3, 2026-09-01). The registry and the state ops
   are in confirmation-sections.ts; the rail, the canvas and the inspector scope
   are in confirmation-page.tsx — this screen owns neither, it only supplies the
   sample booking and the patcher. NOT the booking confirmation page, which is a
   different feature living in ConfirmationPages.tsx. */
import { csMigrate, csToggle, csTypeOf, csAt, type CSState } from "./confirmation-sections";
import { CSRail, CSCanvas, CSInspector, type CSBooking } from "./confirmation-page";

/* ── Prototype style literals ───────────────────────────────────────── */
const FONT = '"DM Sans", "Helvetica Neue", sans-serif';
const SEC = { fontSize: 10, fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase" as const, color: "#697180", marginBottom: 8 };
const FIELD = { border: "1px solid #D1D4DC", borderRadius: 8, padding: "9px 11px", fontSize: 12, color: "#B5BAC6" };
const INPUT = { width: "100%", boxSizing: "border-box" as const, border: "1px solid #D1D4DC", borderRadius: 6, padding: "8px 10px", fontSize: 12.5, fontFamily: FONT, color: "#333333" };
const NOTE = { border: "1px solid #DCCCFC", background: "#F6F2FE", borderRadius: 8, padding: "8px 10px", fontSize: 10.5, lineHeight: 1.5, color: "#5142C7" };
const TZ_OPTIONS = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Europe/London", "Europe/Paris", "Europe/Zurich", "Asia/Singapore", "Australia/Sydney", "UTC"];
const WARN = { border: "1px solid #F3D9A4", background: "#FFFAED", borderRadius: 8, padding: "9px 11px", fontSize: 11, lineHeight: 1.55, color: "#916D0F" };
const MONO = { fontFamily: "ui-monospace, monospace" };
const glyphBox = (tint: string, color: string, size = 30, fs = 13) => ({ width: size, height: size, borderRadius: size > 30 ? 9 : 8, background: tint, color, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: fs, flex: "none" } as const);
/* The designer header is a COMPACT TOOLBAR, so every control in it sits at the
   design system's dense control height (T.control.sm = 32) — jrni-ui's own
   compact tier, the same one Btn size="sm", Input size="sm" and every select
   trigger use. It was five different heights (28 back, 28 page nav, ~30 the two
   segmented strips, 34 Preview, 40 the Btn-default Undo/Redo), which is what
   made the bar read as misaligned rather than dense. */
const HDR_H = 32;
const PRIMARY = { height: 34, padding: "0 16px", border: "none", borderRadius: 8, background: "#5142C7", color: "#FFFFFF", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" } as const;
const GHOST = { height: 34, padding: "0 14px", border: "1px solid #D1D4DC", borderRadius: 8, background: "#FFFFFF", color: "#333333", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" } as const;

/* One template for the list header AND its rows — they must not drift.
   The name track carries a floor and the widest fr share so it can't be
   squeezed out (its children are nowrap+ellipsis, so a bare `1fr` collapses
   to nothing at narrow widths); the secondary columns are minmax so they give
   their slack back to the name instead of holding fixed width. */
/* Grid: check · Journey · Type · Status · Pages · Created by · Updated · Actions.
   "Created by" (2026-08-27) sits beside Updated so the two halves of the record's
   history read together; both are minmax so they give slack back to the name. */
const LIST_COLS = "34px minmax(190px,2.6fr) minmax(76px,1fr) minmax(68px,0.9fr) 40px minmax(96px,1.1fr) minmax(96px,1fr) 140px";

function ensureFont() {
  if (document.getElementById("nj-dm-sans")) return;
  const l = document.createElement("link");
  l.id = "nj-dm-sans"; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700;9..40,800&display=swap";
  document.head.appendChild(l);
}

const Switch = ({ on, onClick }: { on: boolean; onClick: (e: any) => void }) => (
  <button onClick={onClick} style={{ width: 30, height: 17, borderRadius: 999, border: "none", background: on ? "#5142C7" : "#D1D4DC", position: "relative", cursor: "pointer", flex: "none", padding: 0 }}>
    <span style={{ position: "absolute", top: 2, left: on ? 15 : 2, width: 13, height: 13, borderRadius: 999, background: "#FFFFFF", transition: "left 120ms" }} />
  </button>
);

type Route = "home" | "designer" | "experience";
type LibKind = "questions" | "survey" | "promo" | "message" | "tickets";

/** Measure a box with a CALLBACK ref rather than an effect (2026-09-02).
 *  An effect keyed on "the overlay is open" fires before a React portal's own
 *  container effect has run, so `ref.current` is still null, the observer is
 *  never attached, and the measurement stays at its initial zero for the life of
 *  the overlay — which left the Preview board painting at scale(1) and cropped
 *  on all four sides. A callback ref runs when the node actually arrives. */
function useFitBox() {
  const [fit, setFit] = useState({ w: 0, h: 0 });
  const roRef = useRef<ResizeObserver | null>(null);
  const attach = useCallback((node: HTMLDivElement | null) => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (!node) { setFit({ w: 0, h: 0 }); return; }
    const read = () => { const r = node.getBoundingClientRect(); if (r.width && r.height) setFit({ w: r.width, h: r.height }); };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(node);
    roRef.current = ro;
  }, []);
  return { fit, attach };
}

/** How many columns a board splits its queues across (2026-09-02, operator).
 *  Kept DELIBERATELY few: card height is a function of column width, so more
 *  columns is not a way to fit more queues — narrowing cards to 226px wrapped
 *  their block content and tripled their height. Wide cards stay short, and the
 *  queues that do not fit on one screen cycle instead (see useBoardPager). */
function boardColumnsFor(n: number, landscape: boolean) {
  if (n <= 0) return 1;
  if (landscape) return Math.min(n, 3);
  return n <= 6 ? 1 : 2;
}

export default function NewJourneys() {
  const nav = useNav();
  const tenant = useTenant() as any;
  const slug = tenant?.slug ?? "default";
  useEffect(ensureFont, []);

  /* ── Real objects ───────────────────────────────────────────────── */
  const [queues, setQueues] = useState<any[]>([]);
  const [queuesLoaded, setQueuesLoaded] = useState(false);
  useEffect(() => {
    let live = true;
    listQueues().then((p: any) => { if (live) { setQueues(p?.data ?? []); setQueuesLoaded(true); } }).catch(() => { if (live) setQueuesLoaded(true); });
    return () => { live = false; };
  }, [slug]);
  const { events } = useEvents();
  const [cat, setCat] = useState<ServiceCatalogue | null>(null);
  useEffect(() => { let live = true; getServiceCatalogue().then((c) => { if (live) setCat(c); }).catch(() => {}); return () => { live = false; }; }, [slug]);

  const questions = useQuestions();
  const messaging = useMessaging() as any;
  const promos = useDiscounts() as any;
  const ticketGroups = useTicketGroups();
  const allTicketTypes = useTicketTypes();
  const { members: staffMembers } = useStaff();
  const { resources: resourceRecs } = useResources();
  useServices();
  useTenantPublicConfig();
  const [queueTypes, setQueueTypes] = useState<QueueType[]>([]);
  const [boards, setBoards] = useState<MessageBoard[]>([]);
  const [boardsLoaded, setBoardsLoaded] = useState(false);
  useEffect(() => {
    let live = true;
    listQueueTypes().then((r: any) => { if (live) setQueueTypes(r?.types ?? []); }).catch(() => {});
    listMessageBoards().then((r: any) => { if (live) { setBoards(r?.boards ?? r?.data ?? []); setBoardsLoaded(true); } }).catch(() => { if (live) setBoardsLoaded(true); });
    return () => { live = false; };
  }, [slug]);
  const flows = useFlows();
  const allFlows = flows.all();
  const [surveys, setSurveys] = useState<any[]>([]);
  useEffect(() => {
    let live = true;
    /* `listSurveys` is absent from the stubbed api-client some preview builds
       bundle against — calling an undefined export would throw at mount. */
    if (typeof listSurveys !== "function") return;
    (listSurveys as any)({}).then((r: any) => { if (live) setSurveys(r?.surveys ?? r?.data ?? (Array.isArray(r) ? r : [])); }).catch(() => {});
    return () => { live = false; };
  }, [slug]);

  const qGroups = questions.groups() as any[];
  const messages = (messaging.all?.() ?? []) as any[];
  /* Two different incentives, and the library already knows it: a COUPON is a
     code the student enters, a PROMO applies automatically. They are separate
     tabs in the promos library and separate fields on a service or an event, so
     they are separated here rather than offered as one undifferentiated list. */
  const allDiscounts = (promos.all?.() ?? []) as any[];
  const coupons = allDiscounts.filter((d: any) => d.type !== "promo");
  const autoPromos = allDiscounts.filter((d: any) => d.type === "promo");

  /* ── Documents: derived from real objects, overlaid with saved edits ── */
  const derived = useMemo(() => {
    const out: Record<string, Doc> = {};
    /* `ownerFields` forwards whatever created/modified fields the owning record
       carries, so a derived row nobody has opened in the designer can still say
       who made the queue / event / catalogue entry it came from. No op returns
       them today (CD-API-REQUEST-journey-audit-2026-08-27) — the forward is
       deliberately blind so the column lights up the day they ship, with no
       further UI change; nj-audit ignores anything unparseable. */
    const ownerFields = (o: any) => ({
      createdAt: o?.createdAt ?? o?.created_at, createdBy: o?.createdBy ?? o?.created_by,
      updatedAt: o?.updatedAt ?? o?.updated_at, updatedBy: o?.updatedBy ?? o?.updated_by,
    });
    queues.forEach((q: any) => { const r = docFromQueue({ id: q.id, name: q.name, status: q.status, locationName: q.locationName, services: q.services, joinCode: q.joinCode, avgWaitMins: q.avgWaitMins, waiting: q.waiting, ...ownerFields(q) }); out[r.key] = r.doc; });
    (events ?? []).forEach((e: any) => { const r = docFromEvent({ id: e.id, title: e.title, status: e.status, date: e.date, time: e.time, location: e.location, ticketTypeIds: e.ticketTypeIds, isPaid: e.isPaid, description: e.description, bookingQuestions: e.bookingQuestions, surveys: e.surveys, ...ownerFields(e) }); out[r.key] = r.doc; });
    const bookable = ((cat?.services ?? []) as any[]).filter((s) => s.durationMin);
    const lobs = (cat?.lobs ?? []) as any[];
    if (lobs.length) {
      lobs.forEach((l) => {
        const ids = bookable.filter((s) => s.lob === l.id).map((s) => s.id);
        if (ids.length) { const r = docFromApptGroup({ id: l.id, name: l.name, serviceIds: ids, ...ownerFields(l) }); out[r.key] = r.doc; }
      });
    } else if (bookable.length) {
      const r = docFromApptGroup({ id: "all", name: "Booking", serviceIds: bookable.map((s) => s.id) });
      out[r.key] = r.doc;
    }
    /* Display boards are journeys too (2026-08-24): one page, no customer
     * input, configuration in the boards library. */
    boards.forEach((b) => {
      const r = docFromBoard({
        id: b.id, name: b.name, queueIds: b.queueIds ?? [], theme: b.theme, message: b.message,
        queueNames: (b.queueIds ?? []).map((id) => queues.find((q: any) => q.id === id)?.name).filter(Boolean) as string[],
        /* `updatedAt: b.updatedAt` was here BEFORE the spread and was therefore
           dead — `ownerFields` sets `updatedAt` too and wins (2026-09-08).
           Removed rather than reordered: ownerFields' version is the one that
           has been running, and it also reads the snake_case form. */
        ...ownerFields(b),
      });
      out[r.key] = r.doc;
    });
    return out;
  }, [queues, events, cat, boards]);

  const [overlays, setOverlays] = useState<Record<string, Doc> | null>(null);
  const [exps, setExps] = useState<Record<string, Exp>>({});
  useEffect(() => {
    let live = true;
    /* Clear FIRST, synchronously, so a tenant switch cannot leak. The merge
       below keeps anything written since the read started — which is the fix —
       but "anything in state" must not include the previous tenant's documents,
       and this effect re-runs on `slug`. Replacing used to do this clearing as a
       side effect of throwing everything away. */
    setOverlays(null);
    setExps({});
    listJourneyStore(slug).then((env) => {
      if (!live) return;
      /* MERGE, do not replace (operator, 2026-09-02: a journey created in the
         designer did not appear in the list).
         This read starts on mount and resolves whenever the store answers. A
         journey created before it lands was written into `overlays` by
         `saveDoc` — and `setOverlays(env.documents)` threw it away, so the new
         row vanished from the list and the designer was editing a document
         nothing would ever show. Autosave was working; this discarded its
         result.
         Anything already in state WINS over the fetched copy: a write that has
         happened is newer than a read that started before it. The fetched set
         still fills in everything this session has not touched. */
      setOverlays((prev) => ({ ...((env.documents ?? {}) as Record<string, Doc>), ...(prev ?? {}) }));
      setExps((prev) => ({ ...((env.experiences ?? {}) as Record<string, Exp>), ...prev }));
    });
    return () => { live = false; };
  }, [slug]);

  /* An overlay replaces its derived row wholesale \u2014 with ONE exception: a
     document stored before author stamps existed (2026-08-27) has no audit
     block, and dropping the derived row's owner-sourced one would lose the only
     history that row can report. A designer block always wins. */
  const docs = useMemo(() => {
    const merged: Record<string, Doc> = { ...derived };
    Object.entries(overlays ?? {}).forEach(([k, o]) => {
      merged[k] = o.audit ? o : { ...o, audit: derived[k]?.audit };
    });
    return merged;
  }, [derived, overlays]);
  const docKeys = Object.keys(docs);

  /* ── UI state ───────────────────────────────────────────────────── */
  /** Drag payload for palette → column drops (stage 4). A custom type rather
   *  than text/plain, so an unrelated text drag can't be read as a block. */
  /* Payload names come from block-canvas now — one definition, so a palette and
     a canvas cannot disagree about what a drag is carrying. */
  /** Drag payload for reordering a block that is ALREADY on the page. A second
   *  type rather than a flag inside the first, because the two drags accept
   *  different drops: a palette drag may land in a column, a move may not
   *  (a column's children are edited in place, and a move across that boundary
   *  would silently change which array owns the block). */

  const [ui, setUi] = useState({
    route: "home" as Route, curDoc: "", curExp: "" as string, fromExp: "" as string,
    view: "pages" as "pages" | "flow", cur: 0, sel: null as string | null, selStep: null as string | null,
    /** Selected SECTION on a confirmation page. Its own slot beside `sel`
     *  because a section is not a block: it has no uid in `content`, and the
     *  two inspectors are different scopes. */
    selSection: null as string | null,
    tplOpen: false, typePicker: null as { at: number | null } | null, addMenu: null as number | null,
    typesPicker: false,
    lib: null as LibKind | null, expPicker: null as null | "anchor" | "attach", expPickerTab: "all",
    simCalled: false, dragPg: null as number | null, dragOver: null as number | null,
    dragStep: null as string | null, dragOverStep: null as string | null,
    listQuery: "", listFilter: "all", listType: "all", listSort: "updated", listView: "list" as "list" | "grid", listSel: [] as string[],
    rowMenu: null as string | null, renameKey: "", renameVal: "",
    menuPos: null as null | { right: number; top: number | null; bottom: number | null },
    setupView: null as null | "basics" | "details" | "schedule" | "tickets" | "guests" | "messaging" | "publish"
      | "qsetup" | "qhours" | "qstaffing" | "qresources" | "qservices" | "boardcfg" | "onestopcfg"
      | "aservices" | "aplaces" | "awindow" | "aperservice" | "adetails" | "amessaging" | "registration",
    boardSel: null as string | null,
    /** How the lobby TV is MOUNTED (2026-09-02, operator). A board is always
     *  full screen, so the only real variable is the screen's orientation:
     *  landscape for a TV on a wall bracket, portrait for one turned on its
     *  side in an entrance. Landscape is the default because it is what almost
     *  every mounted display is. Designer-local — the published board reads the
     *  real screen, so this is a check that the layout holds, not a property of
     *  the board. */
    boardOrientation: "landscape" as "landscape" | "portrait",
    /** The editor pane. Open by default — an author who selected a block wants to
     *  edit it — and minimizable to a tab, because on a narrow window the page
     *  matters more than the form. */
    inspOpen: true,
    /** "‹containerUid›:‹cellIndex›" while a block is dragged over a column. */
    dropCell: null as string | null,
    /** Top-level insertion point while dragging — the index in the page's
     *  content that the dragged block would land at, or null when the pointer
     *  is not over the page body. */
    /** The block being dragged, so it can dim and so a drop onto its own
     *  position is a no-op rather than a pointless history entry. */
    /** Hovered block — drives the remove affordance. Selected OR hovered, so a
     *  block can be removed without selecting it first. */
    /** Sample service previewed in a page's runtime slot, remembered per page
     *  (operator ruling 2026-08-31) — page uid → catalogue service id. */
    sampleByPage: {} as Record<string, string>,
    viewportByPage: {} as Record<string, "desktop" | "mobile">,
    pagesOpen: false,
    /* Preview (2026-08-25) — the journey's pages as a customer sees them, with
       no authoring chrome. Its own device toggle, so entering preview never
       changes the width the author was editing at. */
    previewOpen: false,
    /* Preview-only viewport (2026-09-02, operator). Seeded from the page when
       preview opens, then independent: switching device in preview asks "how
       does this look on a phone", which is a question about the preview, not an
       edit to the page. It is never written back to `page.viewport`, so the
       canvas behind the scrim stays at whatever width the author was working
       at. */
    previewViewport: "desktop" as "desktop" | "mobile",
  });
  const set = (patch: Partial<typeof ui>) => setUi((u) => ({ ...u, ...patch }));
  /* The library modals are transient: leaving the Tickets tab or the designer
   * must not leave one mounted over whatever opens next. */
  useEffect(() => {
    if (ui.typesPicker && (ui.route !== "designer" || ui.setupView !== "tickets")) setUi((u) => ({ ...u, typesPicker: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.route, ui.setupView]);
  /* The editor overlay reserves its width only when the row can afford it.
     Measured with a ResizeObserver rather than a layout effect on every commit:
     a per-commit measure can OSCILLATE here, because the reservation changes the
     canvas pane's padding, which reflows its content, which can add or remove a
     scrollbar, which changes the measured width, which flips the reservation
     back — React error #185, maximum update depth. The observer only fires on a
     real box change, and the 12px tolerance below swallows a scrollbar's worth
     of jitter so the decision cannot chatter across its own threshold. */
  const [rowW, setRowW] = useState(0);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const rowRoRef = useRef<ResizeObserver | null>(null);
  const designerRowRefCb = useCallback((el: HTMLDivElement | null) => {
    if (rowRoRef.current) { rowRoRef.current.disconnect(); rowRoRef.current = null; }
    rowRef.current = el;
    if (!el) return;
    const read = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      /* Tolerance, not equality: without it a 15px scrollbar appearing is a
         width change, and the width change is what flips the reservation. */
      if (w) setRowW((prev) => (Math.abs(prev - w) < 12 ? prev : w));
    };
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(read);
      ro.observe(el);
      rowRoRef.current = ro;
    }
    read();
  }, []);
  useEffect(() => () => { if (rowRoRef.current) rowRoRef.current.disconnect(); }, []);
  const INSP_MAX = 420;
  /** Can the row afford the editor BESIDE the canvas? 420 is the floor — below
   *  it the preview stops being a preview. */
  const reserveInsp = rowW > 0 && rowW - 212 - 40 - INSP_MAX >= 420;
  /** Never wider than what leaves a readable strip of canvas beside it. */
  const inspW = rowW > 0 ? Math.max(260, Math.min(INSP_MAX, rowW - 212 - 300)) : INSP_MAX;
  /* Selecting a block opens the editor if it was minimized (operator,
   *  2026-09-02). Applies to every journey type: the designer's own blocks, a
   *  confirmation page's sections and a One-Stop page's blocks all set the same
   *  two slots.
   *
   *  This reverses an earlier call not to re-open on click, and the distinction
   *  is why. That call was about a stray click on the canvas; CHOOSING a block is
   *  a statement that you want to edit it, and an editor that stays shut then is
   *  a control that ignored you.
   *
   *  Keyed on the selection, NOT on `inspOpen`, which is what keeps the earlier
   *  concern intact: minimizing the panel while a block stays selected does not
   *  re-run this, so the panel stays shut until you select something ELSE. And
   *  DESELECTING never closes it — an author who cleared a selection has not
   *  asked for the panel to go away. */
  useEffect(() => {
    /* Only where the editor sits BESIDE the canvas. Where it overlays, opening
       on selection hides the very thing that was selected — and the canvas
       carries the per-block and per-section move/remove controls, so it would
       hide the controls the selection exists to reach. */
    if (!reserveInsp) return;
    if (ui.sel !== null || ui.selSection !== null) setUi((u) => (u.inspOpen ? u : { ...u, inspOpen: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.sel, ui.selSection, reserveInsp]);

  const [toast, setToast] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToast(msg);
    window.clearTimeout((showToast as any)._t);
    (showToast as any)._t = window.setTimeout(() => setToast(null), 2800);
  };

  /* ── Persistence (the one seam) ─────────────────────────────────── */
  /* Overlay first (it holds pages, flow and attachments — nothing else has a
     column for those), then push the fields the OWNING object really has onto
     that object: a queue takes name / hero / services through updateQueue, so a
     rename here shows up in the queue console. See nj-writeback for why an event
     and an appointment group can't take a write yet. */
  const saveDoc = (key: string, doc: Doc) => {
    /* Every write to a journey passes through here, so this is the one place the
       author stamps are minted: first save records the creator, every save
       records the last modifier (nj-audit). A derived row's owner-sourced block
       is replaced by a designer block the moment someone saves here — which is
       correct: from then on a person, not the owning record, is the last editor. */
    const stamped: Doc = { ...doc, audit: stampSave(doc.audit) };
    setOverlays((o) => ({ ...(o ?? {}), [key]: stamped }));
    void putJourneyDoc(slug, key, stamped);
    if (!WRITES_BACK(stamped.origin?.kind)) return;
    void writeBackDoc(stamped).then((r) => { if (r.wrote.length) showToast(`Saved onto the ${OWNER_NOUN[stamped.origin?.kind ?? ""] ?? "object"} — ${r.wrote.join(", ")} updated on the object itself`); });
  };
  /* Shut the editor when the row stops being able to afford it. Once, on the
     transition, so a deliberate open on a narrow row is not fought by this. */
  const affordedRef = useRef(true);
  useEffect(() => {
    if (rowW === 0) return;
    if (affordedRef.current && !reserveInsp) setUi((u) => (u.inspOpen ? { ...u, inspOpen: false } : u));
    affordedRef.current = reserveInsp;
  }, [reserveInsp, rowW]);
  /* Undo / redo (stage 3) — the shared editor shell's stack, over the DOCUMENT
     only: pages, blocks and flow. Setup writes to the event or queue RECORD
     through its own ops, which this stack cannot reverse, so it is DISABLED
     while a Setup view is open rather than silently reversing a block edit the
     operator made ten minutes ago in a different view (operator ruling). */
  const history = useEditorHistory<Doc>({
    disabled: !!ui.setupView,
    clone: (d) => JSON.parse(JSON.stringify(d)) as Doc,
  });
  const patchDoc = (fn: (d: Doc) => Doc) => {
    const cur = docs[ui.curDoc];
    if (!cur) return;
    history.push(cur);
    saveDoc(ui.curDoc, { ...fn(cur), edited: true });
  };
  /** Restoring is a plain save of the recovered document — it must NOT go
   *  through patchDoc, or undo would push the state it is replacing. */
  const restoreDoc = (next: Doc) => { if (ui.curDoc) saveDoc(ui.curDoc, { ...next, edited: true }); };
  const patchExp = (fn: (e: Exp) => Exp) => {
    const cur = exps[ui.curExp];
    if (!cur) return;
    const next = fn(cur);
    setExps((x) => ({ ...x, [ui.curExp]: next }));
    void putExperience(slug, ui.curExp, next);
  };

  /* ── Attach — mint the record this journey is about ────────────────
     Until this existed, a designer-created journey stayed a `local:` document
     forever: Setup values were held on it and never reached a record, and the
     wizard was the only surface in the app that could bring an event into
     existence (build plan stage 3's disclosed residual). One button per type,
     each calling that type's own create op — see nj-attach. */
  const [attachBusy, setAttachBusy] = useState(false);
  /* Live-ops message composer (event Publish view). Declared here rather than
     beside the view it drives: the component early-returns a loading shell
     above, so a hook declared further down runs on some renders and not others
     — React error #310. */
  const [evMsg, setEvMsg] = useState<{ tierId: string | null } | null>(null);
  const [evMsgText, setEvMsgText] = useState("");
  const [evMsgSubject, setEvMsgSubject] = useState("");
  const [evMsgChannel, setEvMsgChannel] = useState<"email" | "sms">("email");
  const [evMsgSending, setEvMsgSending] = useState(false);

  const runAttach = async (): Promise<AttachResult | null> => {
    const held = docs[ui.curDoc];
    if (!held || attachBusy) return null;
    /* Fold the LIVE held setup into the document before attaching. Setup edits
       reach `doc.setup` through a 600ms debounce (patchRecord), so a create
       pressed straight after typing read a document that was one debounce
       behind — the event was made with only the fields that happened to have
       landed. The component's own state is the truth at this instant. */
    const d: Doc = held.type === "Queue" ? { ...held, qsetup: { ...(held.qsetup ?? {}), ...q0 } }
      : held.type === "Board" ? { ...held, boardCfg: { ...(held.boardCfg ?? {}), ...b0 } }
      : { ...held, setup: { ...(held.setup ?? {}), ...r0 } as Doc["setup"] };
    const missing = attachBlockers(d);
    if (missing.length) {
      showToast(`Add ${missing.join(" and ")} first — the ${ATTACH_NOUN[d.type ?? ""] ?? "record"} can't be created without it`);
      return null;
    }
    setAttachBusy(true);
    try {
      const r = await attachJourney(d);
      /* Re-key the document onto its new owner. Pages, blocks, flow steps and
         attachments are carried across verbatim — the attach adds an origin,
         it does not rebuild the journey. */
      const next: Doc = { ...d, name: r.origin.label || d.name, origin: r.origin, edited: false, updated: "Just now" };
      /* The held record moves to the new event's key so Setup keeps reading the
         values that were just used to create it — without this the panel reloads
         from the record and briefly shows blanks. */
      if (r.origin.kind === "event" && r.origin.id) setRecords((m) => ({ ...m, [r.origin.id!]: { ...r0, id: r.origin.id! } }));
      const oldKey = ui.curDoc;
      setOverlays((o) => { const x = { ...(o ?? {}) }; delete x[oldKey]; x[r.key] = next; return x; });
      void putJourneyDoc(slug, r.key, next);
      void deleteJourneyDoc(slug, oldKey);
      set({ curDoc: r.key });
      showToast(r.note);
      return r;
    } catch (e) {
      showToast(`Couldn't create the ${ATTACH_NOUN[d.type ?? ""] ?? "record"} — ${e instanceof Error ? e.message : String(e)}. The journey is unchanged.`);
      return null;
    } finally { setAttachBusy(false); }
  };

  const D: Doc | null = docs[ui.curDoc] ?? null;
  const curIdx = D ? Math.min(ui.cur, D.pages.length - 1) : 0;
  const p: NJPage | null = D ? D.pages[curIdx]! : null;
  /** Per-page memory (stage 4) — mobile until the author opts a page into
   *  desktop width, so nothing already open changes width unasked.
   *
   *  A Display Board is the exception and is not a preference: it is a lobby TV,
   *  so it is always full width. Without this it would open at the 440px phone
   *  width with no toggle on screen to escape it. */
  /** The page's canvas width, remembered ON THE DOCUMENT (operator, 2026-09-02)
   *  so an author returns to the page the way they left it. It rides the same
   *  autosave as everything else on the page — `patchPage` → `patchDoc` →
   *  `saveDoc` — rather than a second persistence path of its own.
   *
   *  A Display Board ignores it: that canvas is a lobby TV, always full width,
   *  and with no toggle on screen there would be no way out of a stored
   *  `mobile`. */
  const curViewport: "desktop" | "mobile" =
    /* Inlined rather than reading `isBoardJourney`, which is declared 200 lines
       further down — a `const` read before its declaration is a TDZ crash, not a
       lint warning. */
    (D?.origin?.kind === "board" || D?.type === "Board") ? "desktop"
      : (p?.viewport ?? "mobile");
  const setViewport = (v: "desktop" | "mobile") => patchPage({ viewport: v });
  /* Canvas width from the shared editor shell (stage 3). The Desktop / Mobile
     toggle STAYS — it is what people know, and it keeps its per-page memory —
     and now seeds the width the handles then drag freely from. Never persisted:
     the drag is a check that the layout holds, not a property of the page. */
  /* Canvas width. Two states only, both named for what an author is checking
     rather than for a number: Mobile is 440px — the iPhone 16 Pro Max's logical
     width, the widest phone worth designing against — and Desktop is however wide
     the designer's own window is, because "does this hold on a big screen" is a
     question about THEIR screen, not about 720.

     The width pills and the drag handles are gone (operator, 2026-09-02): an
     arbitrary pixel width is a number nobody was checking against anything, and
     two controls for one decision made the toggle look like a preset rather than
     the answer. `useCanvasWidth` re-seeds whenever `initial` changes, so the
     RESTORED 2026-09-02 (operator): the width presets and the drag handles are
     back. Removing them left Desktop/Mobile as the only control, and Desktop
     means "as wide as this pane" — so there was no way to check a layout at any
     width in between, and no way to sweep through the breakpoints and watch the
     page reflow. The toggle still seeds the width and keeps its per-page memory;
     the presets jump to a named width, and the two bars either side of the
     canvas drag it freely. The drag is never persisted — it is a check that the
     layout holds, not a property of the page. */
  const pageWidth = useCanvasWidth({ initial: curViewport === "desktop" ? 4000 : 440, min: 320, max: 4000 });
  /* The width a block should LAY OUT against (2026-09-02, operator). Responsive
     blocks read this rather than the canvas width directly, because the preview
     overlay renders through the same block renderer: while it is open the phone
     preview must stack its columns even though the canvas behind it is wide. */
  const renderWidth = ui.previewOpen ? (ui.previewViewport === "desktop" ? 1280 : 440) : pageWidth.measured;
  /* Named for what each one IS, so the row reads as widths rather than as
     devices competing with the Desktop/Mobile toggle beside it. */
  const WIDTH_PRESETS = [
    { w: 440, label: "Phone" },
    { w: 768, label: "Tablet" },
    { w: 1180, label: "Laptop" },
    { w: 4000, label: "Full" },
  ];

  /* ── Stage 2/3 — the live event record + readiness (Setup views read this) ── */
  const [records, setRecords] = useState<Record<string, EventLike>>({});
  const eventOriginId = D?.origin?.kind === "event" ? D.origin.id : null;
  useEffect(() => {
    if (!eventOriginId || records[eventOriginId]) return;
    let live = true;
    loadEventDoc(eventOriginId).then((res) => {
      if (live && res) setRecords((r) => ({ ...r, [eventOriginId]: res.record }));
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventOriginId]);
  /** Record state key: the owning event when there is one, otherwise the
   *  journey's own key — so a template-created journey's Setup fields are
   *  editable BEFORE it has an event to write to. */
  const recKey = eventOriginId ?? ui.curDoc;
  const rec: EventLike | null = (recKey ? records[recKey] ?? null : null)
    ?? (!eventOriginId && D?.setup ? (D.setup as EventLike) : null);
  /** Fallback-including record, used everywhere (canvas preview AND Setup
   *  views) — declared here, early, so both can reference it safely. */
  const r0: EventLike = rec ?? ({ id: eventOriginId ?? "", title: D?.name ?? "", status: "draft" } as EventLike);
  /** Readiness reads r0, NOT the nullable rec: on an empty record every check
   *  must come back UNMET. Deriving from `rec` made this `[]`, which made
   *  doneForStep() vacuously true — green dots and an enabled Publish on a
   *  journey with nothing filled in. */
  const readiness = eventReadiness(r0);
  /** Every required field on a Setup step, met. `step` is the Setup tab key —
     the readiness rows used to name the deleted wizard's routes ("create.dates"),
     which meant this screen carried a translation table for a router that no
     longer exists. Extracted 2026-09-02; the rows now speak this screen's own
     vocabulary. */
  const doneForStep = (step: string) => !readiness.some((c) => c.step === step && !c.done);
  const setupDone = {
    basics: doneForStep("basics"), details: doneForStep("details"),
    schedule: doneForStep("schedule"), tickets: doneForStep("tickets"),
  };
  /** Front-loading (decision 3): a new event's Pages group starts collapsed
   *  behind a hint until Basics is done — guidance, never a lock. Chips in the
   *  page preview are the substitute signal, routing to the field that fills them. */
  const isEventJourney = D?.origin?.kind === "event" || D?.type === "Event";
  const pagesCollapsed = !!isEventJourney && !ui.pagesOpen && !setupDone.basics;
  const missingChips = (isEventJourney
    ? [
        !r0.title ? { label: "Event title", to: "basics" } : null,
        !r0.start ? { label: "Date and venue", to: "schedule" } : null,
      ].filter(Boolean)
    : []) as { label: string; to: "basics" | "schedule" }[];
  const patchRecord = (fn: (e: EventLike) => EventLike) => {
    if (!recKey) return;
    const base = records[recKey] ?? (!eventOriginId && D?.setup ? (D.setup as EventLike) : null)
      ?? ({ id: eventOriginId ?? "", title: D?.name ?? "", status: "draft" } as EventLike);
    const next = fn(base);
    setRecords((r) => ({ ...r, [recKey]: next }));
    /* No owning event yet — keep the values in the journey document so they
     * survive reload. Debounced: patchDoc → putJourneyDoc persists the WHOLE
     * document, so writing per keystroke would be one save per character. */
    if (!eventOriginId) {
      if (setupSaveRef.current) window.clearTimeout(setupSaveRef.current);
      const run = () => {
        /* Same as the queue's: an event named only in Setup showed as
           "Untitled event journey" in the list. */
        patchDoc((d) => {
          const typed = String((next as { title?: unknown }).title ?? "").trim();
          const own = (d.name ?? "").trim();
          const rename = typed && (!own || /^untitled/i.test(own));
          return { ...d, setup: next, ...(rename ? { name: typed } : {}) };
        });
        setupSaveRef.current = null;
        flushPendingRef.current = null;
      };
      flushPendingRef.current = () => { if (setupSaveRef.current) { window.clearTimeout(setupSaveRef.current); run(); } };
      setupSaveRef.current = window.setTimeout(run, 600);
    }
  };

  /* ── Queue record — the queue editor's config, same ops (2026-08-24) ──
   * A `queue:` journey OWNS a queue, so every Setup field writes onto that
   * queue through updateQueue (debounced, exactly as the wizard autosaved).
   * A template-created queue journey has no record yet, so its values are
   * held in the document's `qsetup` until one exists. */
  const queueOriginId = D?.origin?.kind === "queue" ? D.origin.id : null;
  const qSummary: QueueSummary | null = queueOriginId ? (((queues as any[]).find((x) => x.id === queueOriginId) as QueueSummary) ?? null) : null;
  const qKey = queueOriginId ?? ui.curDoc;
  const [queueCfgs, setQueueCfgs] = useState<Record<string, QueueConfigLike>>({});
  const qSaveRef = useRef<number | null>(null);
  /**
   * Run any pending Setup write immediately.
   *
   * Setup fields debounce by 700ms before they reach the document (unattached
   * journey) or the record (attached one). Leaving the designer inside that
   * window dropped the last edit — and the header said "Saved", because the
   * DOCUMENT had saved earlier for something else. A queue named and then closed
   * within 700ms lost its name and reported success (operator, 2026-09-02).
   *
   * The pending timer's own closure is what runs, so an early flush writes
   * exactly what the debounce would have — there is no second copy of the
   * payload here to drift from the real one.
   */
  const flushPendingRef = useRef<(() => void) | null>(null);
  const flushSetup = () => { flushPendingRef.current?.(); };
  /* Unmount too: the rail, the browser back button and a closed tab never touch
     the designer's own controls, and a pending write is just as lost. */
  useEffect(() => () => { flushPendingRef.current?.(); }, []);
  const storeCats = ServicesStore.categories() as { id: string; name: string; hue: string }[];
  const storeSvcs = ServicesStore.services() as { id: string; name: string; category?: string | null; listedDuration?: string }[];
  /* The queue editor picked from the studio services store. When that store is
   * empty but the tenant HAS a bookable catalogue (line-of-business shaped),
   * fall back to it so the picker offers the tenant's real services instead of
   * an empty state. */
  const catalogueSvcs = storeSvcs.length ? storeSvcs : ((cat?.services ?? []) as any[]).map((s) => ({ id: String(s.id), name: String(s.name), category: s.lob ? String(s.lob) : null, listedDuration: s.durationMin ? `${s.durationMin} min` : undefined }));
  /* Queue-only services: names a queue already offers that no catalogue object
   * expresses. They are carried by NAME (`name:<name>` ids) so an older queue's
   * list round-trips through the picker instead of being dropped — an edit to
   * an unrelated field must never strip a live queue's services. */
  const QNAME_ID = "name:";
  const queueOnlyNames = [...new Set((queues as any[]).flatMap((qq) => ((qq.services ?? []) as string[])))]
    .filter((n) => n && !catalogueSvcs.some((s) => s.name === n));
  const QUEUE_ONLY_CAT = "__queue_only__";
  const svcCatServices = [
    ...catalogueSvcs,
    ...queueOnlyNames.map((n) => ({ id: QNAME_ID + n, name: n, category: QUEUE_ONLY_CAT as string | null, listedDuration: undefined })),
  ];
  const svcCatCategories = [
    ...(storeCats.length ? storeCats : ((cat?.lobs ?? []) as any[]).map((l, i) => ({ id: String(l.id), name: String(l.name), hue: ["#2D60EB", "#0AA396", "#D61A55", "#916D0F", "#7C3AED", "#1F8A5B"][i % 6]! }))),
    ...(queueOnlyNames.length ? [{ id: QUEUE_ONLY_CAT, name: "Queue-only services", hue: "#8A5B12" }] : []),
  ];
  const qFromSummary = (q: QueueSummary): QueueConfigLike => ({
    id: q.id, name: q.name, typeId: q.typeId, status: q.status,
    locationId: q.locationId, locationName: q.locationName,
    ticketPrefix: q.ticketPrefix ?? "", slaTargetMins: q.slaTargetMins, needed: (q as any).needed ?? 2,
    hours: JSON.parse(JSON.stringify(q.hours ?? [])),
    assignedStaff: [...((q as any).assignedStaff ?? [])],
    /* Selection when saved; else map legacy service NAMES onto catalogue ids —
     * the wizard's own hydration rule. A name with no catalogue object becomes
     * a `name:` entry rather than being dropped, so nothing is lost. */
    serviceSelection: q.serviceSelection?.length
      ? [...q.serviceSelection]
      : ((q.services ?? []).filter(Boolean).map((n) => { const hit = svcCatServices.find((s) => s.name === n); return { type: "service" as const, id: hit ? hit.id : QNAME_ID + n }; })),
    services: [...(q.services ?? [])], resources: [...((q as any).resources ?? [])],
    heroImage: q.heroImage ?? "", journeySource: q.journeySource ?? "", joinCode: q.joinCode,
    messagingFlowId: (q as any).messagingFlowId,
  });
  const qBase = (): QueueConfigLike => queueCfgs[qKey]
    ?? (qSummary ? qFromSummary(qSummary) : null)
    ?? ({ id: "", name: D?.name ?? "", typeId: "quick", ticketPrefix: "", slaTargetMins: 10, needed: 2, hours: [{ days: [0, 1, 2, 3, 4], start: "09:00", end: "17:00" }], assignedStaff: [], serviceSelection: [], services: [], resources: [], heroImage: "", journeySource: "", ...(D?.qsetup ?? {}) } as QueueConfigLike);
  const q0 = qBase();
  /** Ordered selection → flat service names (a category resolves to its
   *  catalogue services, live) — the wizard's rule, unchanged. */
  const resolveServiceNames = (sel: QueueConfigLike["serviceSelection"]): string[] => {
    const names: string[] = [];
    (sel ?? []).forEach((it) => {
      if (it.type === "category") svcCatServices.forEach((s) => { if (s.category === it.id && s.name && !names.includes(s.name)) names.push(s.name); });
      else if (it.id.startsWith(QNAME_ID)) { const n = it.id.slice(QNAME_ID.length); if (n && !names.includes(n)) names.push(n); }
      else { const s = svcCatServices.find((x) => x.id === it.id); if (s?.name && !names.includes(s.name)) names.push(s.name); }
    });
    return names;
  };
  const patchQueue = (fn: (q: QueueConfigLike) => QueueConfigLike) => {
    const base = qBase();
    const next = fn(base);
    const resolved = resolveServiceNames(next.serviceSelection);
    /* Guard: an empty selection only clears the record's services when there was
     * something selected to remove. Otherwise an unrelated edit (a staff row, an
     * hours toggle) could strip a live queue's list. */
    next.services = resolved.length || (next.serviceSelection ?? []).length || (base.serviceSelection ?? []).length
      ? resolved
      : (base.services ?? []);
    setQueueCfgs((m) => ({ ...m, [qKey]: next }));
    if (qSaveRef.current) window.clearTimeout(qSaveRef.current);
    const run = () => {
      if (queueOriginId) {
        void patchQueueConfig(queueOriginId, {
          name: next.name, ticketPrefix: next.ticketPrefix, slaTargetMins: next.slaTargetMins,
          needed: next.needed, hours: next.hours, assignedStaff: next.assignedStaff,
          services: next.services, serviceSelection: next.serviceSelection, resources: next.resources,
          heroImage: next.heroImage || null, journeySource: next.journeySource, messagingFlowId: next.messagingFlowId,
        }).catch(() => showToast("Couldn't save onto the queue — the next edit retries"));
      } else {
        /* The journey's DISPLAY NAME follows the Setup name (operator,
           2026-09-02: "I do not see my queue journey in the list").
           It was there — titled "Untitled queue journey", because `doc.name` and
           `qsetup.name` are different fields and only the second one has an
           input. An author who names a queue "Financial Aid walk-ins" and then
           scans the list for it finds a row called Untitled, which reads exactly
           like the save failed.
           Only while UNATTACHED and only from an untitled name: once a record
           exists the row is the record's, and an author who deliberately renamed
           the journey must not have it overwritten by the queue's name. */
        patchDoc((d) => {
          const typed = (next.name ?? "").trim();
          const own = (d.name ?? "").trim();
          const rename = typed && (!own || /^untitled/i.test(own));
          return { ...d, qsetup: next, ...(rename ? { name: typed } : {}) };
        });
      }
      qSaveRef.current = null;
      flushPendingRef.current = null;
    };
    flushPendingRef.current = () => { if (qSaveRef.current) { window.clearTimeout(qSaveRef.current); run(); } };
    qSaveRef.current = window.setTimeout(run, 700);
  };
  /** The journey's name as an author would recognise it — `doc.name` unless it
   *  is still the template's "Untitled …", in which case whatever they typed in
   *  Setup. Used by the designer header so it agrees with the list row, which
   *  applies the same fallback. */
  const docDisplayName = (() => {
    const own = (D?.name ?? "").trim();
    if (own && !/^untitled/i.test(own)) return own;
    const held = String(
      (D?.setup as { title?: unknown } | undefined)?.title
      ?? (D?.qsetup as { name?: unknown } | undefined)?.name
      ?? (D?.boardCfg as { name?: unknown } | undefined)?.name
      ?? "",
    ).trim();
    return held || own || "Untitled journey";
  })();
  const isQueueJourney = D?.origin?.kind === "queue" || D?.type === "Queue";
  const qType = queueTypes.find((t) => t.id === q0.typeId) ?? null;
  const qChecks = queueReadiness(q0, D?.pages.length ?? 0);
  const qReady = qChecks.every((c) => c.done);

  /* ── Display board record ─────────────────────────────────────────── */
  const boardOriginId = D?.origin?.kind === "board" ? D.origin.id : null;
  const bSummary: MessageBoard | null = boardOriginId ? (boards.find((b) => b.id === boardOriginId) ?? null) : null;
  const bKey = boardOriginId ?? ui.curDoc;
  const [boardCfgs, setBoardCfgs] = useState<Record<string, BoardConfigLike>>({});
  const bSaveRef = useRef<number | null>(null);
  const bFromSummary = (b: MessageBoard): BoardConfigLike => ({
    id: b.id, name: b.name, queueIds: [...(b.queueIds ?? [])], locationId: b.locationId ?? null,
    layout: b.layout, theme: b.theme, showWaitEstimates: b.showWaitEstimates ?? true,
    nameFormat: (b.nameFormat ?? "first-initial") as BoardNameFormat,
    message: b.message ?? "", blocks: b.blocks?.length ? JSON.parse(JSON.stringify(b.blocks)) : defaultBoardBlocks(b.layout ?? "now-serving"),
    updatedAt: b.updatedAt,
  });
  const bBase = (): BoardConfigLike => boardCfgs[bKey]
    ?? (bSummary ? bFromSummary(bSummary) : null)
    ?? ({
      id: `mb_${(ui.curDoc || "new").replace(/[^a-z0-9]/gi, "").slice(-12)}`,
      name: D?.name && D.name !== "Untitled display board" ? D.name : "",
      queueIds: [], locationId: null, theme: "dark", showWaitEstimates: true,
      nameFormat: "first-initial", message: "Welcome — take a seat, we'll text you when it's your turn.",
      blocks: defaultBoardBlocks("now-serving"), ...(D?.boardCfg ?? {}),
    } as BoardConfigLike);
  const b0 = bBase();
  const patchBoard = (fn: (b: BoardConfigLike) => BoardConfigLike) => {
    const next = fn(bBase());
    setBoardCfgs((m) => ({ ...m, [bKey]: next }));
    if (bSaveRef.current) window.clearTimeout(bSaveRef.current);
    const run = () => {
      if (boardOriginId) {
        void saveBoardConfig(next)
          .then(() => listMessageBoards().then((r: any) => setBoards(r?.boards ?? r?.data ?? [])))
          .catch(() => showToast("Couldn't save the board — the next edit retries"));
      } else {
        patchDoc((d) => {
          const typed = (next.name ?? "").trim();
          const own = (d.name ?? "").trim();
          const rename = typed && (!own || /^untitled/i.test(own));
          return { ...d, boardCfg: next, ...(rename ? { name: typed } : {}) };
        });
      }
      bSaveRef.current = null;
      flushPendingRef.current = null;
    };
    flushPendingRef.current = () => { if (bSaveRef.current) { window.clearTimeout(bSaveRef.current); run(); } };
    bSaveRef.current = window.setTimeout(run, 700);
  };
  /* ── Appointment funnel (2026-08-31) ──────────────────────────────
     The type with no record: apptSetup on the DOCUMENT is the store, not a
     holding bag waiting for one. Everything service-scoped stays on the
     catalogue and is referenced. */
  const isApptJourney = D?.origin?.kind === "appointment" || D?.type === "Appointment";
  const a0 = { ...APPT_SETUP_DEFAULTS, ...(D?.apptSetup ?? {}) };
  const patchAppt = (patch: Partial<typeof a0>) =>
    patchDoc((d) => ({ ...d, apptSetup: { ...APPT_SETUP_DEFAULTS, ...(d.apptSetup ?? {}), ...patch } }));
  const aChecks = D ? apptReadiness(a0, D) : [];
  const aReady = aChecks.every((c) => c.done);

  const onestopOriginId = D?.origin?.kind === "onestop" ? D.origin.id : null;
  const isBoardJourney = D?.origin?.kind === "board" || D?.type === "Board";
  /** Does this journey type have a FLOW? A Display Board and a One-Stop page are
   *  single surfaces — nobody walks through them, they arrive and choose — so
   *  there is no sequence to model and no Pages/Flow toggle to show. */
  const isOneStopJourney = D?.origin?.kind === "onestop" || D?.type === "One-Stop";
  const hasFlow = !isBoardJourney && !isOneStopJourney;
  const bChecks = boardReadiness(b0);
  const bReady = bChecks.every((c) => c.done);

  /* ── All Setup/Publish hooks, hoisted together (never split by code that
   *  could throw before render completes — keeps the hook list identical
   *  across renders regardless of what D/rec/readiness turn out to be). ── */  const [addrVerify, setAddrVerify] = useState<"idle" | "checking" | "valid" | "invalid">("idle");
  const [guestDraft, setGuestDraft] = useState<GuestEntry>({ firstName: "", email: "" });
  const [guestSearch, setGuestSearch] = useState("");
  const [guestResults, setGuestResults] = useState<{ firstName: string; lastName?: string; email: string }[]>([]);
  const [guestSearching, setGuestSearching] = useState(false);
  const [segments, setSegments] = useState<{ id: string; name: string; count: number }[]>([]);
  const [publishBusy, setPublishBusy] = useState<null | "publish" | "offline" | "cancel" | "submit">(null);
  const setupSaveRef = useRef<number | null>(null);

  const [tagDraft, setTagDraft] = useState("");
  const [addrSuggestions, setAddrSuggestions] = useState<{ label: string }[]>([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [heroDrop, setHeroDrop] = useState(false);
  const [newCustomDate, setNewCustomDate] = useState("");
  /* Queue/board local UI state (live board poll, message modal). The service
     drag/drop cursors moved into CatalogPicker with the reorder it now owns. */
  const [qHeroDrag, setQHeroDrag] = useState(false);
  const [qBoard, setQBoard] = useState<QueueBoardState | null>(null);
  const [msgTargets, setMsgTargets] = useState<QueueEntry[] | null>(null);
  const [msgText, setMsgText] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  const [bDragType, setBDragType] = useState<string | null>(null);
  /** Auto-scroll while a drag hovers near the canvas's top or bottom edge —
   *  without it a block at the bottom of a long page cannot reach the top, since
   *  getting there means leaving the viewport and ending the drag.
   *
   *  Declared UP HERE with the other hooks, not beside the canvas it attaches
   *  to: everything below the `if (loading) return` is conditional, and a hook
   *  down there runs on some renders and not others — React error #310. */
  const canvasScrollRef = useDragAutoScroll();
  /* Board canvas + Preview measurement (2026-09-02). Callback refs, not effects
     — see useFitBox for why the effect version never attached inside the portal.
     Two separate boxes because both can be mounted at once. */
  const { fit: boardFit, attach: boardFitRef } = useFitBox();
  const { fit: previewFit, attach: previewFitRef } = useFitBox();
  /* Board paging. Declared here with the other hooks because the board view is
     built past this screen's early returns, and a hook behind a return is React
     #310. Keyed off the board config's queue ids, which are known this early. */
  const bColCount = boardColumnsFor(b0.queueIds.length, ui.boardOrientation === "landscape");
  const bPager = useBoardPager(b0.queueIds, bColCount, ui.boardOrientation === "landscape" ? 20 : 16);
  useEffect(() => {
    if (!queueOriginId || q0.status !== "open") { setQBoard(null); return; }
    let live = true;
    const load = () => getQueueBoard(queueOriginId).then((b: any) => { if (live) setQBoard(b ?? null); }).catch(() => {});
    load();
    const t = window.setInterval(load, 10_000);
    return () => { live = false; window.clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueOriginId, q0.status]);
  const approvalsStore = useApprovals();
  const { locations: savedLocations } = useLocations();
  useEffect(() => {
    const term = guestSearch.trim();
    if (!term) { setGuestResults([]); return; }
    let live = true;
    setGuestSearching(true);
    const t = window.setTimeout(() => {
      listGuests({ search: term, limit: 8 } as never).then((res: any) => {
        if (!live) return;
        const raw: Record<string, unknown>[] = res?.guests ?? res?.data ?? [];
        setGuestResults(raw.slice(0, 8).map((r) => ({ firstName: String(r.firstName ?? r.first_name ?? ""), lastName: r.lastName ? String(r.lastName) : undefined, email: String(r.email ?? "") })));
      }).catch(() => setGuestResults([])).finally(() => live && setGuestSearching(false));
    }, 300);
    return () => { live = false; window.clearTimeout(t); };
  }, [guestSearch]);
  useEffect(() => {
    let live = true;
    listGuestSegments().then((res: any) => {
      if (!live) return;
      const raw: Record<string, unknown>[] = res?.segments ?? res?.data ?? [];
      setSegments(raw.map((s) => ({ id: String(s.id ?? ""), name: String(s.name ?? "Segment"), count: Number(s.count ?? 0) })));
    }).catch(() => setSegments([]));
    return () => { live = false; };
  }, []);

  /* The journey's flow, migrated from whatever shape it was stored in. Declared
     HERE, above every reader: the inspector and the library picker rows are
     evaluated earlier in this function body than the flow view is, so declaring
     it beside the flow view left both reading it inside its temporal dead zone
     and the screen threw on mount. */
  const qfFlow: QFFlow = D ? njFlowOf(D) : { trigger: { event: "screen_services" }, steps: [] };
  /** A win-back send anywhere in the tree — what the promo attachment row used
   *  to infer from "flowNo is non-empty", which was true of any branched flow. */
  const flowHasPromo = qfFlatten(qfFlow.steps).some((st) => st.kind === "comms" && st.promo);

  const patchPage = (patch: Partial<NJPage>) => patchDoc((d) => ({ ...d, pages: d.pages.map((pg, i) => (i === curIdx ? { ...pg, ...patch } : pg)) }));
  const patchContent = (fn: (c: Block[]) => Block[]) => patchDoc((d) => ({ ...d, pages: d.pages.map((pg, i) => (i === curIdx ? { ...pg, content: hoistHero(fn(pg.content)) } : pg)) }));

  /* ── Attachments — real library records ─────────────────────────── */
  const at = D?.attach ?? {};
  const attGroup = qGroups.find((g) => g.id === at.groupId) ?? qGroups[0] ?? null;
  /* An EVENT's survey is the event's, held on the record (2026-09-02). Other
     journey types still read the document until their own owner gains the field
     — a queue's survey is per service and already says so.
     No first-in-library fallback, matching the ticket group above: an unattached
     survey has to read as unattached everywhere rather than as the first row in
     the library, or an author sees a survey they never chose. */
  const surveyRefId = isEventJourney ? (r0.surveys ?? [])[0] ?? null : at.surveyId ?? null;
  const attSurvey = surveyRefId ? (surveys.find((s: any) => s.id === surveyRefId) ?? null) : null;
  /* No first-in-library fallback: the tickets page, the ATTACHMENTS chip and the
   * Setup · Tickets tab all read this one value, so an unattached event must say
   * so everywhere rather than inventing a group. */
  const attTickets = ticketGroups.find((g) => g.id === at.ticketGroupId) ?? null;
  /* An EVENT's coupons are the event's, held on the record (2026-09-02) — and
     there can be several, so this is a LIST rather than the single attachment the
     journey document used to hold. Queue and appointment journeys take theirs
     from the service, which the designer already says.
     No first-in-library fallback: an unattached coupon must read as unattached
     rather than as whichever row happens to be first. */
  const promoIds: string[] = isEventJourney
    ? [...(r0.coupons ?? []), ...(r0.promos ?? [])]
    : (at.promoId ? [at.promoId] : []);
  /* Resolved against the WHOLE library: the card summarises both incentives, and
     which kind each one is shows in its own row rather than in the heading. */
  const attPromos = promoIds
    .map((id) => allDiscounts.find((c: any) => c.id === id))
    .filter(Boolean) as any[];
  const attPromo = attPromos[0] ?? null;
  const surveyItemCount = (s: any) => (s?.items?.length ?? s?.questions?.length ?? 0);
  const qName = attGroup?.name || "No question group attached";
  const qCount = attGroup?.items?.length ?? 0;
  const svName = attSurvey?.name || "No survey attached";
  const svCount = surveyItemCount(attSurvey);
  /* One coupon reads as its code; several read as a count, because listing three
     codes in a card this size truncates the third and implies there are only two. */
  const promoCode = attPromos.length === 0 ? "No coupon attached"
    : attPromos.length === 1 ? (attPromo?.code || attPromo?.name || "Untitled coupon")
    : `${attPromos.length} coupons`;
  const promoOff = attPromos.length === 0
    ? (isEventJourney ? "add one in Setup · Tickets" : "pick one from the promos library")
    : attPromos.length === 1
      ? ([attPromo.type, attPromo.value != null ? String(attPromo.value) : null].filter(Boolean).join(" ") || "coupon")
      : attPromos.map((c: any) => c.code || c.name).filter(Boolean).join(" · ");
  const tixName = attTickets?.name || "No ticket group attached";
  const tixTiers = (attTickets?.tiers ?? []) as any[];

  /* ── Setup → page mirrors ─────────────────────────────────────────
   *  Setup OWNS these values; pages read them from here and never keep a copy,
   *  so a page preview cannot disagree with what Setup holds. One derivation
   *  each, shared by the cores, the content blocks and the full-screen preview.
   *
   *  Audit 2026-08-31 found four silent breaks this section closes: the Welcome
   *  page's `eventdetails` block had NO renderer at all (the schedule never
   *  reached the page), Countdown counted down to a hardcoded "04d 02h 14m",
   *  the Tickets page showed only an attached GROUP and never the individual
   *  ticket types Setup selected, and the Contact/Register pages showed a COUNT
   *  of attached booking questions instead of the questions. */
  const setupLoc = (savedLocations as any[]).find((l) => l.id === r0.locationId) ?? null;
  const setupVenue = (r0.locationMode ?? "saved") === "custom" ? (r0.venueName ?? "") : (setupLoc?.name ?? "");
  const setupAddress = (r0.locationMode ?? "saved") === "custom" ? (r0.address ?? "") : (setupLoc?.address ?? r0.address ?? "");
  const asDate = (v?: string | null): Date | null => { if (!v) return null; const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d; };
  const startAt = asDate(r0.start);
  const endAt = asDate(r0.end);
  const sameDay = !!startAt && !!endAt && startAt.toDateString() === endAt.toDateString();
  const DFMT = { weekday: "short", day: "numeric", month: "long", year: "numeric" } as const;
  const TFMT = { hour: "numeric", minute: "2-digit" } as const;
  const setupDateLine = !startAt ? ""
    : (!endAt || sameDay)
      ? startAt.toLocaleDateString(undefined, DFMT)
      : `${startAt.toLocaleDateString(undefined, DFMT)} — ${endAt.toLocaleDateString(undefined, DFMT)}`;
  const setupTimeLine = !startAt ? "" : r0.allDay ? "All day"
    : [`${startAt.toLocaleTimeString(undefined, TFMT)}${endAt ? ` – ${endAt.toLocaleTimeString(undefined, TFMT)}` : ""}`,
       r0.timezone ? tzShortLabel(r0.timezone) : null].filter(Boolean).join(" · ");
  const setupRepeatLine = r0.repeatIndex ? (REPEAT_LABELS_ALL[r0.repeatIndex] ?? "") : "";

  /** Setup · Tickets in full: the attached GROUP's tiers AND the individual
   *  ticket types Setup selected, each carrying its own allocation. The Tickets
   *  page renders this one list, so what a customer would see IS the record. */
  const tixTypeRows = ((r0.ticketTypeIds ?? []) as string[]).map((id) => {
    const t = (allTicketTypes as any[]).find((x) => x.id === id);
    if (!t) return null;
    const cfg = (r0.ticketTypeConfigs ?? {})[id] ?? ({} as { qty?: number; kind?: string; price?: number });
    return { id, from: "type" as const, name: t.name || "Untitled ticket", kind: cfg.kind ?? t.kind ?? null, price: cfg.price ?? t.price, qty: cfg.qty ?? t.defaultAllocation ?? null };
  }).filter(Boolean) as any[];
  const tixRows = [
    ...tixTiers.map((t) => ({ id: t.id, from: "group" as const, name: t.name || "Untitled tier", kind: t.kind ?? null, price: t.price, qty: t.defaultAllocation ?? null })),
    ...tixTypeRows,
  ];
  /** One ticket row as the customer sees it — shared by the Tickets core and
   *  the ticket-group attachment block. */
  const tixRowView = (t: any) => (
    <div key={`${t.from}:${t.id}`} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", background: "#FFFFFF", borderRadius: 9, padding: "8px 11px" }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 12, fontWeight: 700 }}>{t.name}</span>
        {t.qty != null ? <span style={{ display: "block", fontSize: 10, color: "#697180" }}>{t.qty} allocated</span> : null}
      </span>
      <span style={{ fontSize: 11.5, fontWeight: 800, color: "#D01647", flex: "none" }}>{t.kind === "free" || t.kind === "invite" ? "Free" : tgMoney(t.price)}</span>
    </div>
  );

  /** The attached group's real questions, in group order. Group-level Required
   *  overrides the question's own, exactly as the group editor does. */
  const questionsOf = (groupId: string | null | undefined) => {
    const g = groupId ? qGroups.find((x: any) => x.id === groupId) : null;
    return ((g?.items ?? []) as any[]).map((it: any) => {
      const q = questions.getQ(it.qid);
      if (!q) return null;
      return { id: q.id, label: q.label || "Untitled question", type: q.type, help: q.help ?? "", required: it.required ?? q.required, options: q.options ?? [] };
    }).filter(Boolean) as any[];
  };
  const attQuestions = questionsOf(attGroup?.id);
  /** One attached question, rendered as the customer sees it. Shared by the
   *  Contact/Register cores and the booking-questions attachment block. */
  const qFieldView = (q: any) => {
    const opts = ((q.options ?? []) as { id: string; label: string }[]).slice(0, 4);
    const plain = q.type === "heading" || q.type === "label";
    const body =
      q.type === "heading" ? <span style={{ display: "block", fontSize: 12.5, fontWeight: 800, color: "#333333" }}>{q.label}</span>
      : q.type === "label" ? <span style={{ display: "block", fontSize: 11, color: "#697180", lineHeight: 1.5 }}>{q.label}</span>
      : q.type === "long" ? <div style={{ ...FIELD, minHeight: 44 }}>{q.help || "Long answer"}</div>
      : q.type === "radio" || q.type === "checks" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {(opts.length ? opts : [{ id: "a", label: "Option A" }, { id: "b", label: "Option B" }]).map((o) => (
            <span key={o.id} style={{ fontSize: 11, color: "#697180" }}>{q.type === "checks" ? "☐" : "◯"} {o.label}</span>
          ))}
        </div>
      )
      : q.type === "yesno" ? <span style={{ fontSize: 11, color: "#697180" }}>◯── off</span>
      : q.type === "stepper" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 7, border: "1px solid #D1D4DC", borderRadius: 999, padding: "3px 9px", fontSize: 11, fontWeight: 700, color: "#697180" }}>− <span style={{ color: "#333333" }}>0</span> +</span>
      : q.type === "file" ? <div style={{ ...FIELD, border: "1px dashed #D1D4DC" }}>Choose a file…</div>
      : <div style={FIELD}>{q.type === "select" || q.type === "multi" ? (opts[0]?.label ?? "Choose…") : q.type === "datetime" ? "dd / mm / yyyy" : q.type === "number" ? "0" : (q.help || "Your answer")}</div>;
    return (
      <div key={q.id}>
        {plain ? null : (
          <span style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#333333", marginBottom: 4 }}>
            {q.label}{q.required ? <span style={{ color: "#D01647" }}> *</span> : null}
          </span>
        )}
        {body}
        {q.help && !plain && q.type !== "long" ? <span style={{ display: "block", fontSize: 10, color: "#B5BAC6", marginTop: 3 }}>{q.help}</span> : null}
      </div>
    );
  };

  const setAttach = (patch: Record<string, string | null>) => patchDoc((d) => ({ ...d, attach: { ...(d.attach ?? {}), ...patch } }));

  /* ── Catalogues behind the service pickers (real) ───────────────── */
  const catServices = (cat?.services ?? []) as any[];
  const lobName = (id: string) => ((cat?.lobs as any[] | undefined)?.find((l) => l.id === id)?.name ?? "");
  const GL = ["◰", "◳", "✦", "▣", "◭", "◒", "◓", "▤", "◈", "◐", "✎"];
  const originQueue = D?.origin?.kind === "queue" ? queues.find((q: any) => q.id === D.origin!.id) : null;
  /* Setup · Services is the source of a queue's list — its own copy says "the
   * order here is the order on the Services page". Reading only the SAVED queue
   * record meant a service added in Setup didn't reach the page until the queue
   * was re-saved, so the designer's live config comes first. */
  const queueSetupNames = ((q0.services ?? []) as string[]).filter(Boolean);
  const queueSvcKey = queueSetupNames.join("|");
  const queueCat = useMemo(() => {
    const names: string[] = queueSetupNames.length ? queueSetupNames : (originQueue?.services?.length ? originQueue.services : catServices.map((s) => s.name));
    const wait = originQueue?.avgWaitMins ? `~${originQueue.avgWaitMins} min` : "";
    return names.map((n: string, i: number) => ({ id: n, glyph: GL[i % GL.length]!, name: n, dept: "", wait }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueSvcKey, originQueue, catServices]);
  const apptCat = useMemo(() => catServices.filter((s) => s.durationMin).map((s, i) => ({ id: s.id, glyph: ["◔", "◑", "◕", "◓", "◒"][i % 5]!, name: s.name, dept: lobName(s.lob), wait: `${s.durationMin} min` })), [catServices]);
  const isApptPage = p?.id === "apptsvc";
  const svcCatalogue = isApptPage ? apptCat : queueCat;
  /* The page MIRRORS Setup (operator, 2026-09-02). It used to hold its own
   * `p.svc` list, which is the drift this pass removes: a page could offer
   * something Setup did not, or omit something it did, and the only cure was a
   * warning band with a reconcile button. Setup's selection — in Setup's order
   * — is now the single answer, and a legacy `p.svc` is ignored rather than
   * migrated, because Setup already holds the same list. */
  const svcOn = svcCatalogue;

  /* ── Live availability for the Pick-a-time core (never invented) ── */
  const availSlug = D?.origin?.slug ?? null;
  const grid = useJourneyAvailability({ slug: p?.id === "appttime" ? availSlug : null, serviceId: svcOn[0]?.id ?? null, days: 14 });

  /* ── Canonical editors (ruling ④) ───────────────────────────────── */
  const goRoute = (route: string, params?: Record<string, string>) => { set({ lib: null }); nav.go(route, params as any); };

  /* The rows the One-Stop scope pickers select from — taken from the JOURNEYS
     the operator can already see in the list, because that is what they are
     picking: "the advising desk" is a queue journey, and its `origin` carries the
     real queue id the block's scope needs. Deriving them here rather than reading
     the underlying stores again means the names in the picker are the names in
     the Journeys list, which is the whole point.

     Sources this screen genuinely does not have — locations, org nodes,
     categories, staff — are left UNDEFINED rather than passed as `[]`. The picker
     hides an unavailable source entirely; an empty array would claim the tenant
     has none. */
  const osLibs = {
    queues: Object.values(docs)
      .filter((d) => d.origin?.kind === "queue")
      .map((d) => ({ id: String(d.origin!.id), name: d.name, meta: d.sub })),
    events: Object.values(docs)
      .filter((d) => d.origin?.kind === "event")
      .map((d) => ({ id: String(d.origin!.id), name: d.name, meta: d.sub })),
    services: catServices.map((s) => ({
      id: s.id,
      name: s.name,
      meta: [s.durationMin ? `${s.durationMin} min` : null, lobName(s.lob)].filter(Boolean).join(" · ") || undefined,
    })),
    createRoute: {
      events: { label: "Create an event journey", go: () => goRoute("newjourneys") },
      queues: { label: "Create a queue journey", go: () => goRoute("newjourneys") },
      services: { label: "Open the service catalogue", go: () => goRoute("ops.catalogue") },
    },
  };

  /* ── Page / flow mutations ──────────────────────────────────────── */
  const pageLock = (d: Doc, i: number): string | null => {
    const pg = d.pages[i];
    if (!pg) return null;
    const r = INV[pg.id];
    if (!r) return null;
    return d.pages.filter((x) => x.id === pg.id).length <= 1 ? r : null;
  };
  const addPage = (typeId: string, flowAt: number | null) => {
    if (!D) return;
    const t = regOf(typeId);
    const n = D.pages.filter((x) => x.id === typeId).length;
    const uid = njUid("pg");
    const page = mkPage(typeId, t.label + (n ? ` ${n + 1}` : ""), typeId === "content" ? { uid, content: [{ uid: uid + "b", type: "text", text: "Add your copy here — pick more blocks from the palette." }] } : { uid });
    const newIdx = D.pages.length;
    patchDoc((d) => {
      /* The new step joins the TRUNK after the last page step there. Pages
         inside a decision branch keep their place; a page added from the Pages
         view has no branch to belong to. */
      const m = (d.flow?.steps ?? []).slice();
      let pos = flowAt;
      if (pos == null) { pos = 0; m.forEach((st, i) => { if (st.kind === "page") pos = i; }); }
      m.splice((pos ?? 0) + 1, 0, { id: uid, kind: "page", page: String(newIdx), cfg: {} });
      return { ...d, pages: [...d.pages, page], flow: { ...d.flow, steps: m } };
    });
    set({ typePicker: null, cur: newIdx, sel: null, selStep: uid });
    showToast(`${t.label} page added — the flow now has a step for it`);
  };
  const removePage = (i: number) => {
    if (!D || D.pages.length <= 1) return;
    const lock = pageLock(D, i);
    if (lock) { showToast(`Blocked — ${lock} Core invariants are enforced, not advised.`); return; }
    /* One walk over the whole tree: drop the deleted page's step wherever it
       sits (trunk or any branch) and shift the indices above it down. The three
       flat lists needed three passes and still missed nested decisions. */
    const fix = (steps: QFStep[]): QFStep[] => qfMapAll(steps, (st) => {
      if (st.kind !== "page") return st;
      const idx = Number(st.page);
      if (idx === i) return null;
      return idx > i ? { ...st, page: String(idx - 1) } : st;
    });
    patchDoc((d) => ({ ...d, pages: d.pages.filter((_, x) => x !== i), flow: { ...d.flow, steps: fix(d.flow?.steps ?? []) } }));
    let c = curIdx; if (i < c) c -= 1;
    set({ cur: Math.max(0, Math.min(c, D.pages.length - 2)), sel: null });
  };
  const clearDrag = { dragPg: null, dragOver: null, dragStep: null, dragOverStep: null } as const;
  const movePage = (from: number | null, to: number | null) => {
    if (!D || from == null || to == null || from === to) { set({ ...clearDrag }); return; }
    const order = D.pages.map((_, i) => i);
    order.splice(to, 0, order.splice(from, 1)[0]!);
    const map: Record<number, number> = {};
    order.forEach((oldI, newI) => { map[oldI] = newI; });
    const preview = order.map((i) => D.pages[i]!);
    const ci = preview.map((x) => x.id).lastIndexOf("apptconfirm");
    if (ci !== -1 && ci !== preview.length - 1) {
      set({ ...clearDrag });
      showToast("Blocked — Confirmation must stay the last page of the funnel. Core invariants are enforced, not advised.");
      return;
    }
    patchDoc((d) => {
      const pages = order.map((i) => d.pages[i]!);
      const remap = (steps: QFStep[]): QFStep[] => qfMapAll(steps, (st) =>
        st.kind === "page" ? { ...st, page: String(map[Number(st.page)] ?? Number(st.page)) } : st);
      const fm = remap(d.flow?.steps ?? []);
      /* Trunk page steps follow the new page order; branch pages keep theirs,
         because a branch's sequence is the operator's, not the page list's. */
      const slots: number[] = [];
      fm.forEach((st, i) => { if (st.kind === "page") slots.push(i); });
      const inOrder = slots.map((i) => fm[i]!).slice().sort((a, b) => Number((a as { page: string }).page) - Number((b as { page: string }).page));
      slots.forEach((pos, j) => { fm[pos] = inOrder[j]!; });
      return { ...d, pages, flow: { ...d.flow, steps: fm } };
    });
    set({ cur: map[curIdx] ?? 0, ...clearDrag });
    showToast("Pages reordered — the flow follows");
  };
  const insertStep = (idx: number, step: QFStep) => {
    patchDoc((d) => { const m = (d.flow?.steps ?? []).slice(); m.splice(idx + 1, 0, step); return { ...d, flow: { ...d.flow, steps: m } }; });
    set({ addMenu: null, selStep: step.id });
  };

  /* ── Block helpers ──────────────────────────────────────────────── */
  const bval = (c: Block, k: string): any => (c && c[k] !== undefined ? c[k] : (BLOCK_DEF[c.type] ?? {})[k]);
  const setBval = (uid: string, k: string, v: unknown) => patchContent((list) => mapContent(list, (x) => (x.uid === uid ? { ...x, [k]: v } : x)));
  const pval = (k: string): any => (p && p[k] !== undefined ? p[k] : (CORE_DEF[p?.id ?? ""] ?? {})[k]);
  const tg = (k: string) => { const t = p?.toggles.find((x) => x.k === k); return t ? t.on : false; };
  /* How the customer leaves this page. Derived from the page's position and the
     journey's commit point (nj-model/stepNavFor) — the canvas draws it, the
     inspector only ever edits the commit page's wording. */
  const pageNav = D ? stepNavFor(D as any, curIdx) : null;
  const commitLabelDefault = String(D?.commitLabel || COMMIT_LABEL_BY_TYPE[D?.type ?? ""] || "Confirm");
  const applyImage = (c: Block, urls: string[]) => {
    if (!urls.length) return;
    if (c.type === "gallery") {
      patchContent((list) => list.map((x) => {
        if (x.uid !== c.uid) return x;
        const have = String(x.urls ?? "").split("\n").filter(Boolean);
        const next = have.concat(urls);
        return { ...x, urls: next.join("\n"), count: String(Math.max(Number(x.count ?? 3), next.length)) };
      }));
      showToast(`${urls.length} ${urls.length === 1 ? "image" : "images"} added to the gallery`);
      return;
    }
    const key = c.type === "hero" ? "img" : "src";
    patchContent((list) => list.map((x) => (x.uid === c.uid ? { ...x, [key]: urls[0], ...(c.type === "hero" ? { bg: "Photo" } : {}) } : x)));
    showToast(c.type === "hero" ? "Hero background set" : "Image set");
  };
  const dropImage = (c: Block) => (e: any) => {
    e.preventDefault(); e.stopPropagation();
    const files = Array.from<File>(e.dataTransfer?.files ?? []).filter((f) => /^image\//.test(f.type));
    const url = e.dataTransfer?.getData ? (e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain")) : "";
    if (!files.length && url) { applyImage(c, [url]); return; }
    if (!files.length) { showToast("Drop an image file, or paste a URL in the panel on the right"); return; }
    Promise.all(files.map((f) => new Promise<string>((res) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.readAsDataURL(f); }))).then((urls) => applyImage(c, urls));
  };
  const cfgFor = (c: Block): CfgRow[] => {
    if (c.type === "button") {
      const kind = bval(c, "linkKind");
      return (BLOCK_CFG.button ?? []).concat([["linkKind", "Links to", "sel", ["URL", "Content page"]]] as CfgRow[],
        kind === "Content page" ? ([["page", "Page", "sel", ["Pick a page…"].concat((D?.pages ?? []).map((x) => x.label))]] as CfgRow[]) : ([["href", "URL", "line"]] as CfgRow[]));
    }
    if (c.type === "social") {
      const out: CfgRow[] = [];
      NETS.forEach((n) => { out.push([n[0], n[1], "tog"]); out.push([n[0] + "Url", `${n[1]} link`, "line", undefined, !bval(c, n[0])]); });
      return out;
    }
    return BLOCK_CFG[c.type] ?? [];
  };

  /* ── Experiences ────────────────────────────────────────────────── */
  const expJourney = (key: string) => {
    const x = exps[key];
    if (x) return { key, name: x.name, ty: "Experience", tint: TYPE_TINT.Experience![0], color: TYPE_TINT.Experience![1], pages: x.attached.length };
    const d = docs[key];
    if (!d) return { key, name: "Missing journey", ty: "Pages", tint: "#F4F5F7", color: "#697180", pages: 0 };
    const ty = docType(d);
    const c = TYPE_TINT[ty] ?? TYPE_TINT.Pages!;
    return { key, name: d.name, ty, tint: c[0], color: c[1], pages: d.pages.length };
  };
  const E: Exp | null = ui.curExp && exps[ui.curExp] ? exps[ui.curExp]! : null;
  const ownerKey = Object.keys(exps).find((k) => exps[k]!.anchor === ui.curDoc || exps[k]!.attached.some((a) => a.doc === ui.curDoc));
  const owner = ownerKey ? exps[ownerKey]! : null;

  /* ── List rows (real objects + experiences) ─────────────────────── */
  const openDoc = (k: string, fromExp = "") => {
    const d = docs[k];
    const ps = qfFlatten(d?.flow?.steps ?? []).filter((st) => st.kind === "page")[0];
    set({ route: "designer", curDoc: k, view: "pages", cur: 0, sel: null, simCalled: false, selStep: ps?.id ?? null, addMenu: null, fromExp });
  };
  const allRows = useMemo(() => {
    const rows: any[] = [];
    Object.keys(exps).forEach((k) => {
      const e = exps[k]!;
      const inside = (e.anchor ? 1 : 0) + e.attached.length;
      rows.push({
        key: k, glyph: "◈", pageCount: inside, name: e.name, type: "Experience",
        sub: `Experience — ${e.anchor ? (docs[e.anchor]?.name ?? "missing anchor") : "no anchor yet"} + ${e.attached.length} attached journey${e.attached.length === 1 ? "" : "s"}, one check-in`,
        tint: TYPE_TINT.Experience![0], color: TYPE_TINT.Experience![1],
        status: e.status, stColor: e.status === "Live" ? "#027D47" : "#916D0F",
        pages: `${inside} journeys`, updated: e.updated, isExp: true, edited: true,
        /* Experiences have no audit block — they are their own resource with its
           own store, and stamping them was not part of this ask. Empty cells. */
        created: { who: "", when: "", title: "" } as AuditCell, modified: { who: "", when: "", title: "" } as AuditCell, mts: 0,
        onOpen: () => set({ route: "experience", curExp: k, expPicker: null, fromExp: "" }),
      });
    });
    docKeys.forEach((k) => {
      const d = docs[k]!;
      const ty = docType(d);
      const TT = TYPE_TINT[ty] ?? TYPE_TINT.Pages!;
      rows.push({
        key: k, glyph: TYPE_GLYPH[ty] ?? "▤", pageCount: d.pages.length,
        /* The name an author would recognise. `doc.name` is the designer
           header's field; a journey named only in Setup has an untitled one, and
           the row read "Untitled queue journey" while the queue was called
           something. The mirror in patchQueue keeps them in step from now on —
           this covers documents SAVED BEFORE that, which would otherwise stay
           untitled in the list forever. */
        name: (() => {
          const own = (d.name ?? "").trim();
          if (own && !/^untitled/i.test(own)) return own;
          const held = String(
            (d.setup as { title?: unknown } | undefined)?.title
            ?? (d.qsetup as { name?: unknown } | undefined)?.name
            ?? (d.boardCfg as { name?: unknown } | undefined)?.name
            ?? "",
          ).trim();
          return held || own || "Untitled journey";
        })(),
        sub: d.sub,
        type: ty, tint: TT[0], color: TT[1],
        status: d.edited ? `${d.status} · edited` : d.status,
        stColor: /^Live/.test(d.status) ? "#027D47" : /^Cancel/.test(d.status) ? "#D01647" : "#916D0F",
        /* The history the row can honestly tell. A journey saved in the designer
           carries its own stamps; a derived row nobody has opened falls back to
           the OWNING record's created/modified fields, and shows nothing at all
           when that record carries none (see nj-audit / the filed API request). */
        pages: String(d.pages.length), updated: d.edited ? "Just now" : d.updated === "—" ? "Not edited" : d.updated, edited: d.edited,
        created: createdCell(d.audit), modified: modifiedCell(d.audit),
        /** Sort key for "Recently updated": the real instant when there is one. */
        mts: d.audit?.updatedAt ? (Date.parse(d.audit.updatedAt) || 0) : 0,
        origin: d.origin, isExp: false, onOpen: () => openDoc(k),
      });
    });
    return rows;
  }, [exps, docs, docKeys.join("|")]);

  const liveCount = allRows.filter((r) => /^Live/.test(r.status)).length;
  const draftCount = allRows.filter((r) => /^Draft/.test(r.status)).length;
  const expCount = allRows.filter((r) => r.type === "Experience").length;
  /* Folder organization — the shared Folder Library shell (family "journeys"),
   * the same one Messages, Booking questions, Surveys and Events mount. Items
   * are filed by journey key, so a queue, event, board or experience files the
   * same way. */
  const jlib = useFolderLibrary({
    family: "journeys",
    ids: allRows.map((r) => r.key),
    canWrite: true,
    allLabel: "All journeys",
    searchAllLabel: "Search all journeys",
    query: ui.listQuery,
    failTitle: "Couldn't file that journey",
  });
  const jPool = new Set(jlib.pool);
  const selSet = Array.from(jlib.sel);
  const clearSel = () => jlib.setSel(new Set());
  /** Type counts are folder-scoped, like every other count on this screen. */
  const typeCounts = allRows.filter((r) => jPool.has(r.key)).reduce((m: Record<string, number>, r) => { m[r.type] = (m[r.type] ?? 0) + 1; return m; }, {});
  const TYPE_ORDER = ["Queue", "Event", "Appointment", "Board", "Experience", "Mixed", "Pages"];
  const q = ui.listQuery.trim().toLowerCase();
  const filtering = !!q || ui.listFilter !== "all" || ui.listType !== "all";
  let rows = allRows.filter((r) => {
    if (!jPool.has(r.key)) return false;
    if (ui.listType !== "all" && r.type !== ui.listType) return false;
    if (ui.listFilter === "live" && !/^Live/.test(r.status)) return false;
    if (ui.listFilter === "draft" && !/^Draft/.test(r.status)) return false;
    if (ui.listFilter === "exp" && r.type !== "Experience") return false;
    if (!q) return true;
    return `${r.name} ${r.type} ${r.sub}`.toLowerCase().includes(q);
  });
  if (ui.listSort === "name") rows = rows.slice().sort((a, b) => a.name.localeCompare(b.name));
  /* Recently updated: real timestamps first (newest first), then the rows that
     only know they were edited here, then the rest — A–Z inside each band. */
  else if (ui.listSort === "updated") rows = rows.slice().sort((a, b) => (b.mts - a.mts) || Number(b.edited) - Number(a.edited) || a.name.localeCompare(b.name));
  else if (ui.listSort === "type") rows = rows.slice().sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name));
  else if (ui.listSort === "pages") rows = rows.slice().sort((a, b) => b.pageCount - a.pageCount);
  const allChecked = rows.length > 0 && rows.every((r) => jlib.sel.has(r.key));
  const someChecked = selSet.length > 0 && !allChecked;

  const exportCsv = (keys: string[]) => {
    const head = ["name", "type", "status", "pages", "created by", "created", "last modified by", "last modified", "source"];
    const body = keys.map((k) => {
      const r = allRows.find((x) => x.key === k);
      if (!r) return "";
      return [r.name, r.type, r.status, r.pages, r.created.who, r.created.when, r.modified.who, r.modified.when || r.updated, r.origin?.label ?? (r.isExp ? "experience" : "—")]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",");
    }).filter(Boolean);
    const blob = new Blob([[head.join(","), ...body].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `journeys-${slug}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    showToast(`Exported ${body.length} journe${body.length === 1 ? "y" : "ys"} as CSV`);
  };
  const duplicateKeys = (keys: string[]) => {
    let n = 0;
    keys.forEach((k) => {
      const d = docs[k];
      if (!d) return;
      const key = `local:${njUid("nj")}`;
      /* A copy is a NEW journey: dropping the source's audit block lets saveDoc
         stamp whoever duplicated it as the creator, not the original author. */
      saveDoc(key, { ...JSON.parse(JSON.stringify(d)), name: `${d.name} (copy)`, status: "Draft", updated: "Just now", edited: false, audit: undefined, origin: { kind: "local", id: key, label: `Copied from ${d.name}` } });
      n += 1;
    });
    set({ listSel: [] });
    showToast(`Duplicated ${n} journey${n === 1 ? "" : "s"} as draft${n === 1 ? "" : "s"}`);
  };
  const deleteKeys = (keys: string[]) => {
    const local = keys.filter((k) => k.startsWith("local:") || exps[k]);
    const owned = keys.filter((k) => !local.includes(k));
    local.forEach((k) => {
      if (exps[k]) { setExps((x) => { const n = { ...x }; delete n[k]; return n; }); void deleteExperience(slug, k); }
      else { setOverlays((o) => { const n = { ...(o ?? {}) }; delete n[k]; return n; }); void deleteJourneyDoc(slug, k); }
    });
    set({ listSel: [] });
    if (local.length && owned.length) showToast(`Deleted ${local.length} · ${owned.length} belong to a queue or event — delete those in their own screen`);
    else if (local.length) showToast(`Deleted ${local.length} journey${local.length === 1 ? "" : "s"}`);
    else showToast("These journeys belong to a queue or event — delete the object in its own screen");
  };
  const renameRow = (key: string, name: string) => {
    const clean = name.trim();
    if (!clean) { set({ renameKey: "", rowMenu: null }); return; }
    if (exps[key]) {
      const next = { ...exps[key]!, name: clean, updated: "Just now" };
      setExps((x) => ({ ...x, [key]: next }));
      void putExperience(slug, key, next);
      set({ renameKey: "", rowMenu: null });
      showToast(`Renamed to “${clean}”`);
      return;
    }
    const d = docs[key];
    if (!d) return;
    /* saveDoc pushes the name onto the owning queue too (updateQueue owns `name`). */
    saveDoc(key, { ...d, name: clean, edited: true, updated: "Just now" });
    set({ renameKey: "", rowMenu: null });
    showToast(WRITES_BACK(d.origin?.kind) ? `Renamed to “${clean}” — saving onto the ${OWNER_NOUN[d.origin!.kind] ?? "object"}` : `Renamed to “${clean}”`);
  };

  const revertKeys = (keys: string[]) => {
    let n = 0;
    keys.forEach((k) => { if (!k.startsWith("local:") && (overlays ?? {})[k]) { setOverlays((o) => { const x = { ...(o ?? {}) }; delete x[k]; return x; }); void deleteJourneyDoc(slug, k); n += 1; } });
    set({ listSel: [] });
    showToast(n ? `Reverted ${n} journey${n === 1 ? "" : "s"} to what its object says` : "Nothing to revert — no unsaved designer edits in the selection");
  };

  const loading = overlays === null || (!queuesLoaded && !docKeys.length);

  /* ── Template picker ──────────────────────────────────────────────
     THE FOUR v1 JOURNEY TYPES, in scope order (operator 2026-08-31): Queue,
     Appointment, Event, One-Stop. The picker is the product's statement of what
     a journey can be, so anything not in v1 does not appear here.

     Removed rather than disabled: `board` (a display board is a lobby SCREEN,
     not a journey a visitor travels — it is still created from Queues ▸ Boards
     and still opens in this designer when derived) and `blank` (a journey with
     no type cannot be attached to a record, so it dead-ends at the one thing
     every template exists to reach). Both template paths still exist in
     `nj-model.templateDoc` and `docFromBoard`, so derived and existing
     documents keep working — only the entry point is scoped.

     One-Stop keeps id `exp`: the record it creates IS the experience document
     (slug + header + ordered blocks), which is what a One-Stop page hosts. Only
     the operator-facing name changed. As of 2026-09-01 it creates a real
     experience PAGE rather than the old anchor-and-attach record. */
  const templates = [
    { id: "queue", name: "Queue", sub: "Join → wait → contact → in line → CSAT, flow seeded", glyph: "☰", tint: "#FAF1DE", color: "#8A5B12", tagLabel: "V1" },
    { id: "appt", name: "Appointment", sub: "Service → time → contact → confirm", glyph: "◔", tint: "#E0F2F1", color: "#0AA396", tagLabel: "V1" },
    { id: "event", name: "Event", sub: "Landing → register → tickets", glyph: "◆", tint: "#FDEBF3", color: "#D61A55", tagLabel: "V1" },
    { id: "exp", name: "One-Stop", sub: "One page — the desks, services and events a student can reach in one visit", glyph: "◈", tint: "#E4E1F7", color: "#262265", tagLabel: "V1" },
  ];
  const pickTemplate = (id: string) => {
    /* One-Stop is a journey DOCUMENT like every other type — same designer, same
       page rail, same canvas, same inspector, same preview, same undo stack, same
       autosave. It differs only in what its page holds: One-Stop registry blocks
       instead of the designer's copy blocks. Nothing about it needs a screen of
       its own, and giving it one was the mistake this replaces. */
    /* The `as any` pair was here only because `templateDoc`'s union omitted
       "onestop" while the function already had an "onestop" branch (2026-09-08).
       The union carries it now, so the first cast is gone; `id` stays cast
       because it arrives as a plain string from the picker. */
    const r = templateDoc(id === "exp" ? "onestop" : (id as Parameters<typeof templateDoc>[0]));
    if (id === "exp") {
      const skeleton = oneStopSkeletonBlocks({ locale: "en-US", timeZone: tzContext().zone });
      const page = r.doc.pages[0]!;
      page.osBlocks = skeleton.blocks.map((b) => ({ uid: njUid("os"), ...b })) as any;
      page.osSettings = { navLabel: null, locale: "en-US", supportedLocales: ["en-US"], timeZone: tzContext().zone };
    }
    saveDoc(r.key, r.doc);
    const ps = qfFlatten(r.doc.flow?.steps ?? []).filter((st) => st.kind === "page")[0];
    const firstSetup = r.doc.type === "Event" ? "basics" : r.doc.type === "Queue" ? "qsetup" : r.doc.type === "Board" ? "boardcfg" : r.doc.type === "One-Stop" ? "onestopcfg" : null;
    set({ tplOpen: false, route: "designer", curDoc: r.key, view: "pages", cur: 0, sel: null, selStep: ps?.id ?? null, setupView: firstSetup as any });
    showToast(`${templates.find((t) => t.id === id)?.name} created — attach it to a real object from its pages`);
  };

  /* ── Deep links (2026-08-25) ──────────────────────────────────────
     The Events list is the create/edit/preview entry point for events, so
     `newjourneys` has to accept being told what to open:

       ?new=event                       → start a new Event journey in the designer
       ?journey=event:<id>              → open that event's journey
       ?journey=event:<id>&preview=1    → open it and show the customer preview

     Before this, every one of those landed on the journeys list and the caller's
     intent was dropped. The params are consumed ONCE and then replaced out of the
     URL, so a refresh can't mint a second draft. A `journey` link waits for the
     document to exist — event rows arrive asynchronously from useEvents. */
  const linkRef = useRef("");
  useEffect(() => {
    if (overlays === null) return;
    const want = String(nav.params?.journey ?? "");
    const fresh = String(nav.params?.new ?? "");
    const preview = String(nav.params?.preview ?? "") === "1";
    const sig = `${want}|${fresh}|${preview}`;
    if ((!want && !fresh) || linkRef.current === sig) return;
    if (want) {
      if (!docs[want]) return; // the owning record hasn't loaded yet — try again next render
      linkRef.current = sig;
      openDoc(want);
      if (preview) set({ previewOpen: true });
    } else {
      const tpl = templates.some((t) => t.id === fresh) ? fresh : "blank";
      linkRef.current = sig;
      pickTemplate(tpl);
    }
    nav.replace("newjourneys");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav.params?.journey, nav.params?.new, nav.params?.preview, overlays, docKeys.join("|")]);

  /* Esc leaves the preview; leaving the designer closes it too. The listener is
     on the window in the CAPTURE phase, so a nested overlay that stops keydown
     propagation can't strand the operator inside the preview. */
  useEffect(() => {
    if (!ui.previewOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setUi((u) => ({ ...u, previewOpen: false })); };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [ui.previewOpen]);
  useEffect(() => {
    if (ui.previewOpen && ui.route !== "designer") setUi((u) => ({ ...u, previewOpen: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.route]);

  /* ── Library picker rows ────────────────────────────────────────── */
  const libRows = (kind: LibKind) => {
    if (kind === "questions") return qGroups.map((g) => ({ id: g.id, name: g.name || "Untitled group", meta: `${g.items?.length ?? 0} questions · ${g.status}`, on: attGroup?.id === g.id, pick: () => { setAttach({ groupId: g.id }); showToast(`Attached "${g.name}" — the journey references it by id (ruling ④)`); }, edit: () => goRoute("questions.group.edit", { id: g.id }) }));
    if (kind === "survey") return surveys.map((s: any) => ({ id: s.id, name: s.name || "Untitled survey", meta: `${surveyItemCount(s)} questions`, on: attSurvey?.id === s.id, pick: () => {
      /* Writes where that journey type keeps it — the event record for an event,
         the document for the rest. One picker, two homes, rather than a picker
         that quietly writes to the wrong one. */
      if (isEventJourney) {
        const next = [s.id];
        patchRecord((x) => ({ ...x, surveys: next }));
        if (eventOriginId) void patchEventMessaging(eventOriginId, { surveys: next });
      } else {
        setAttach({ surveyId: s.id });
      }
      showToast(`Attached "${s.name}" — edits open the surveys library`);
    }, edit: () => goRoute("surveys.edit", { id: s.id }) }));
    if (kind === "tickets") return ticketGroups.map((g) => ({ id: g.id, name: g.name || "Untitled group", meta: `${g.tiers?.length ?? 0} ticket types`, on: ((r0.ticketGroupIds ?? [])[0] ?? at.ticketGroupId) === g.id, pick: () => { setAttach({ ticketGroupId: g.id }); patchRecord((x) => ({ ...x, ticketGroupIds: [g.id] })); if (eventOriginId) attachEventTicketGroupIds(eventOriginId, [g.id]); showToast(`Attached "${g.name}" — tickets render from the library`); }, edit: () => goRoute("ticket-groups") }));
    if (kind === "promo") return coupons.map((c: any) => ({ id: c.id, name: c.code || c.name || "Untitled promo", meta: [c.type, c.status].filter(Boolean).join(" · "), on: promoIds.includes(c.id), pick: () => {
      /* Writes where that journey type keeps it — the event record for an event,
         the document for anything that still has no owner field. Toggles rather
         than replaces: an event accepts several codes, so picking a second must
         not silently drop the first. */
      if (isEventJourney) {
        const cur = r0.coupons ?? [];
        const next = cur.includes(c.id) ? cur.filter((x: string) => x !== c.id) : [...cur, c.id];
        patchRecord((x) => ({ ...x, coupons: next }));
        if (eventOriginId) void patchEventTickets(eventOriginId, { coupons: next });
        showToast(next.includes(c.id) ? `Accepting "${c.code ?? c.name}" on this event` : `Removed "${c.code ?? c.name}"`);
        return;
      }
      setAttach({ promoId: c.id });
      showToast(`Attached "${c.code ?? c.name}" to the win-back message`);
    }, edit: () => goRoute("promos.edit", { id: c.id }) }));
    /* The step being attached to is the one selected on the flow canvas. This
       row used to read an undefined `selStep` and write through `d.flowMain`,
       so it could only ever have touched a trunk step — now the tree walk
       reaches a step inside any branch. */
    const selId = ui.selStep;
    const selFlowStep = selId ? qfFlatten(qfFlow.steps).filter((st) => st.id === selId)[0] : null;
    const selMsgId = selFlowStep && selFlowStep.kind === "comms" ? selFlowStep.messageId : null;
    return messages.map((m: any) => ({ id: m.id, name: m.name || "Untitled message", meta: `${(m.channels ?? []).join(" · ")} · ${m.status}`, on: selMsgId === m.id, pick: () => { if (selId) patchDoc((d) => ({ ...d, flow: { ...d.flow, steps: qfMapAll(d.flow?.steps ?? [], (st) => (st.id === selId && st.kind === "comms" ? { ...st, messageId: m.id, hint: undefined } : st)) } })); showToast(`Step now sends "${m.name}" from the message library`); }, edit: () => goRoute("messaging.compose", { id: m.id }) }));
  };

  /* ══════════════════════════════════════════════════════════════════
     Render
     ══════════════════════════════════════════════════════════════════ */
  const shell = (children: any) => (
    /* Journeys-section tabs (2026-08-31): Journeys · Confirmation pages. The
       confirmation-page count is not loaded here — that screen owns it. */
    <Screen rail="new-journeys" tabs={journeysSectionTabs(nav, "newjourneys")} activeTab="newjourneys">
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "#F4F5F7", fontFamily: FONT, color: "#333333", fontSize: 13 }}>{children}</div>
    </Screen>
  );

  /* ════════════════════════════════════════════════════════════════════
   *  NO HOOKS BELOW THIS LINE.
   *
   *  The early return underneath is the reason. Everything after it runs only
   *  when `loading` is false, so a `useState` / `useEffect` / `useMemo` /
   *  `useRef` — or any custom hook that calls one — placed below runs on some
   *  renders and not others. React reports that as the unhelpful minified error
   *  #310 ("rendered more hooks than during the previous render"), pointing at
   *  the new hook rather than at this return.
   *
   *  This file is ~5,000 lines and its render body reads like top-to-bottom
   *  markup, so "declare it next to what uses it" is the natural instinct and
   *  the wrong one. THREE features have now tripped over it: the live-ops
   *  composer, the guest fields, and the drag auto-scroll. Every hook belongs in
   *  the block above; the value it returns can be used anywhere below.
   *  ═══════════════════════════════════════════════════════════════════ */
  if (loading) {
    return shell(<div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, color: "#697180" }}>Loading journeys from your queues, events and catalogue…</div>);
  }

  /* ── HOME ───────────────────────────────────────────────────────── */
  const home = (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "20px 24px 14px", borderBottom: "1px solid #E6E8EC", background: "#FFFFFF", flexShrink: 0 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: "-0.01em" }}>Journeys</h1>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", background: "#EDE5FE", color: "#5142C7", borderRadius: 999, padding: "3px 10px" }}>PHASE 5</span>
          </div>
          <p style={{ fontSize: 12.5, color: "#697180", margin: "4px 0 0", lineHeight: 1.5 }}>
            {allRows.length} journeys · {liveCount} live · {draftCount} draft · {expCount} experiences
          </p>
        </div>
        <button onClick={() => exportCsv(rows.map((r) => r.key))} style={GHOST}>↓ Export</button>
        <button onClick={() => set({ tplOpen: true })} style={PRIMARY}>+ New journey</button>
      </div>

      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, flexShrink: 0 }}>
        {[
          { glyph: "▤", tint: "#EDE5FE", color: "#5142C7", value: allRows.length, label: "Journeys" },
          { glyph: "◉", tint: "#E6F4EE", color: "#027D47", value: liveCount, label: "Live" },
          { glyph: "✎", tint: "#FFFAED", color: "#916D0F", value: draftCount, label: "Drafts" },
          { glyph: "◈", tint: "#E4E1F7", color: "#262265", value: expCount, label: "Experiences" },
        ].map((s) => (
          <div key={s.label} style={{ background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 11 }}>
            <span style={glyphBox(s.tint, s.color)}>{s.glyph}</span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 19, fontWeight: 700, lineHeight: 1.15 }}>{s.value}</span>
              <span style={{ display: "block", fontSize: 11, color: "#697180", whiteSpace: "nowrap" }}>{s.label}</span>
            </span>
          </div>
        ))}
      </div>

      <FolderShellFrame lib={jlib}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 0 10px", flexShrink: 0, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, flex: "1 1 200px", maxWidth: 280, minWidth: 180, boxSizing: "border-box", height: 32, padding: "0 11px", border: "1px solid #D1D4DC", borderRadius: 8, background: "#FFFFFF" }}>
          <span style={{ color: "#B5BAC6", fontSize: 12 }}>⌕</span>
          <input value={ui.listQuery} onChange={(e) => { set({ listQuery: e.target.value }); clearSel(); }} placeholder="Find a journey by name or type" style={{ flex: 1, minWidth: 0, border: "none", outline: "none", fontSize: 12.5, background: "transparent", color: "#333333", fontFamily: FONT }} />
        </span>
        <span style={{ width: 1, height: 20, background: "#E6E8EC", margin: "0 4px" }} />
        {[{ k: "all", label: `All · ${allRows.length}` }, { k: "live", label: `Live · ${liveCount}` }, { k: "draft", label: `Drafts · ${draftCount}` }, { k: "exp", label: `Experiences · ${expCount}` }].map((c) => {
          const on = ui.listFilter === c.k;
          return <button key={c.k} onClick={() => { set({ listFilter: c.k }); clearSel(); }} style={{ height: 28, padding: "0 11px", border: `1px solid ${on ? "#5142C7" : "#D1D4DC"}`, borderRadius: 999, background: on ? "#F6F2FE" : "#FFFFFF", color: on ? "#5142C7" : "#333333", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flex: "none", fontFamily: FONT }}>{c.label}</button>;
        })}
        <span style={{ width: 1, height: 20, background: "#E6E8EC", margin: "0 4px" }} />
        {[{ k: "all", label: `All types · ${jlib.pool.length}` }, ...TYPE_ORDER.filter((ty) => typeCounts[ty]).map((ty) => ({ k: ty, label: `${ty} · ${typeCounts[ty]}` }))].map((c) => {
          const on = ui.listType === c.k;
          return <button key={c.k} onClick={() => { set({ listType: c.k }); clearSel(); }} style={{ height: 28, padding: "0 11px", border: `1px solid ${on ? "#262265" : "#D1D4DC"}`, borderRadius: 999, background: on ? "#E4E1F7" : "#FFFFFF", color: on ? "#262265" : "#333333", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flex: "none", fontFamily: FONT }}>{c.label}</button>;
        })}
        <span style={{ width: 1, height: 20, background: "#E6E8EC", margin: "0 4px" }} />
        {/* The design system's own single-select, not a native <select>
            (operator, 2026-09-02). The browser control drew the OS menu — system
            highlight blue, system type, nothing to do with the rest of the row —
            and the tinted box around it was a hand-built imitation of the
            trigger Dropdown already owns, along with its keyboard model and its
            listbox roles. Dropdown had no compact tier until now; it does. */}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flex: "none" }}>
          <span style={{ fontSize: 12, color: "#697180", fontWeight: 500 }}>Sort</span>
          <Dropdown
            size="sm"
            portal
            aria-label="Sort journeys"
            value={ui.listSort}
            onChange={(v) => set({ listSort: v })}
            options={[
              { value: "updated", label: "Recently updated" },
              { value: "name", label: "Name A–Z" },
              { value: "type", label: "Type" },
              { value: "pages", label: "Most pages" },
            ]}
            style={{ width: 176 }}
          />
        </span>
        <span style={{ display: "inline-flex", marginLeft: "auto", flex: "none", border: "1px solid #D1D4DC", borderRadius: 8, overflow: "hidden" }}>
          <button onClick={() => set({ listView: "list" })} style={{ height: 28, padding: "0 11px", border: "none", background: ui.listView === "list" ? "#5142C7" : "#FFFFFF", color: ui.listView === "list" ? "#FFFFFF" : "#333333", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>☰ List</button>
          <button onClick={() => set({ listView: "grid" })} style={{ height: 28, padding: "0 11px", border: "none", background: ui.listView === "grid" ? "#5142C7" : "#FFFFFF", color: ui.listView === "grid" ? "#FFFFFF" : "#333333", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>▦ Cards</button>
        </span>
      </div>

      <FolderShellBars lib={jlib} />

      {selSet.length > 0 ? (
        <div style={{ margin: "0 0 10px", padding: "8px 14px", background: "#FFFFFF", border: "1px solid #DCCCFC", borderRadius: 8, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          {[{ label: "⧉ Duplicate", fn: () => { duplicateKeys(selSet); clearSel(); } }, { label: "↺ Revert edits", fn: () => { revertKeys(selSet); clearSel(); } }, { label: "↓ Export", fn: () => { exportCsv(selSet); clearSel(); } }].map((b) => (
            <button key={b.label} onClick={b.fn} style={{ border: "none", background: "transparent", color: "#5142C7", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "4px 6px", fontFamily: FONT }}>{b.label}</button>
          ))}
          <button onClick={() => { deleteKeys(selSet); clearSel(); }} style={{ border: "none", background: "transparent", color: "#D01647", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "4px 6px", fontFamily: FONT }}>✕ Delete</button>
        </div>
      ) : null}

      <div style={{ flex: 1, minHeight: 0 }}>
        {rows.length === 0 ? (
          <div style={{ background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 12, padding: "44px 24px", textAlign: "center" }}>
            <span style={glyphBox("#F4F5F7", "#697180", 40, 17)}>▤</span>
            <div style={{ fontSize: 14, fontWeight: 700, marginTop: 12 }}>{filtering ? "No journeys match those filters" : "No journeys yet"}</div>
            <div style={{ fontSize: 12.5, color: "#697180", marginTop: 4 }}>{filtering ? "Try a different search or clear the filters to see everything." : "Your queues, events and appointment catalogue appear here as journeys. Start one from a template too."}</div>
            <button onClick={() => (filtering ? (set({ listQuery: "", listFilter: "all", listType: "all" }), jlib.setScope("all")) : set({ tplOpen: true }))} style={{ ...PRIMARY, marginTop: 14 }}>{filtering ? "Clear filters" : "+ New journey"}</button>
          </div>
        ) : ui.listView === "list" ? (
          <div style={{ background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: LIST_COLS, gap: 10, alignItems: "center", padding: "9px 16px", background: "#FAFAFD", borderRadius: "11px 11px 0 0", borderBottom: "1px solid #E6E8EC", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#697180" }}>
              <span onClick={() => jlib.setSel(allChecked ? new Set() : new Set(rows.map((r) => r.key)))} style={{ width: 15, height: 15, borderRadius: 4, border: `1px solid ${allChecked ? "#5142C7" : "#D1D4DC"}`, background: allChecked ? "#5142C7" : someChecked ? "#C18FE8" : "#FFFFFF", color: "#FFFFFF", fontSize: 10, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>{allChecked ? "✓" : someChecked ? "–" : ""}</span>
              <span>Journey</span><span>Type</span><span>Status</span><span>Pages</span><span>Created by</span><span>Updated</span><span style={{ textAlign: "right" }}>Actions</span>
            </div>
            {rows.map((r, ri) => {
              const on = jlib.sel.has(r.key);
              const last = ri === rows.length - 1;
              /* The card can't clip (no overflow:hidden — a row menu has to be able
                 to hang past the last row), so the corners are rounded per edge row. */
              return (
                <div key={r.key} {...jlib.dragProps(r.key)} onClick={r.onOpen} style={{ display: "grid", gridTemplateColumns: LIST_COLS, gap: 10, alignItems: "center", padding: "13px 16px", borderBottom: last ? "none" : "1px solid #F4F5F7", borderRadius: last ? "0 0 11px 11px" : 0, cursor: "pointer", background: on ? "#FCFBFF" : "#FFFFFF" }}>
                  <span onClick={(e) => { e.stopPropagation(); jlib.toggleSel(r.key, !on); }} style={{ width: 15, height: 15, borderRadius: 4, border: `1px solid ${on ? "#5142C7" : "#D1D4DC"}`, background: on ? "#5142C7" : "#FFFFFF", color: "#FFFFFF", fontSize: 10, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>{on ? "✓" : ""}</span>
                  <span style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={glyphBox(r.tint, r.color, 28, 12)}>{r.glyph}</span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "flex", alignItems: "center", fontSize: 13, fontWeight: 700, color: "#333333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}{jlib.folderChips(r.key)}</span>
                      <span style={{ display: "block", fontSize: 11.5, color: "#697180", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.sub}</span>
                    </span>
                  </span>
                  <span><span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: r.tint, color: r.color }}>{r.type}</span></span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: r.stColor }}><span style={{ width: 6, height: 6, borderRadius: 999, background: r.stColor }} />{r.status}</span>
                  <span style={{ fontSize: 12, color: "#697180" }}>{r.pages}</span>
                  {/* Created by / Updated — who above, when below. A row with no
                      recorded author renders an em dash and says why on hover,
                      rather than borrowing a name from the signed-in user.
                      TODO(i18n): journeys.list.createdBy / notRecorded / by — this
                      screen is the prototype-verbatim port and is not keyed yet. */}
                  <span style={{ minWidth: 0 }} title={r.created.title || "No author recorded — this journey hasn't been saved in the designer yet"}>
                    <span style={{ display: "block", fontSize: 12, color: r.created.who ? "#333333" : "#B5BAC6", fontWeight: r.created.who ? 600 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.created.who || "—"}</span>
                    {r.created.when ? <span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 1, whiteSpace: "nowrap" }}>{r.created.when}</span> : null}
                  </span>
                  <span style={{ minWidth: 0 }} title={r.modified.title || ""}>
                    <span style={{ display: "block", fontSize: 12, color: "#697180", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.modified.when || r.updated}</span>
                    {r.modified.who ? <span style={{ display: "block", fontSize: 11, color: "#B5BAC6", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>by {r.modified.who}</span> : null}
                  </span>
                  <span onClick={(e) => e.stopPropagation()} style={{ justifySelf: "end" }}>
                    <RowActions
                      testid="njourneys-row"
                      onView={() => { if (r.origin?.route) goRoute(r.origin.route, r.origin.params); else r.onOpen(); }}
                      viewLabel={r.origin?.route ? `View ${r.origin.label}` : r.isExp ? "View experience" : "View journey"}
                      onEdit={r.onOpen}
                      editLabel="Edit in the Journey Designer"
                      onDuplicate={() => duplicateKeys([r.key])}
                      moreHeader={r.name}
                      moreItems={[
                        jlib.foldersMenuItem(r.key),
                        { icon: <Icon.edit size={14} />, label: "Rename…", onClick: () => set({ renameKey: r.key, renameVal: r.name }) },
                        ...(r.origin?.route ? [{ icon: <Icon.external size={14} />, label: `Open the ${r.origin.kind} →`, onClick: () => goRoute(r.origin.route, r.origin.params) }] : []),
                        ...(r.edited && !r.isExp ? [{ icon: <Icon.arrow dir="left" size={14} />, label: "Revert designer edits", onClick: () => revertKeys([r.key]) }] : []),
                        { icon: <Icon.download size={14} />, label: "Export as CSV", onClick: () => exportCsv([r.key]) },
                        "divider" as const,
                        { icon: <Icon.trash size={14} />, label: "Delete", danger: true, onClick: () => deleteKeys([r.key]) },
                      ]}
                    />
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
            {rows.map((r) => (
              <div key={r.key} onClick={r.onOpen} style={{ background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 12, overflow: "hidden", cursor: "pointer", display: "flex", flexDirection: "column" }}>
                <div style={{ height: 84, background: r.tint, display: "flex", alignItems: "flex-end", justifyContent: "space-between", padding: 12 }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: r.color }}>{r.glyph}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: "#FFFFFF", color: r.color }}>{r.type}</span>
                </div>
                <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</div>
                  <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5 }}>{r.sub}</div>
                </div>
                <div style={{ padding: "8px 14px", borderTop: "1px solid #F4F5F7", background: "#FAFAFD", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11.5, color: "#697180" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600, color: r.stColor }}><span style={{ width: 6, height: 6, borderRadius: 999, background: r.stColor }} />{r.status}</span>
                  <span title={r.modified.title || ""}>{r.pages} · {r.modified.when || r.updated}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        <p style={{ fontSize: 11.5, color: "#B5BAC6", margin: "12px 2px 0" }}>
          Rows come from this tenant's queues, events, display boards and appointment catalogue — the eye opens the object itself, the pencil opens its journey in the designer. Experiences compose them by reference (phase 5). Folders are labels: a journey can sit in several.
          {NJ_STUBBED ? " A queue's name, hero image and services save onto the queue itself." : ""}
        </p>
      </div>
      </FolderShellFrame>
      <FolderShellOverlays lib={jlib} />
    </div>
  );

  /* ── Canvas cores ───────────────────────────────────────────────── */
  const accent = "#14131F";
  const heroBlock = p?.content.find((c) => c.type === "hero") ?? null;
  /** Hero background: the block's own image, else the hero image Setup · Basics
   *  holds. An event with a hero image in Setup showed a bare gradient before. */
  const heroImg = heroBlock ? String(bval(heroBlock, "img") || r0.heroImage || "") : "";
  const waitLabel = originQueue?.avgWaitMins ? `~${originQueue.avgWaitMins} min` : "—";
  const aheadLabel = originQueue ? `${originQueue.waiting ?? 0} ahead of you${originQueue.locationName ? ` · ${originQueue.locationName}` : ""}` : "live from the queue when this journey is attached";
  const firstSvc = svcOn[0] ?? null;
  const hubRows = owner && owner.attached.length
    ? owner.attached.map((a) => { const j = expJourney(a.doc); return { glyph: TYPE_GLYPH[j.ty] ?? "▤", tint: j.tint, color: j.color, name: j.name, meta: a.scope }; })
    : [{ glyph: "◈", tint: "#E4E1F7", color: "#262265", name: "Not in an experience yet", meta: "add this page's journey to one" }];

  /* ── The journey end page (stage 3, 2026-09-01) ────────────────────
   *  `sections` is read through csMigrate on every render rather than trusted:
   *  a document may predate a section, or carry one its type stopped allowing
   *  when the journey was retyped. Declared here, above core(), the inspector
   *  and the left pane — all three read it. */
  const csType = csTypeOf(D?.type);
  const isConfirmPage = !!p && (p.id === "confirmation" || p.id === "apptconfirm");
  const csState: CSState | null = isConfirmPage && p ? csMigrate(p.sections, csType) : null;
  const setSections = (next: CSState) => patchPage({ sections: next });

  /* ── The One-Stop page, in the SAME three panes as everything else ──────
   *  Read exactly the way `isConfirmPage`/`csState` are, and for the same
   *  reason: the page's own parts are not the designer's copy blocks, but they
   *  belong in the designer's rail, canvas and inspector rather than in a screen
   *  of their own. Everything shared comes free — undo/redo over the document,
   *  autosave through saveDoc, the Preview overlay, the page rail, Setup.
   *
   *  `ui.selSection` carries the selected BLOCK id here, the same slot the
   *  confirmation page uses for its selected section: both are "a part of this
   *  page that is not a `content` block", and a second slot would have to be
   *  cleared in every place this one already is. */
  const isOneStopPage = p?.id === "onestop";
  type OSBlock = { uid: string; id: string; type: string; version: number; config: Record<string, unknown>; visibility?: "always" | "signed-in" | "signed-out" };
  const osBlocks: OSBlock[] = (p?.osBlocks ?? []) as OSBlock[];
  const setOsBlocks = (next: OSBlock[]) => patchPage({ osBlocks: next });

  /** The draft's blocks with their scopes resolved against the records the
   *  designer already holds, so a picked queue or service RENDERS on the canvas
   *  rather than showing the block's empty state.
   *
   *  Not a hook: the input is three arrays the designer keeps loaded and a block
   *  list that changes on every edit, so a memo would recompute anyway and would
   *  be one more thing to place correctly relative to the early return. */
  /* ── What a One-Stop page can offer ───────────────────────────────────
   *  The three rails themselves, NOT the journeys derived from them (operator,
   *  2026-09-02). Two reasons, and the second one was a live defect.
   *
   *  A One-Stop page offers a student a bookable service, a desk to join or an
   *  event to sign up for. Those are the objects on the Appointments, Queues and
   *  Events rails. A journey is how one of them is *conducted*, which is a
   *  different noun — and picking journeys meant a queue with no journey could
   *  never be offered at all.
   *
   *  THE DEFECT: an appointment journey's `origin.id` is a catalogue GROUP id,
   *  and the block's scope key is `services`, which the resolver and the canvas
   *  preview both read as SERVICE ids. Ticking one wrote a group id where a
   *  service id was expected, so appointments could never render. Reading the
   *  rails directly makes the id that is stored the id that is looked up, by
   *  construction rather than by care.
   */
  /* ONE BLOCK PER ITEM (operator ruling, 2026-09-02).
   *
   *  Ticking three services used to add three ids to one `appointments` block.
   *  It now adds three `book-appointment` blocks, each naming one service — so a
   *  Notice can sit between two of them, which a single list block made
   *  impossible.
   *
   *  These three types already existed in the registry and were sitting disabled
   *  in the palette. They are the right carriers: each holds exactly one object
   *  id, and the renderer reads it from a key list that accepts `queueId` as
   *  readily as `scope.queues`.
   *
   *  The plural list blocks stay available for the case they are actually good
   *  at — "every service in this category", where the author does not want to
   *  name each one. */
  const OS_ITEM_FOR: Record<string, { type: string; idKey: string }> = {
    service: { type: "book-appointment", idKey: "preselects.serviceId" },
    queue: { type: "join-queue", idKey: "queueId" },
    event: { type: "register-event", idKey: "eventId" },
  };

  /** Read a dotted key off a block config. */
  const osCfgId = (config: Record<string, unknown> | undefined, path: string): string | null => {
    let cur: any = config;
    for (const k of path.split(".")) { if (cur == null || typeof cur !== "object") return null; cur = cur[k]; }
    return typeof cur === "string" && cur ? cur : null;
  };

  const osJourneyRows: { key: string; kind: string; id: string; name: string; sub: string; group: string }[] = [
    ...catServices
      .filter((s: any) => s.durationMin)
      .map((s: any) => ({
        key: `svc:${s.id}`, kind: "service", id: String(s.id),
        name: String(s.name ?? s.id),
        sub: [s.durationMin ? `${s.durationMin} min` : null, lobName(s.lob)].filter(Boolean).join(" · "),
        group: "Bookable services",
      })),
    ...(queues as any[]).map((q) => ({
      key: `q:${q.id}`, kind: "queue", id: String(q.id),
      name: String(q.name ?? q.id),
      sub: [q.locationName, q.status].filter(Boolean).join(" · "),
      group: "Queues",
    })),
    ...((events ?? []) as any[]).map((e) => ({
      key: `ev:${e.id}`, kind: "event", id: String(e.id),
      name: String(e.title ?? e.name ?? e.id),
      sub: [e.date, e.location].filter(Boolean).join(" · "),
      group: "Events",
    })),
  ];

  /** Which objects of this kind are on the page — one block each. */
  const osScopeIds = (kind: string): string[] => {
    const map = OS_ITEM_FOR[kind];
    if (!map) return [];
    return osBlocks
      .filter((b) => b.type === map.type)
      .map((b) => osCfgId(b.config, map.idKey))
      .filter((x): x is string => !!x);
  };

  /** Build one per-item block, named after the object. */
  const osItemBlock = (kind: string, id: string, taken: string[]) => {
    const map = OS_ITEM_FOR[kind]!;
    const row = osJourneyRows.find((r) => r.kind === kind && r.id === id);
    const locale = p?.osSettings?.locale ?? "en-US";
    const config: Record<string, unknown> = row?.name ? { title: { [locale]: row.name } } : {};
    const parts = map.idKey.split(".");
    let cursor: Record<string, unknown> = config;
    parts.forEach((k, i) => {
      if (i === parts.length - 1) cursor[k] = id;
      else { cursor[k] = cursor[k] ?? {}; cursor = cursor[k] as Record<string, unknown>; }
    });
    return { uid: njUid("os"), id: mintBlockId(map.type, taken), type: map.type, version: 1, config };
  };

  /**
   * What a drag from the palette drops.
   *
   * The operator's two answers only reconcile one way. Ticking in Configuration
   * places blocks immediately (answer 1), AND dragging an offer block brings the
   * ticked things on together (answer 2) — which is only consistent if dragging
   * inserts the ticked items that are NOT already on the page, and falls back to
   * one unconfigured block when there are none left to catch up.
   *
   * So the palette entry means "put what I picked here", and when there is
   * nothing outstanding it means "add one I did not pick" — which is the case
   * answer 1 called out. One control, and its behaviour is always the useful one.
   */
  const osInsertAt = (type: string, at: number) => {
    const kind = Object.keys(OS_ITEM_FOR).find((k) => OS_ITEM_FOR[k]!.type === type);
    const taken = osBlocks.map((b) => b.id);
    const next = osBlocks.slice();
    const where = Math.max(0, Math.min(at, next.length));

    if (kind) {
      const onPage = osScopeIds(kind);
      const missing = osJourneyRows.filter((r) => r.kind === kind).map((r) => r.id).filter((id) => !onPage.includes(id));
      if (missing.length > 0) {
        const added = missing.map((id) => { const blk = osItemBlock(kind, id, taken); taken.push(blk.id); return blk; });
        next.splice(where, 0, ...added);
        setOsBlocks(next);
        set({ selSection: added[0]!.id, sel: null });
        showToast(added.length === 1 ? `Added ${osLabel(type)}` : `Added ${added.length} — everything picked in Configuration`);
        return;
      }
      /* Nothing outstanding: an unconfigured block, chosen in the editor. */
      const blk = { uid: njUid("os"), id: mintBlockId(type, taken), type, version: 1, config: {} };
      next.splice(where, 0, blk);
      setOsBlocks(next);
      set({ selSection: blk.id, sel: null });
      showToast("Added — pick which one in the editor on the right");
      return;
    }

    /* Content block: seeded so it is visible the moment it lands. */
    const blk = {
      uid: njUid("os"),
      id: mintBlockId(type, taken),
      type,
      version: 1,
      config: osSeedConfig(type, p?.osSettings?.locale ?? "en-US"),
    };
    next.splice(where, 0, blk);
    setOsBlocks(next);
    set({ selSection: blk.id, sel: null });
  };

  const osToggleJourney = (kind: string, id: string) => {
    const map = OS_ITEM_FOR[kind];
    if (!map) return;
    const existing = osBlocks.find((b) => b.type === map.type && osCfgId(b.config, map.idKey) === id);
    if (existing) {
      setOsBlocks(osBlocks.filter((b) => b.uid !== existing.uid));
      if (ui.selSection === existing.id) set({ selSection: null });
      return;
    }
    /* Same builder the palette drag uses — one definition of "a block for this
       object", so a ticked block and a dragged block cannot differ. */
    const blk = osItemBlock(kind, id, osBlocks.map((b) => b.id));
    setOsBlocks([...osBlocks, blk]);
    set({ selSection: blk.id, sel: null });
  };

  const osPage = D?.pages.find((pg) => pg.id === "onestop") ?? null;
  const osCfg = osPage?.osSettings ?? {};
  const patchOsSettings = (patch: Record<string, unknown>) => {
    const idx = (D?.pages ?? []).findIndex((pg) => pg.id === "onestop");
    if (idx < 0) return;
    patchDoc((d) => ({ ...d, pages: d.pages.map((pg, i) => (i === idx ? { ...pg, osSettings: { ...(pg.osSettings ?? {}), ...patch } } : pg)) }));
  };

  const osPicked = osJourneyRows.filter((r) => osScopeIds(r.kind).includes(r.id)).length;
  const osChecks: { label: string; done: boolean }[] = [
    { label: "Page name", done: !!(D?.name || "").trim() && !/^untitled/i.test((D?.name || "").trim()) },
    { label: "Time zone", done: !!osCfg.timeZone },
    { label: "What this page offers", done: osPicked > 0 },
  ];
  const osReady = osChecks.every((c) => c.done);

  const osRendered = withPreviewData(
    osBlocks,
    { queues: queues as never, services: catServices as never, events: (events ?? []) as never },
    p?.osSettings?.timeZone ?? tzContext().zone,
  );

  /** The services this journey offers and what each would ask — the rows behind
   *  the sample-service switcher. Lifted out of `blockView` in stage 4 so the
   *  runtime slot on Contact and the end page's Answers section resolve the
   *  SAME list and share `ui.sampleByPage`: two switchers offering different
   *  services on one journey would be a defect only the operator would catch. */
  const sampleSvcRows: { id: string; name: string; groupId: string | null }[] = (isQueueJourney || isApptJourney)
    ? (isApptJourney ? (a0.serviceSelection ?? []) : (q0.services ?? []).map(String)).map((id: any) => {
        const sv = ((cat?.services ?? []) as any[]).filter((x: any) => x.id === id || x.name === id)[0];
        const method = isApptJourney ? "appointments" : "queueing";
        return sv
          ? { id: String(sv.id), name: String(sv.name), groupId: (sv.questionGroups?.[method] ?? null) as string | null }
          : { id: String(id), name: String(id), groupId: null };
      })
    : [];
  /** The switcher's pick for THIS page, remembered per page like the Contact
   *  page's (operator ruling 2026-08-31). */
  const csPageUid = p ? String(p.uid ?? p.id) : "";
  const csSample = sampleSvcRows.filter((r) => r.id === (ui.sampleByPage[csPageUid] ?? ""))[0] ?? null;
  const csSampleQs = csSample ? questionsOf(csSample.groupId) : [];
  /** The sample booking the sections render against. Every field is a value
   *  the designer ALREADY holds — the event record, the queue, the catalogue —
   *  so the preview cannot disagree with Setup. Nothing here is authored, and
   *  a value that isn't set stays undefined so the section shows its empty
   *  state naming the step that sets it. Answers is the one section whose
   *  content depends on a choice the customer has not made yet, so on a queue
   *  or an appointment it follows the sample service and says which one. */
  const csBooking: CSBooking = {
    brandName: tenant?.name ?? undefined,
    serviceName: csType === "event" ? undefined : (csSample?.name ?? firstSvc?.name),
    eventName: D?.type === "Event" ? D?.name : undefined,
    dateLine: setupDateLine || undefined,
    timeLine: setupTimeLine || undefined,
    durationLine: firstSvc?.wait,
    advisor: ((cat?.advisors as any[] | undefined)?.[0]?.name) ?? undefined,
    ticketLine: csType === "event" ? (tixRows[0]?.name ?? undefined) : undefined,
    placeName: csType === "event" ? (setupVenue || undefined) : (originQueue?.locationName ?? D?.origin?.label ?? undefined),
    address: setupAddress || undefined,
    virtual: csType === "event" ? !!(r0.method?.virtual && !r0.method?.inPerson) : false,
    answers: csType === "event"
      ? attQuestions.map((q: any) => ({ label: q.label, value: "—" }))
      : csSampleQs.map((q: any) => ({ label: q.label, value: "—" })),
    /* Only ever set where the answers came from a service the DESIGNER picked;
       an event's group is the real one and needs no disclaimer. */
    answersBadge: csType === "event" || !csSample ? undefined : `Sample · ${csSample.name}`,
  };

  /* The step footer — FIXED FURNITURE (operator, 2026-09-02). It is in the
     canvas because an author has to see the page the customer sees, and it is
     not selectable, editable or deletable because it is derived: the only thing
     an author owns here is the commit button's wording, which the inspector
     holds. Back sits left, the forward button right; on the first page the
     forward button keeps its slot and the left one is simply absent.

     Sticky to the bottom only when the content overflows, which is what
     `position: sticky` already means — no viewport test, no second rule. */
  const stepNavFooter = () => {
    /* A Display Board and a One-Stop page are single surfaces — same predicate
       as the Pages/Flow toggle and "+ Add page". Nothing to navigate. */
    if (!hasFlow || !pageNav || (!pageNav.back && !pageNav.next)) return null;
    return (
      <div className="nj-stepnav" title="Back and Next follow the page's position in the flow — the commit button's wording is editable, the buttons themselves aren't"
        style={{ position: "sticky", bottom: 0, display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderTop: "1px solid #F4F5F7", background: "#FFFFFF", cursor: "default" }}>
        {pageNav.back ? (
          <span style={{ height: 38, padding: "0 18px", display: "inline-flex", alignItems: "center", borderRadius: 10, border: "1px solid #D1D4DC", background: "#FFFFFF", color: "#333333", fontSize: 13, fontWeight: 600 }}>Back</span>
        ) : null}
        <span style={{ flex: 1 }} />
        <span style={{ height: 38, padding: "0 22px", display: "inline-flex", alignItems: "center", borderRadius: 10, background: accent, color: "#FFFFFF", fontSize: 13, fontWeight: 700 }}>{pageNav.label}</span>
      </div>
    );
  };

  const core = () => {
    if (!p || !p.core) return null;
    const box = { margin: "12px 14px", borderRadius: 12, padding: 13, cursor: "pointer", position: "relative" as const, border: `2px solid ${ui.sel === "core" ? "#5142C7" : p.tint}`, background: "#FFFFFF" };
    const cta = (label: string, wide = false) => <button style={{ marginTop: 12, height: 38, padding: wide ? undefined : "0 22px", width: wide ? "100%" : undefined, border: "none", borderRadius: 10, background: accent, color: "#FFFFFF", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{label}</button>;
    const svcRow = (s: any, tint: string, color: string, meta?: string | null, dept?: string | null) => (
      <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 12px" }}>
        <span style={glyphBox(tint, color)}>{s.glyph}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{s.name}</span>
          {dept ? <span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{dept}</span> : null}
        </span>
        {meta ? <span style={{ fontSize: 11, fontWeight: 700, color, background: tint, borderRadius: 999, padding: "3px 9px", flex: "none" }}>{meta}</span> : null}
      </div>
    );
    const emptySvc = (where: string) => <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "18px 12px", textAlign: "center", fontSize: 11.5, color: "#697180", marginTop: 6 }}>No services selected yet — pick them in <b>{where}</b> on the right.</div>;

    return (
      <div onClick={() => set({ sel: ui.sel === "core" ? null : "core", selSection: null })} className="nj-core" style={box}>
        <span style={{ position: "absolute", top: -9, left: 10, background: p.color, color: "#FFFFFF", fontSize: 9, fontWeight: 800, letterSpacing: "0.05em", borderRadius: 4, padding: "1px 7px" }}>CORE · {p.core.toUpperCase()}</span>

        {p.id === "services" ? (<>
          {svcOn.length === 0 ? emptySvc("Services offered") : null}
          <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 6 }}>{svcOn.map((s) => svcRow(s, "#ECF1FE", "#2D60EB", tg("est") ? (s.wait || null) : null))}</div>
        </>) : null}

        {p.id === "wait" ? (
          <div style={{ textAlign: "center", padding: "10px 0 4px" }}>
            <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: "-0.02em" }}>{waitLabel}</div>
            <div style={{ fontSize: 12, color: "#697180", marginTop: 2 }}>{aheadLabel}</div>
            {/* No button here: the footer's forward button IS this action
                (operator, 2026-09-02). A core action beside Next was two
                controls for one step, and the core one could be pressed before
                the page was valid. */}
            {tg("book") ? (<>
              <div style={{ fontSize: 11.5, color: "#5142C7", fontWeight: 600, marginTop: 9 }}>{String(pval("altText") ?? "")}</div>
              <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 3, ...MONO }}>→ {String(pval("altUrl") || "no link set")}</div>
            </>) : null}
          </div>
        ) : null}

        {p.id === "contact" || p.id === "register" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}><div style={FIELD}>First name</div><div style={FIELD}>Last name</div></div>
            {p.id === "contact" || (r0.standardFields ?? ["email"]).includes("email") ? <div style={FIELD}>Email address</div> : null}
            {p.id === "contact" || (r0.standardFields ?? ["email"]).includes("phone") ? <div style={FIELD}>Mobile number</div> : null}
            {p.id === "contact" && tg("party") ? <div style={FIELD}>Party size</div> : null}
            {p.id === "register" && tg("guest") ? <div style={FIELD}>+1 guest name (optional){r0.maxGuests ? ` · up to ${r0.maxGuests}` : ""}</div> : null}
            {p.id === "contact" && tg("sms") ? <div style={{ fontSize: 11, color: "#697180" }}>☑ Text me updates about my place in line</div> : null}
            {p.id === "register" && r0.consent?.marketing ? <div style={{ fontSize: 11, color: "#697180" }}>☐ Send me offers and updates</div> : null}
            {p.id === "register" && r0.consent?.contactSharing ? <div style={{ fontSize: 11, color: "#697180" }}>☐ Share my details with the event host</div> : null}
            {/* The attached group's REAL questions, in group order. This page is
                where a customer answers them, so it shows the fields — the count
                lives on the attachment chip. */}
            {attQuestions.length ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 9, borderTop: "1px solid #F4F5F7", paddingTop: 11, marginTop: 2 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em", color: "#7C3AED", textTransform: "uppercase" }}>{qName}</span>
                  <span style={{ fontSize: 10, color: "#B5BAC6" }}>{attQuestions.length} question{attQuestions.length === 1 ? "" : "s"}</span>
                </div>
                {attQuestions.map(qFieldView)}
              </div>
            ) : null}
            {/* Submit lives in the footer — see the note on the wait page. */}
            {p.id === "register" && r0.approvalMode ? <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", ...MONO }}>submits for approval — doesn't confirm immediately</div> : null}
          </div>
        ) : null}

        {p.id === "waiting" ? (ui.simCalled ? (
          <div style={{ textAlign: "center", padding: "12px 0 6px" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#027D47" }}>It's your turn!</div>
            <div style={{ fontSize: 12.5, color: "#697180", marginTop: 4 }}>The next available server is ready for you.</div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "8px 0 2px" }}>
            {tg("pos") ? (<>
              <div style={{ fontSize: 30, fontWeight: 800 }}>{originQueue ? `#${(originQueue.waiting ?? 0) + 1}` : "—"}</div>
              <div style={{ fontSize: 11.5, color: "#697180" }}>{originQueue?.avgWaitMins ? `in line · about ${originQueue.avgWaitMins} min` : "in line"}</div>
            </>) : null}
            <div style={{ height: 6, borderRadius: 999, background: "#E6E8EC", margin: "12px 6px" }}><span style={{ display: "block", width: "55%", height: 6, borderRadius: 999, background: accent }} /></div>
            {tg("msg") ? (
              <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
                {["Running late", "Message the host"].map((x) => <span key={x} style={{ fontSize: 11, fontWeight: 600, border: "1px solid #D1D4DC", borderRadius: 999, padding: "5px 12px", color: "#333333" }}>{x}</span>)}
                <span style={{ fontSize: 11, fontWeight: 600, border: "1px solid #D1D4DC", borderRadius: 999, padding: "5px 12px", color: "#D01647" }}>Leave the line</span>
              </div>
            ) : null}
          </div>
        )) : null}

        {p.id === "apptsvc" ? (<>
          {svcOn.length === 0 ? emptySvc("Appointment services offered") : null}
          <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 6 }}>{svcOn.map((s) => svcRow(s, "#E0F2F1", "#0AA396", tg("dur") ? s.wait : null, tg("dept") ? s.dept : null))}</div>
        </>) : null}

        {p.id === "appttime" ? (
          <div style={{ marginTop: 6 }}>
            {grid.days.length ? (<>
              <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                {grid.days.slice(0, 4).map((d, i) => (
                  <span key={d.iso} style={{ width: 44, padding: "6px 0", borderRadius: 8, border: `1px solid ${i === 0 ? "#0AA396" : "#D1D4DC"}`, background: i === 0 ? "#0AA396" : "#FFFFFF", textAlign: "center" }}>
                    <span style={{ display: "block", fontSize: 9.5, fontWeight: 700, color: i === 0 ? "#FFFFFF" : "#333333", opacity: 0.75 }}>{d.dow}</span>
                    <span style={{ display: "block", fontSize: 13, fontWeight: 800, color: i === 0 ? "#FFFFFF" : "#333333" }}>{d.date.d}</span>
                  </span>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 10 }}>
                {grid.times.slice(0, 6).map((t, i) => (
                  <span key={t} style={{ padding: "8px 0", borderRadius: 8, border: `1px solid ${i === 0 ? "#0AA396" : "#D1D4DC"}`, background: i === 0 ? "#0AA396" : "#FFFFFF", color: i === 0 ? "#FFFFFF" : "#333333", textAlign: "center", fontSize: 12, fontWeight: 700 }}>{t}</span>
                ))}
              </div>
              {tg("adv") && (cat?.advisors as any[] | undefined)?.length ? <div style={{ fontSize: 11, color: "#697180", textAlign: "center", marginTop: 9 }}>with <b>{(cat!.advisors as any[])[0].name}</b>{firstSvc ? ` · ${firstSvc.wait}` : ""}</div> : null}
            </>) : (
              <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "18px 12px", textAlign: "center", fontSize: 11.5, color: "#697180" }}>
                {grid.loading ? "Loading availability…" : availSlug ? "No bookable slots in the next fortnight for this service." : "Attach this journey to a booking catalogue with a public link to show live slots here."}
              </div>
            )}
            <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", marginTop: 7, ...MONO }}>slots via the journey availability contract (2026-08-14)</div>
          </div>
        ) : null}

        {isConfirmPage && csState ? (
          <CSCanvas
            state={csState}
            type={csType}
            booking={csBooking}
            fields={{ bannerTitle: p.bannerTitle, cancelHours: p.cancelHours, note: p.note }}
            sel={ui.selSection}
            onSelect={(id) => set({ selSection: id, sel: id ? null : ui.sel })}
            onChange={setSections}
            accent={accent}
            answersPick={csType === "event" ? null : {
              options: sampleSvcRows.map((r) => ({ id: r.id, name: r.name })),
              selected: ui.sampleByPage[csPageUid] ?? "",
              onPick: (id: string) => set({ sampleByPage: { ...ui.sampleByPage, [csPageUid]: id } }),
            }}
          />
        ) : null}

        {p.id === "eventlanding" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 12px" }}>
              <span style={glyphBox("#FDEBF3", "#D61A55")}>▦</span>
              <span><span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{(events ?? []).find((e: any) => `event:${e.id}` === ui.curDoc)?.date ?? "Date from the event"}</span><span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{(events ?? []).find((e: any) => `event:${e.id}` === ui.curDoc)?.time ?? "—"}</span></span>
            </div>
            {tg("mapcard") ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 12px" }}>
                <span style={glyphBox("#FDEBF3", "#D61A55")}>◉</span>
                <span><span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{(events ?? []).find((e: any) => `event:${e.id}` === ui.curDoc)?.location ?? "Location from the event"}</span><span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>map &amp; parking</span></span>
              </div>
            ) : null}
            {tg("cta") ? <button style={{ height: 38, border: "none", borderRadius: 10, background: accent, color: "#FFFFFF", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{String(pval("cta") ?? "Register")}</button> : null}
          </div>
        ) : null}

        {p.id === "tickets" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 6 }}>
            {tixRows.length ? (<>
              {attTickets ? <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em", color: "#D61A55", textTransform: "uppercase" }}>{attTickets.name || "Ticket group"}</div> : null}
              {tixRows.map((t) => (
                <div key={`${t.from}:${t.id}`} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 12px" }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{t.name}</span>
                    {tg("sold") && t.qty != null ? <span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{t.qty} allocated</span> : null}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "#D01647", flex: "none" }}>{t.kind === "free" || t.kind === "invite" ? "Free" : tgMoney(t.price)}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7, border: "1px solid #D1D4DC", borderRadius: 999, padding: "3px 9px", fontSize: 12, fontWeight: 700, color: "#697180", flex: "none" }}>− <span style={{ color: "#333333" }}>0</span> +</span>
                </div>
              ))}
            </>) : <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "18px 12px", textAlign: "center", fontSize: 11.5, color: "#697180" }}>No tickets yet — attach a ticket group or pick ticket types in <b>Setup · Tickets</b>.</div>}
            {tg("promo2") ? <div style={{ ...FIELD, border: "1px dashed #D1D4DC" }}>Promo code</div> : null}
            {/* Checkout is the footer's forward button on this page. */}
            <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", ...MONO }}>checkout stays the fixed payment component — presentation only is composable</div>
            {r0.waitlist ? <div style={{ fontSize: 11, color: "#697180", textAlign: "center" }}>Sold out? Join the waitlist — we'll email you if a ticket frees up.</div> : null}
          </div>
        ) : null}

        {p.id === "exphub" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
            {tg("badge") ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E4E1F7", background: "#FAFAFE", borderRadius: 10, padding: "10px 12px" }}>
                <span style={{ ...glyphBox("#E6F4EE", "#027D47"), borderRadius: 999, fontWeight: 800 }}>✓</span>
                <span><span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>Checked in</span><span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>your QR works everywhere at this event</span></span>
              </div>
            ) : null}
            {hubRows.map((h, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 12px" }}>
                <span style={glyphBox(h.tint, h.color)}>{h.glyph}</span>
                <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700 }}>{h.name}</span>
                {tg("waits") ? <span style={{ fontSize: 11, fontWeight: 700, color: h.color, background: h.tint, borderRadius: 999, padding: "3px 9px", flex: "none" }}>{h.meta}</span> : null}
              </div>
            ))}
            <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", ...MONO }}>rows = the experience's attached journeys · finishing one returns here</div>
          </div>
        ) : null}

        {p.id === "csat" ? (
          <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
            <div style={{ fontSize: 20, letterSpacing: 4, color: "#F5C61E" }}>★★★★★</div>
            {/* Post-commit pages keep their own actions, as LINKS rather than
                buttons (operator, 2026-09-02): the journey is over, so nothing
                here should carry the weight of a step. */}
            <div style={{ marginTop: 12, fontSize: 12.5, fontWeight: 700, color: accent, textDecoration: "underline" }}>{String(pval("cta") ?? "Take the survey")}</div>
            {tg("review") ? <div style={{ fontSize: 11.5, color: "#5142C7", fontWeight: 600, marginTop: 8 }}>Leave us a review</div> : null}
          </div>
        ) : null}
      </div>
    );
  };

  const contentBlocks = (p?.content ?? []).filter((c) => c.type !== "hero");

  /* ── Drag and drop on the canvas ────────────────────────────────
   *  Two drags, one set of drop targets. A PALETTE drag carries a block type and
   *  inserts a new block; a MOVE drag carries a uid and relocates an existing
   *  one. Both land on the same insertion lines between top-level blocks, so an
   *  author learns one gesture.
   *
   *  Indices here are into `contentBlocks` — the page's content WITHOUT the hero.
   *  Writing back through `patchContent` re-attaches the hero and `hoistHero`
   *  pins it to the top, so no drop target can put anything above it and the
   *  "hero is always first" invariant is never a thing this code has to check.
   */
  const writeBlocks = (next: Block[]) => {
    const hero = (p?.content ?? []).filter((c) => c.type === "hero");
    patchContent(() => [...hero, ...next]);
  };

  const insertTypeAt = (type: string, at: number) => {
    const uid = njUid("c");
    const next = contentBlocks.slice();
    next.splice(Math.max(0, Math.min(at, next.length)), 0, { uid, type, text: CDEF[type] });
    writeBlocks(next);
    set({ sel: uid });
    if (type === "hero") showToast("Hero added — it always sits at the top of the page");
  };

  /* `moveBlockTo` and `nudgeBlock` retired: BlockCanvas owns reordering for every
     journey type, and it calls `onMove(from, to)` with indices. Two copies of
     splice-and-write were two places for the no-op-drag guard to be forgotten. */
  const removeBlock = (uid: string) => {
    writeBlocks(contentBlocks.filter((c) => c.uid !== uid));
    if (ui.sel === uid) set({ sel: null });
  };

  const blockView = (c: Block, i: number, opts?: { inCell?: boolean }) => {
    const ring = ui.sel === c.uid ? "#5142C7" : "transparent";
    const align = bval(c, "align") === "Center" ? "center" : bval(c, "align") === "Right" ? "right" : "left";
    const attKind = ATTACH_TYPES[c.type];
    /* Service-scoped on this journey type? Then the journey-wide attachment is
       a lie: the customer sees the value for the service THEY picked, which is
       unknowable at design time (operator ruling 2026-08-31). Events are exempt
       — one event, one set of answers, known now. Tickets are event-only and
       event-scoped, so they are never in this set. */
    const serviceScoped = (isQueueJourney || isApptJourney) && ["qgroup", "survey", "promo"].indexOf(c.type) > -1;
    const attMeta: Record<string, { name: string; meta: string; tint: string; bd: string; color: string; glyph: string }> = {
      qgroup: { name: qName, meta: `${attQuestions.length || qCount} booking questions render here`, tint: "#F6F2FE", bd: "#DCCCFC", color: "#5142C7", glyph: "?" },
      survey: { name: svName, meta: `${svCount} questions render here`, tint: "#E6F4EE", bd: "#A7DACB", color: "#027D47", glyph: "✎" },
      promo: { name: promoCode, meta: promoOff, tint: "#FFFAED", bd: "#F3D9A4", color: "#916D0F", glyph: "⌾" },
      ticketgrp: { name: tixName, meta: `${tixRows.length} ticket types render here`, tint: "#FDF3F6", bd: "#E7A4B8", color: "#D61A55", glyph: "⬧" },
    };
    return (
      /* The wrapper belongs to BlockCanvas now (2026-09-02): drag, selection,
         hover, the ↑↓× controls and the drop geometry are shared with every other
         journey type, so this function is back to its one job — how a block LOOKS.
         A block inside a COLUMN keeps its own click-to-select wrapper: cells are
         nested lists that BlockCanvas does not own, and a nested block must not
         drag onto the page's gaps and change which array holds it. */
      <div
        key={c.uid}
        className="nj-block"
        onClick={opts?.inCell ? (e) => { e.stopPropagation(); set({ sel: ui.sel === c.uid ? null : c.uid, selSection: null }); } : undefined}
        style={opts?.inCell
          ? { margin: "0 0 8px", position: "relative", cursor: "pointer", outline: `2px solid ${ring}`, outlineOffset: 2, borderRadius: 8 }
          : { position: "relative" }}>
        {serviceScoped ? (() => {
          /* Which services this journey offers, and what each would ask —
             derived once above, shared with the end page's Answers section. */
          const rows = sampleSvcRows;
          const distinct = new Set(rows.map((r) => r.groupId).filter(Boolean)).size;
          /* `p` is nullable here (the same guard L2169 already makes). An
             unattached page has no uid to key the sample switcher on, so "" is
             the honest key rather than "undefined" (2026-09-08). */
          const pageUid = p ? String(p.uid ?? p.id) : "";
          const sampleId = ui.sampleByPage[pageUid] ?? "";
          const sample = rows.filter((r) => r.id === sampleId)[0] ?? null;
          const sampleQs = sample ? questionsOf(sample.groupId) : [];
          const noun = c.type === "qgroup" ? "Booking questions" : c.type === "survey" ? "Survey" : "Promo code";
          const pending = c.type !== "qgroup";
          return (
            <div style={{ border: "1px dashed #C9C2EE", background: "#FBFAFF", borderRadius: 9, padding: "11px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                <span style={glyphBox("#F6F2FE", "#5142C7")}>{c.type === "qgroup" ? "?" : c.type === "survey" ? "✎" : "⌾"}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#5142C7", flex: 1, minWidth: 0 }}>
                  {noun} for the selected service
                </span>
                <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#5142C7", background: "#EDE5FE", borderRadius: 999, padding: "2px 8px" }}>Resolves at runtime</span>
              </div>
              <div style={{ fontSize: 10.5, color: "#5142C7", opacity: 0.85, marginTop: 5, lineHeight: 1.5 }}>
                {pending
                  ? `Set per service once the catalogue carries it — see Setup · ${isApptJourney ? "Per-service settings" : "Services"}.`
                  : `${rows.length} service${rows.length === 1 ? "" : "s"} offered · ${distinct} distinct group${distinct === 1 ? "" : "s"} · `}
                {!pending ? <a href="#" onClick={(e) => { e.preventDefault(); set({ setupView: (isApptJourney ? "aperservice" : "qservices") as any }); }} style={{ color: "#5142C7", fontWeight: 700 }}>see them in Setup</a> : null}
              </div>
              {/* Sample preview (operator ruling): one service's REAL questions,
                  labelled as a sample so it is never mistaken for the page's
                  own content. The choice is remembered per page. */}
              {!pending && rows.length ? (
                <div style={{ marginTop: 9, borderTop: "1px solid #EDE5FE", paddingTop: 9 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: sample ? 8 : 0 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#8A92A0" }}>Preview as</span>
                    {rows.map((r) => {
                      const on = r.id === sampleId;
                      return (
                        <button key={r.id} onClick={(e) => { e.stopPropagation(); set({ sampleByPage: { ...ui.sampleByPage, [pageUid]: on ? "" : r.id } }); }}
                          style={{ padding: "3px 9px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 10.5, fontWeight: 700, border: `1px solid ${on ? "#5142C7" : "#D9DCE3"}`, background: on ? "#5142C7" : "#FFFFFF", color: on ? "#FFFFFF" : "#4B5563", whiteSpace: "nowrap" }}>
                          {r.name}
                        </button>
                      );
                    })}
                  </div>
                  {sample ? (
                    <div style={{ border: "1px solid #DCCCFC", background: "#FFFFFF", borderRadius: 8, padding: "10px 11px" }}>
                      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#8A5A08", background: "#FFF7E8", borderRadius: 999, padding: "2px 8px", display: "inline-block", marginBottom: 8 }}>Sample · {sample.name}</div>
                      {sampleQs.length
                        ? <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>{sampleQs.map(qFieldView)}</div>
                        : <div style={{ ...NOTE, margin: 0 }}>This service asks no questions — the page shows only the standard fields.</div>}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })() : attKind ? (() => {
          const m = attMeta[c.type]!;
          return (
            <div style={{ border: `1px dashed ${m.bd}`, background: m.tint, borderRadius: 8, padding: "10px 11px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ ...glyphBox(m.color, "#FFFFFF", 22, 11), borderRadius: 6, fontWeight: 800 }}>{m.glyph}</span>
                <span style={{ flex: 1, fontSize: 11.5, fontWeight: 700, color: m.color }}>{m.name}</span>
                <span style={{ fontSize: 8.5, fontWeight: 800, background: m.color, color: "#FFFFFF", borderRadius: 3, padding: "1px 5px" }}>ATTACHED</span>
              </div>
              <div style={{ fontSize: 10.5, color: m.color, opacity: 0.85, marginTop: 5 }}>
                {m.meta} · <a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); set({ lib: attKind as LibKind }); }} style={{ color: m.color, fontWeight: 700 }}>Change</a>
              </div>
              {/* An attachment renders what IS attached, not a description of it:
                  a booking-questions or tickets page shows the customer's actual
                  fields and tickets. */}
              {c.type === "qgroup" && attQuestions.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 9, background: "#FFFFFF", border: `1px solid ${m.bd}`, borderRadius: 8, padding: "11px 12px" }}>{attQuestions.map(qFieldView)}</div>
              ) : null}
              {c.type === "ticketgrp" && tixRows.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 9 }}>{tixRows.map(tixRowView)}</div>
              ) : null}
            </div>
          );
        })() : null}

        {c.type === "notice" ? <div style={{ border: `1px solid ${({ Neutral: "#E6E8EC", Info: "#C9DAFB", Warning: "#F3D9A4", Success: "#A7DACB" } as any)[bval(c, "tone")] ?? "#E6E8EC"}`, background: ({ Neutral: "#F4F5F7", Info: "#ECF1FE", Warning: "#FFFAED", Success: "#E6F4EE" } as any)[bval(c, "tone")] ?? "#F4F5F7", borderRadius: 8, padding: "9px 12px", fontSize: 11.5, textAlign: align as any, color: ({ Neutral: "#697180", Info: "#2D60EB", Warning: "#916D0F", Success: "#027D47" } as any)[bval(c, "tone")] ?? "#697180" }}>{bval(c, "icon") ? "◈ " : ""}{c.text}</div> : null}
        {c.type === "text" ? <div style={{ fontSize: bval(c, "size") === "Large" ? 15 : bval(c, "size") === "Small" ? 11 : 12.5, textAlign: align as any, color: "#333333", lineHeight: 1.55, padding: "2px 4px" }}>{c.text}</div> : null}
        {c.type === "button" ? (
          <div style={{ textAlign: "center" }}>
            <span style={{ display: bval(c, "full") ? "block" : "inline-block", padding: "9px 20px", border: `1.5px solid ${bval(c, "style") === "Text link" ? "transparent" : accent}`, background: bval(c, "style") === "Primary" ? accent : "transparent", borderRadius: 10, fontSize: 12.5, fontWeight: 700, color: bval(c, "style") === "Primary" ? "#FFFFFF" : accent }}>{c.text}</span>
            <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 4, ...MONO }}>→ {bval(c, "linkKind") === "Content page" ? (bval(c, "page") && bval(c, "page") !== "Pick a page…" ? bval(c, "page") : "no page picked") : (bval(c, "href") || "no link set")}</div>
          </div>
        ) : null}
        {c.type === "divider" ? <hr style={{ border: "none", borderTop: `1px ${String(bval(c, "style") ?? "Solid").toLowerCase()} #D1D4DC`, margin: ({ Tight: "0 8px", Normal: "4px 8px", Loose: "14px 8px" } as any)[bval(c, "space")] ?? "4px 8px" }} /> : null}
        {c.type === "spacer" ? <div style={{ height: ({ Small: 12, Medium: 24, Large: 48 } as any)[bval(c, "height")] ?? 24 }} /> : null}
        {c.type === "image" ? (
          <div>
            <div onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }} onDrop={dropImage(c)} style={{ height: ({ "Wide 16:9": 96, Square: 150, "Tall 4:5": 180, "Banner 3:1": 62 } as any)[bval(c, "ratio")] ?? 96, borderRadius: bval(c, "round") ? 8 : 0, background: bval(c, "src") ? `url(${bval(c, "src")}) center/cover` : "repeating-linear-gradient(45deg, #EDEDF2, #EDEDF2 8px, #F6F6FA 8px, #F6F6FA 16px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, color: "#697180", textAlign: "center", padding: "0 10px", ...MONO }}>{bval(c, "src") ? "" : "drop an image here, or add a URL on the right"}</div>
            {bval(c, "caption") ? <div style={{ fontSize: 10.5, color: "#697180", marginTop: 5, textAlign: "center" }}>{bval(c, "caption")}</div> : null}
          </div>
        ) : null}
        {c.type === "video" ? (
          <div>
            <div style={{ height: 110, borderRadius: 10, background: "#1F2430", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <span style={{ ...glyphBox("rgba(255,255,255,0.18)", "#FFFFFF", 34, 13), borderRadius: 999 }}>▶</span>
              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", ...MONO }}>{bval(c, "url") || "video — paste URL"}</span>
            </div>
            {bval(c, "caption") ? <div style={{ fontSize: 10.5, color: "#697180", marginTop: 5, textAlign: "center" }}>{bval(c, "caption")}</div> : null}
          </div>
        ) : null}
        {c.type === "gallery" ? (
          <div onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }} onDrop={dropImage(c)}>
            <div style={{ display: "grid", gridTemplateColumns: bval(c, "layout") === "Carousel" ? `repeat(${bval(c, "count") ?? 3}, 96px)` : `repeat(${Math.min(3, Number(bval(c, "count") ?? 3))}, 1fr)`, gap: 6, overflow: "hidden" }}>
              {Array.from({ length: Number(bval(c, "count") ?? 3) }).map((_, n) => {
                const u = String(bval(c, "urls") ?? "").split("\n").map((x) => x.trim()).filter(Boolean)[n] ?? "";
                return <span key={n} style={{ height: 54, borderRadius: 8, background: u ? `url(${u}) center/cover` : "repeating-linear-gradient(45deg, #EDEDF2, #EDEDF2 8px, #F6F6FA 8px, #F6F6FA 16px)" }} />;
              })}
            </div>
            <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 5, textAlign: "center", ...MONO }}>drop images here, or list URLs on the right</div>
          </div>
        ) : null}
        {c.type === "map" ? (() => {
          /* Falls back to the venue Setup · Details holds, so a map block never
             sits empty next to a page that already knows the address. */
          const authored = String(bval(c, "address") ?? "");
          const shown = authored || setupAddress || setupVenue;
          return (
            <div>
              <div style={{ height: 90, borderRadius: 10, border: "1px solid #E6E8EC", background: "repeating-linear-gradient(45deg, #EDEDF2, #EDEDF2 8px, #F6F6FA 8px, #F6F6FA 16px)", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "0 10px" }}>
                <span style={{ color: "#D01647", fontSize: 14 }}>◉</span>
                <span style={{ fontSize: 10.5, color: "#697180", ...MONO }}>{shown || "address — set it on the right, or in Setup · Details"}</span>
              </div>
              {!authored && shown ? <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 4, textAlign: "center", ...MONO }}>from Setup · Details</div> : null}
              {bval(c, "directions") ? <div style={{ fontSize: 11, color: "#5142C7", fontWeight: 700, marginTop: 5, textAlign: "center" }}>Get directions</div> : null}
            </div>
          );
        })() : null}
        {c.type === "faq" ? (
          <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "9px 12px", fontSize: 12, fontWeight: 600, borderBottom: "1px solid #F4F5F7" }}>{c.text}<span style={{ color: "#B5BAC6" }}>▾</span></div>
            {bval(c, "expand") ? <div style={{ padding: "0 12px 9px", fontSize: 11.5, color: "#697180", lineHeight: 1.5, borderBottom: "1px solid #F4F5F7" }}>{bval(c, "a1")}</div> : null}
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "9px 12px", fontSize: 12, fontWeight: 600 }}>{bval(c, "text2")}<span style={{ color: "#B5BAC6" }}>▾</span></div>
            {bval(c, "expand") ? <div style={{ padding: "0 12px 9px", fontSize: 11.5, color: "#697180", lineHeight: 1.5 }}>{bval(c, "a2")}</div> : null}
          </div>
        ) : null}
        {c.type === "quote" ? (
          <div style={{ borderLeft: "3px solid #C18FE8", padding: "2px 0 2px 12px" }}>
            <div style={{ fontSize: 13, fontStyle: "italic", color: "#333333", lineHeight: 1.5 }}>“{c.text}”</div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 6 }}>
              {bval(c, "photo") ? <span style={{ width: 22, height: 22, borderRadius: 999, background: "repeating-linear-gradient(45deg, #EDEDF2, #EDEDF2 8px, #F6F6FA 8px, #F6F6FA 16px)", flex: "none" }} /> : null}
              <span style={{ fontSize: 10.5, color: "#697180" }}>— {bval(c, "who")}</span>
            </div>
          </div>
        ) : null}
        {c.type === "stats" ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, textAlign: "center" }}>
            {[["s1v", "s1l"], ["s2v", "s2l"], ["s3v", "s3l"]].map(([v, l]) => (
              <span key={v} style={{ background: "#F4F5F7", borderRadius: 8, padding: "10px 4px" }}>
                <span style={{ display: "block", fontSize: 16, fontWeight: 800 }}>{bval(c, v!)}</span>
                <span style={{ display: "block", fontSize: 9.5, color: "#697180" }}>{bval(c, l!)}</span>
              </span>
            ))}
          </div>
        ) : null}
        {c.type === "countdown" ? (() => {
          /* Counts down to the event's real start. It used to print a fixed
             "04d 02h 14m" no matter what Setup · Schedule said. */
          const ms = startAt ? startAt.getTime() - Date.now() : 0;
          const d = ms > 0 ? Math.floor(ms / 86400000) : 0;
          const h = ms > 0 ? Math.floor((ms % 86400000) / 3600000) : 0;
          const mi = ms > 0 ? Math.floor((ms % 3600000) / 60000) : 0;
          const se = ms > 0 ? Math.floor((ms % 60000) / 1000) : 0;
          const pad = (n: number) => String(n).padStart(2, "0");
          const parts = bval(c, "units") === "Days / hours / min"
            ? [`${pad(d)}d`, `${pad(h)}h`, `${pad(mi)}m`]
            : [`${pad(d * 24 + h)}h`, `${pad(mi)}m`, `${pad(se)}s`];
          return (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", color: "#697180", textTransform: "uppercase", marginBottom: 6 }}>{bval(c, "label")}</div>
              {startAt ? (<>
                <div style={{ display: "inline-flex", gap: 6 }}>
                  {parts.map((x, n) => <span key={n} style={{ background: "#1F2430", color: "#FFFFFF", borderRadius: 6, padding: "6px 9px", fontSize: 14, fontWeight: 800, ...MONO }}>{x}</span>)}
                </div>
                <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 5, ...MONO }}>{ms > 0 ? `to ${setupDateLine}` : "the start date has passed"}</div>
              </>) : (
                <div style={{ border: "1px dashed #D1D4DC", borderRadius: 8, padding: "12px 10px", fontSize: 11.5, color: "#697180" }}>Set a start date in <b>Setup · Schedule</b> — the countdown runs to it.</div>
              )}
            </div>
          );
        })() : null}
        {c.type === "social" ? (
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            {NETS.filter((s) => bval(c, s[0])).map((s) => (
              <span key={s[0]} style={{ width: 28, height: 28, borderRadius: 999, border: "1px solid #D1D4DC", color: "#697180", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, position: "relative" }}>
                {s[2]}{bval(c, s[0] + "Url") ? <span style={{ position: "absolute", bottom: -1, right: -1, width: 8, height: 8, borderRadius: 999, background: "#027D47", border: "1.5px solid #FFFFFF" }} /> : null}
              </span>
            ))}
          </div>
        ) : null}

        {/* Event details — the date/venue line. Reads the event record LIVE and
            is never authored here; the block's own config only chooses which
            pieces show. Until this pass the block existed in the palette, the
            schema AND on every seeded Welcome page, but had no renderer at all,
            so nothing entered in Setup · Schedule ever reached the page. */}
        {/* Container (stage 4). Cells render through blockView itself, so a
            nested block gets the same selection ring, the same inspector and the
            same move/delete controls as one on the page body. */}
        {c.type === "columns" ? (() => {
          const cells = blockCells(c) ?? [[], []];
          const ratio = String(bval(c, "ratio") ?? "1-1");
          const cols = ratio === "2-1" ? "2fr 1fr" : ratio === "1-2" ? "1fr 2fr" : "1fr 1fr";
          const gap = Number(bval(c, "gap")) || 20;
          return (
            <div style={{ display: "grid", gridTemplateColumns: renderWidth < 520 && bval(c, "stackMobile") !== false ? "1fr" : cols, gap }}>
              {cells.map((cell, ci) => {
                const over = ui.dropCell === `${c.uid}:${ci}`;
                return (
                  <div key={ci}
                    onDragOver={(e) => { if (e.dataTransfer?.types?.includes(BLOCK_ADD_DND)) { e.preventDefault(); e.stopPropagation(); if (!over) set({ dropCell: `${c.uid}:${ci}` }); } }}
                    onDragLeave={() => { if (over) set({ dropCell: null }); }}
                    onDrop={(e) => {
                      const type = e.dataTransfer?.getData(BLOCK_ADD_DND);
                      set({ dropCell: null });
                      if (!type) return;
                      e.preventDefault(); e.stopPropagation();
                      const uid = njUid("c");
                      patchContent((list) => addToCell(list, c.uid, ci, { uid, type, text: CDEF[type] }));
                      set({ sel: uid });
                      showToast(`${type} block dropped into column ${ci + 1}`);
                    }}
                    style={{ minHeight: 64, borderRadius: 8, border: `1px ${cell.length ? "solid" : "dashed"} ${over ? "#5142C7" : cell.length ? "transparent" : "#D1D4DC"}`, background: over ? "#F6F2FE" : "transparent", padding: cell.length ? 0 : "6px 4px", display: "flex", flexDirection: "column", justifyContent: cell.length ? "flex-start" : "center" }}>
                    {cell.length
                      ? cell.map((child, k) => blockView(child, k, { inCell: true }))
                      : <span style={{ fontSize: 10.5, color: over ? "#5142C7" : "#B5BAC6", textAlign: "center", lineHeight: 1.5 }}>{over ? "Drop to add" : "Drag a block here"}</span>}
                  </div>
                );
              })}
            </div>
          );
        })() : null}

        {c.type === "eventdetails" ? (() => {
          const card = bval(c, "layout") === "Card";
          const showDate = bval(c, "showDate") !== false;
          const showLoc = bval(c, "showLocation") !== false;
          const rows = [
            showDate && (setupDateLine || setupTimeLine)
              ? { k: "when", glyph: "▦", title: setupDateLine || "Date not set", sub: setupTimeLine }
              : null,
            showLoc && (setupVenue || setupAddress || r0.virtualProvider)
              ? {
                  k: "where", glyph: "◉",
                  title: setupVenue || setupAddress || "Online",
                  sub: setupVenue && setupAddress ? setupAddress : (!setupVenue && !setupAddress && r0.virtualProvider ? "Join link sent when you register" : ""),
                }
              : null,
          ].filter(Boolean) as { k: string; glyph: string; title: string; sub: string }[];
          if (!rows.length) return (
            <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "14px 12px", fontSize: 11.5, color: "#697180", textAlign: "center", lineHeight: 1.55 }}>
              The date and venue appear here as soon as they exist — set them in <b>Setup · Schedule</b> and <b>Setup · Details</b>.
            </div>
          );
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: card ? 7 : 5 }}>
              {rows.map((r) => card ? (
                <div key={r.k} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 12px" }}>
                  <span style={glyphBox("#FDEBF3", "#D61A55")}>{r.glyph}</span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{r.title}</span>
                    {r.sub ? <span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{r.sub}</span> : null}
                  </span>
                </div>
              ) : (
                <div key={r.k} style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, lineHeight: 1.5 }}>
                  <span style={{ color: "#D61A55", flex: "none" }}>{r.glyph}</span>
                  <span style={{ minWidth: 0 }}>
                    <b style={{ fontWeight: 700 }}>{r.title}</b>
                    {r.sub ? <span style={{ color: "#697180" }}> · {r.sub}</span> : null}
                  </span>
                </div>
              ))}
              {showDate && setupRepeatLine ? <div style={{ fontSize: 10.5, color: "#697180", paddingLeft: card ? 2 : 20 }}>Repeats · {setupRepeatLine}</div> : null}
              {bval(c, "showAddToCalendar") !== false ? (
                <span style={{ alignSelf: "flex-start", marginTop: 3, border: `1.5px solid ${accent}`, borderRadius: 10, padding: "7px 16px", fontSize: 12, fontWeight: 700, color: accent }}>Add to calendar</span>
              ) : null}
              <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 1, ...MONO }}>live from Setup · Schedule and Setup · Details — not authored here</div>
            </div>
          );
        })() : null}

        {ui.sel === c.uid ? (
          <span style={{ position: "absolute", top: -13, right: 0, display: "inline-flex", gap: 2, background: "#5142C7", borderRadius: 6, padding: 2 }}>
            {!opts?.inCell && i > 0 ? <button onClick={(e) => { e.stopPropagation(); patchContent((list) => { const n = list.slice(); const a = n.findIndex((x) => x.uid === c.uid); const prev = n.slice(0, a).map((x, j) => [x, j] as [Block, number]).filter((pair) => pair[0].type !== "hero").pop(); if (!prev) return list; const pi = prev[1]; [n[pi], n[a]] = [n[a]!, n[pi]!]; return n; }); }} style={{ width: 20, height: 18, border: "none", background: "transparent", color: "#FFFFFF", fontSize: 10, cursor: "pointer" }}>↑</button> : null}
            {!opts?.inCell && i < contentBlocks.length - 1 ? <button onClick={(e) => { e.stopPropagation(); patchContent((list) => { const n = list.slice(); const a = n.findIndex((x) => x.uid === c.uid); if (a === n.length - 1) return list; [n[a + 1], n[a]] = [n[a]!, n[a + 1]!]; return n; }); }} style={{ width: 20, height: 18, border: "none", background: "transparent", color: "#FFFFFF", fontSize: 10, cursor: "pointer" }}>↓</button> : null}
            {!attKind ? <button onClick={(e) => {
              e.stopPropagation();
              const cells = blockCells(c);
              const inside = cells ? cells.reduce((n, cell) => n + cell.length, 0) : 0;
              if (inside && !window.confirm(`Delete this columns block and the ${inside} block${inside === 1 ? "" : "s"} inside it?`)) return;
              /* mapContent, not filter: a nested block lives inside a cell and
                 a flat filter would never find it. */
              patchContent((list) => mapContent(list, (x) => (x.uid === c.uid ? null : x)));
              set({ sel: null });
            }} style={{ width: 20, height: 18, border: "none", background: "transparent", color: "#FFFFFF", fontSize: 10, cursor: "pointer" }}>✕</button> : null}
          </span>
        ) : null}
      </div>
    );
  };

  const selB = ui.sel ? findContentBlock(p?.content ?? [], ui.sel) : null;
  const lockReason = D ? pageLock(D, curIdx) : null;
  /* Queues and appointment funnels resolve questions, surveys and promos from
     the SERVICE, so the journey-wide pickers are withdrawn (operator ruling
     2026-08-31). Events keep theirs: one event, one set, known at design time. */

  const inspector = (
    /* The editor pane, as a slide-out over the canvas rather than a third column
       squeezed beside it (operator, 2026-09-02). Same posture as Edit a Service:
       it OVERLAYS, so the page underneath keeps the width the author was checking
       at and does not reflow every time a block is selected. 420px — wide enough
       for a link group or a chip list, which 264 never was. */
    <div
      style={{
        position: "absolute", top: 0, right: 0, bottom: 0, zIndex: 20,
        width: inspW, maxWidth: "92%",
        background: "#FFFFFF", borderLeft: "1px solid #E6E8EC",
        boxShadow: "-10px 0 28px rgba(20,19,31,0.10)",
        padding: 14, paddingTop: 0, overflow: "auto",
        transform: ui.inspOpen ? "translateX(0)" : "translateX(100%)",
        transition: "transform 170ms ease",
      }}
    >
      <div style={{ position: "sticky", top: 0, zIndex: 1, background: "#FFFFFF", display: "flex", alignItems: "center", gap: 8, padding: "12px 0 10px", borderBottom: "1px solid #F4F5F7", marginBottom: 12 }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, flex: 1 }}>Editor</span>
        <button
          onClick={() => set({ inspOpen: false })}
          title="Minimize the editor"
          style={{ width: 26, height: 26, border: "1px solid #D1D4DC", borderRadius: 6, background: "#FFFFFF", color: "#697180", fontSize: 13, lineHeight: 1, cursor: "pointer", fontFamily: FONT }}
        >
          ›
        </button>
      </div>
      {(ui.sel === "core" || ui.sel === "heading" || ui.sel === null) && p ? (<>
        <div style={SEC}>Page text</div>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Heading</div>
        <input value={p.h} onChange={(e) => patchPage({ h: e.target.value })} style={{ ...INPUT, marginBottom: 8 }} />
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Subheading</div>
        <textarea value={p.s} onChange={(e) => patchPage({ s: e.target.value })} rows={2} style={{ ...INPUT, fontSize: 12, resize: "vertical", marginBottom: 12 }} />
        <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "0 0 12px" }} />
      </>) : null}

      {p?.core && (ui.sel === "core" || ui.sel === null) && !isConfirmPage ? (<>
        <div style={SEC}>{p.core} — settings</div>
        {p.toggles.map((t) => (
          <div key={t.k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, marginBottom: 9 }}>{t.label}
            <Switch on={t.on} onClick={() => patchPage({ toggles: p.toggles.map((x) => (x.k === t.k ? { ...x, on: !x.on } : x)) })} />
          </div>
        ))}
        {(CORE_CFG[p.id] ?? []).map((f) => (
          <div key={f[0]} style={{ marginBottom: 9 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{f[1]}</div>
            <input value={String(pval(f[0]) ?? "")} onChange={(e) => patchPage({ [f[0]]: e.target.value } as any)} style={{ ...INPUT, fontSize: 12 }} />
          </div>
        ))}
      </>) : null}

      {/* The commit button's wording — the ONLY part of the footer an author
          owns. Whether a page shows Back or Next is derived; what the button
          that creates the booking says is a real editorial decision, and it is
          per-page because a journey can commit somewhere unusual. Empty falls
          back to the journey default from Setup, which falls back to the type's
          own wording. */}
      {p && pageNav?.isCommit && (ui.sel === "core" || ui.sel === "heading" || ui.sel === null) ? (<>
        <div style={SEC}>Navigation</div>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Button label</div>
        <input value={String(p.commitLabel ?? "")} placeholder={commitLabelDefault} onChange={(e) => patchPage({ commitLabel: e.target.value } as any)} style={{ ...INPUT, fontSize: 12, marginBottom: 6 }} />
        <div style={NOTE}>This page's forward button is what creates the booking, so it says what that does rather than "Next". Leave it empty to use the journey's own default ({commitLabelDefault}).</div>
        <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "12px 0" }} />
      </>) : null}

      {p && (p.id === "services" || p.id === "apptsvc") && (ui.sel === "core" || ui.sel === null) ? (<>
        <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "4px 0 12px" }} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8 }}>
          <span style={{ ...SEC, marginBottom: 0 }}>{isApptPage ? "Appointment services offered" : "Services offered"}</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 10, color: "#B5BAC6" }}>{svcCatalogue.length} from Setup</span>
        </div>
        {/* READ-ONLY MIRROR of Setup (operator, 2026-09-02). This used to be a
            second editable list, which is why it needed a drift warning and a
            reconcile button: a page could quietly offer something Setup did not.
            The page now shows what Setup offers, in Setup's order, and the only
            way to change it is the one picker on the setup step. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 10, maxHeight: 250, overflow: "auto" }}>
          {svcCatalogue.length ? svcCatalogue.map((s) => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid #EEF0F3", background: "#FBFBFC", borderRadius: 7, padding: "7px 9px" }}>
              <span style={{ flex: 1, minWidth: 0 }}><span style={{ display: "block", fontSize: 11.5, fontWeight: 600 }}>{s.name}</span>{s.dept ? <span style={{ display: "block", fontSize: 10, color: "#697180" }}>{s.dept}</span> : null}</span>
              <span style={{ fontSize: 10, color: "#B5BAC6", flex: "none" }}>{s.wait}</span>
            </div>
          )) : <div style={{ border: "1px dashed #D1D4DC", borderRadius: 7, padding: "12px 10px", fontSize: 11, color: "#697180" }}>{isApptPage ? "No bookable services in the catalogue yet." : "This journey isn't attached to a queue — its services come from the queue."}</div>}
        </div>
        <button onClick={() => set({ setupView: isApptPage ? "aservices" : "qservices", sel: null })} style={{ ...GHOST, width: "100%" }}>Change what's offered in Setup →</button>
      </>) : null}

      {/* Registration settings moved to Setup · Registration (2026-08-31). They
          are event RECORD fields — approval, guest limits, which contact fields
          are captured — and they were being edited from a page inspector, which
          is the duplicate authoring surface this pass removes. The page still
          RENDERS them; it no longer owns them. */}
      {p?.id === "register" && (ui.sel === "core" || ui.sel === null) ? (<>
        <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "4px 0 12px" }} />
        <div style={SEC}>Registration</div>
        <div style={{ border: "1px dashed #C9C2EE", background: "#FBFAFF", borderRadius: 8, padding: "10px 11px", fontSize: 11, lineHeight: 1.55, color: "#5142C7" }}>
          {(r0.standardFields ?? ["email"]).join(" · ")}{r0.approvalMode ? " · approval required" : ""}{r0.maxGuests ? ` · up to ${r0.maxGuests} guests` : ""}
          <br />
          <a href="#" onClick={(e) => { e.preventDefault(); set({ setupView: "registration" }); }}>Edit in Setup · Registration →</a>
        </div>
      </>) : null}

      {lockReason ? <div style={{ ...WARN, marginTop: 12, marginBottom: 12 }}>🔒 <b>Required page.</b> {lockReason} Invariants are enforced, not advised.</div> : null}

      {/* Section scope. A section is not a block, so it gets its own scope
          rather than being squeezed into the block inspector — what it renders
          comes from the booking, and the only things an author sets are the
          three page fields below. */}
      {isOneStopPage && ui.selSection ? (() => {
        const block = osBlocks.find((b) => b.id === ui.selSection);
        if (!block) return null;
        return (
          <OneStopInspector
            block={block as any}
            locale={p?.osSettings?.locale ?? "en-US"}
            libs={osLibs}
            takenIds={osBlocks.map((b) => b.id)}
            onPatchConfig={(config) => setOsBlocks(osBlocks.map((b) => (b.uid === block.uid ? { ...b, config } : b)))}
            onRename={(next) => {
              const trimmed = next.trim();
              if (!trimmed || trimmed === block.id) return;
              setOsBlocks(osBlocks.map((b) => (b.uid === block.uid ? { ...b, id: trimmed } : b)));
              set({ selSection: trimmed });
            }}
            onVisibility={(visibility) =>
              setOsBlocks(osBlocks.map((b) => (b.uid === block.uid ? { ...b, visibility: visibility === "always" ? undefined : visibility } : b)))
            }
          />
        );
      })() : null}

      {/* ui.selSection holds a section ROW UID (2026-09-01), not a section id —
          the inspector still keys off the registry, so the uid resolves first. */}
      {isConfirmPage && csState && ui.selSection && csAt(csState, ui.selSection) ? (
        <CSInspector
          id={csAt(csState, ui.selSection)!.type}
          type={csType}
          fields={{ bannerTitle: p?.bannerTitle, cancelHours: p?.cancelHours, note: p?.note, surveyPrompt: p?.surveyPrompt }}
          /* Spread rather than pass through: `NJPage` carries an index
             signature (`[k: string]: unknown`, nj-model.ts), so `Partial<NJPage>`
             wants one too — and TypeScript gives an implicit index signature to
             an anonymous object type but never to an `interface`, which is what
             `CSPageFields` is. Spreading keeps every field and its type; it is
             not a cast, and nothing is widened or dropped. */
          onPatch={(patch) => patchPage({ ...patch })}
          hidden={!!csAt(csState, ui.selSection)?.hidden}
          onToggle={() => setSections(csToggle(csState, ui.selSection!))}
        />
      ) : null}

      {isConfirmPage && csState && !ui.selSection ? (
        <div style={{ ...NOTE, marginTop: 12 }}>
          This page is built from <b>sections</b>, not settings — pick one on the left to see what it renders and what you can set. Blocks you add sit below them.
        </div>
      ) : null}

      {selB ? (<>
        <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "4px 0 12px" }} />
        <div style={SEC}>{({ qgroup: "Booking questions", survey: "Survey", promo: "Promo", ticketgrp: "Ticket group" } as any)[selB.type] ?? selB.type} block</div>
        {blockVariant(selB.type, "page") ? (
          <div style={{ ...NOTE, marginTop: -4, marginBottom: 10 }}>{blockVariant(selB.type, "page")}</div>
        ) : null}
        {ATTACH_TYPES[selB.type] ? (
          <button onClick={() => set({ lib: ATTACH_TYPES[selB.type] as LibKind })} style={{ ...GHOST, width: "100%", marginBottom: 10 }}>Pick from the library →</button>
        ) : null}
        {cfgFor(selB).map((f) => {
          const kind = f[2]; const k = f[0]; const v = bval(selB, k);
          if (kind === "tog") return <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, marginBottom: 9 }}>{f[1]}<Switch on={!!v} onClick={() => setBval(selB.uid, k, !v)} /></div>;
          if (kind === "area") return <div key={k} style={{ marginBottom: 9 }}><div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{f[1]}</div><textarea value={String(v ?? "")} onChange={(e) => setBval(selB.uid, k, e.target.value)} rows={2} style={{ ...INPUT, fontSize: 12, resize: "vertical" }} /></div>;
          // Block config picker: the design system's Dropdown, not the browser's
          // native select (operator, 2026-09-02) — same ruling as the list
          // toolbar's Sort. `portal` because the inspector body scrolls.
          if (kind === "sel") return <div key={k} style={{ marginBottom: 9 }}><div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{f[1]}</div><Dropdown size="sm" portal aria-label={String(f[1])} value={String(v ?? "")} onChange={(nv: string) => setBval(selB.uid, k, nv)} options={(f[3] ?? []).map((o) => ({ value: o, label: o }))} style={{ width: "100%" }} /></div>;
          return <div key={k} style={{ marginBottom: 9, opacity: f[4] ? 0.45 : 1 }}><div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{f[1]}</div><input value={String(v ?? "")} onChange={(e) => setBval(selB.uid, k, e.target.value)} placeholder={f[4] ? "switch it on first" : ""} style={{ ...INPUT, fontSize: 12 }} /></div>;
        })}
      </>) : null}

      {/* The ATTACHMENTS section is gone (operator ruling, 2026-09-02).
          It summarised booking questions, the survey, a coupon and the ticket
          group — but Setup owns every one of those, and three of the four are
          properties of the SERVICE or the EVENT rather than of the journey. A
          second place to read them was a second place for them to drift, and the
          section's own copy had already drifted: it described a journey-wide
          survey and a journey-wide coupon, neither of which is how they are set.
          Tickets were the one row that was still true, and Setup · Tickets says
          it better — that picker writes to the event record. */}
    </div>
  );

  /** PAGES row — always visible beside SETUP (2026-08-24): a page pill jumps
   *  straight to that page's canvas, whatever view you were in. */
  const pagesStrip = D ? (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#FFFFFF", borderBottom: "1px solid #E6E8EC", overflowX: "auto", flexShrink: 0 }}>
      <span style={{ ...SEC, marginBottom: 0, marginRight: 4, flex: "none" }}>Pages</span>
      {D.pages.map((pg, i) => {
        const locked = !!pageLock(D, i);
        const on = i === curIdx && !ui.setupView && ui.view === "pages";
        return (
          <div key={String(pg.uid ?? i)} onClick={() => set({ cur: i, sel: null, selSection: null, simCalled: false, setupView: null, view: "pages" })} draggable onDragStart={(e) => { e.dataTransfer?.setData("text/plain", ""); set({ dragPg: i }); }} onDragOver={(e) => { e.preventDefault(); if (ui.dragOver !== i) set({ dragOver: i }); }} onDrop={(e) => { e.preventDefault(); movePage(ui.dragPg, i); }} onDragEnd={() => set({ dragPg: null, dragOver: null })}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 11px", borderRadius: 999, border: `1px solid ${ui.dragOver === i ? "#C18FE8" : on ? "#5142C7" : "#E6E8EC"}`, background: ui.dragOver === i ? "#EDE5FE" : on ? "#F6F2FE" : "#FFFFFF", cursor: "grab", flex: "none" }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: pg.color, flex: "none" }} />
            <span style={{ fontSize: 11.5, fontWeight: 700, color: on ? "#5142C7" : "#333333", whiteSpace: "nowrap" }}>{pg.label}</span>
            {on && D.pages.length > 1 && !locked ? <button onClick={(e) => { e.stopPropagation(); removePage(i); }} style={{ border: "none", background: "transparent", color: "#697180", fontSize: 10, cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button> : null}
            {on && locked ? <button onClick={(e) => { e.stopPropagation(); showToast(`Blocked — ${pageLock(D, i)} Core invariants are enforced, not advised.`); }} style={{ border: "none", background: "transparent", fontSize: 9, cursor: "not-allowed", padding: 0, lineHeight: 1 }}>🔒</button> : null}
          </div>
        );
      })}
      {/* "+ Add page" only where the type's pages are the author's to choose.
          Three cases are not:
           · a Display Board and a One-Stop page are single surfaces — the whole
             experience IS that one page (`hasFlow`);
           · a QUEUE journey's pages are seeded on creation and are the set we
             want (operator, 2026-09-02) — same ruling that removed the starter
             picker, since both offered to change a page set the template owns.
          Removing the control does not lock the journey down: pages can still be
          removed, reordered and edited, and the Flow canvas still adds steps.
          What is gone is inventing a page the type has no place for. */}
      {hasFlow && !isQueueJourney ? (
        <button onClick={() => set({ typePicker: { at: null } })} style={{ height: 28, padding: "0 12px", border: "1px dashed #B9A8F0", borderRadius: 999, background: "#FCFBFF", color: "#5142C7", fontSize: 11.5, fontWeight: 700, cursor: "pointer", flex: "none", fontFamily: FONT }}>+ Add page</button>
      ) : null}
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 10.5, color: "#B5BAC6", flex: "none", ...MONO }}>{!hasFlow ? "one page — this type has no others" : isQueueJourney ? "these pages come with the queue" : ""}</span>
    </div>
  ) : null;

  const pagesView = D && p ? (
    <>

      <div ref={designerRowRefCb} style={{ flex: 1, display: "flex", minHeight: 0, position: "relative" }}>
        <div style={{ width: 212, background: "#FFFFFF", borderRight: "1px solid #E6E8EC", padding: 14, overflow: "auto", flexShrink: 0 }}>
          {/* Sections sit ABOVE the block palette (operator ruling 2026-08-31):
              on a confirmation page they are the page, and blocks are the
              extra an author may add. */}
          {isOneStopPage ? (
            <OSRail blocks={osBlocks} />
          ) : null}
          {isConfirmPage && csState ? (
            <CSRail state={csState} type={csType} sel={ui.selSection}
              onSelect={(id) => set({ selSection: id, sel: id ? null : ui.sel })}
              onChange={setSections} />
          ) : null}
          {/* The designer's own copy-block palette and its Core block card are
              hidden on a One-Stop page: its content blocks (Notice, Button, FAQ,
              Quote, Figures, Social, Spacer) are already in the One-Stop palette
              above, typed by the registry the publish validates against, and a
              second palette offering near-identical names that save to a
              different array is the definition of redundant. There is no core
              block either — the page IS its blocks. */}
          {!isOneStopPage ? (<>
          <div style={SEC}>Blocks</div>
          {/* One column, not two. Each entry now carries a checkbox, a label and
              a count; at two columns in a 212px rail that is ~95px per tile and
              the labels ellipsised away. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 18 }}>
            {blocksFor(p.id).map((bd) => {
              /* Top-level only: a Text block dropped into a column must not
                 make the palette's Text entry read as "already on the page",
                 and unchecking it then must not reach into the column. */
              const mine = contentBlocks.filter((c) => c.type === bd.type);
              const on = mine.length > 0;
              /* A CHECKBOX, checked ⇄ unchecked (operator, 2026-09-03). Click-to-add
                 alone left no way back from the palette — the tile went checked and
                 stayed checked, so the control could be set but never unset.
                 Unchecking removes the LAST block of that type rather than every
                 one at once: a repeatable type can hold several blocks of authored
                 copy, and one click should never take more than one of them. The
                 count says how many are there, so the consequence is legible
                 before the press. Drag still places a block somewhere other than
                 the end; the × on the block is still there too. */
              const toggle = () => {
                if (!on) { insertTypeAt(bd.type, contentBlocks.length); return; }
                const last = mine[mine.length - 1]!;
                removeBlock(last.uid);
              };
              return (
                <button key={bd.type}
                  draggable
                  onClick={toggle}
                  onDragStart={(e) => { e.dataTransfer?.setData(BLOCK_ADD_DND, bd.type); if (e.dataTransfer) e.dataTransfer.effectAllowed = "copy"; }}
                  onDragEnd={() => set({ dropCell: null })}
                  title={on
                    ? `${bd.label} · ${mine.length} on this page. Click to take the last one off, or drag to add another where you choose.`
                    : `Click to add ${bd.label} at the end, or drag onto the page to place it.`}
                  style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 8px", border: `1px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#F6F2FE" : "#FFFFFF", color: on ? "#5142C7" : "#333333", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: FONT, textAlign: "left" }}>
                  <span style={{ width: 14, height: 14, borderRadius: 4, border: `1px solid ${on ? "#5142C7" : "#C2C7D2"}`, background: on ? "#5142C7" : "#FFFFFF", color: "#FFFFFF", fontSize: 9, lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{on ? "✓" : ""}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{bd.label}</span>
                  {mine.length > 1 ? <span style={{ fontSize: 9.5, fontWeight: 800, color: "#5142C7", flex: "none", ...MONO }}>{mine.length}</span> : null}
                </button>
              );
            })}
          </div>
          <div style={SEC}>Core block</div>
          {p.core
            ? <div style={{ border: "1px solid #DCCCFC", background: "#F6F2FE", borderRadius: 8, padding: 10, fontSize: 11, lineHeight: 1.5, color: "#333333", marginBottom: 18 }}><b style={{ color: "#5142C7" }}>{p.core}</b><br />Seeded with the page — one per page, restorable if deleted. Carries the page's function.</div>
            : <div style={{ border: "1px dashed #D1D4DC", background: "#FAFAFD", borderRadius: 8, padding: 10, fontSize: 11, lineHeight: 1.5, color: "#697180", marginBottom: 18 }}>Content page — no core. Compose it entirely from blocks.</div>}
          </>) : null}
          <div style={SEC}>Attached to</div>
          <div style={{ border: "1px solid #E6E8EC", borderRadius: 8, padding: "9px 10px", fontSize: 11, lineHeight: 1.5, color: "#333333" }}>
            {D.origin?.kind === "local" ? <span style={{ color: "#697180" }}>{D.origin.label}</span> : (<>
              <b>{D.origin?.label}</b><br />
              <a href="#" onClick={(e) => { e.preventDefault(); if (D.origin?.route) goRoute(D.origin.route, D.origin.params); }}>Open the {D.origin?.kind} →</a>
            </>)}
            <span style={{ display: "block", marginTop: 6, color: "#697180" }}>
              {WRITES_BACK(D.origin?.kind)
                ? "Renaming this journey, its hero image and its services save onto the queue itself."
                : "This journey's own pages and flow are saved here."}
            </span>
          </div>
        </div>

        <div ref={canvasScrollRef} style={{ flex: 1, background: "#F1F2F6", overflow: "auto", padding: "18px 20px 30px", paddingRight: ui.inspOpen && reserveInsp ? INSP_MAX + 20 : 20, minWidth: 0, position: "relative", display: "flex", flexDirection: "column" }}>
          {p.id === "waiting" ? (
            <button onClick={() => set({ simCalled: !ui.simCalled })} style={{ position: "absolute", top: 14, right: 20, zIndex: 5, height: 32, padding: "0 14px", borderRadius: 999, border: `1.5px solid ${ui.simCalled ? "#5142C7" : "#D1D4DC"}`, background: ui.simCalled ? "#F6F2FE" : "#FFFFFF", color: ui.simCalled ? "#5142C7" : "#333333", fontSize: 12, fontWeight: 700, cursor: "pointer", boxShadow: "0 1px 3px rgba(20,19,31,0.1)", fontFamily: FONT }}>{ui.simCalled ? "‹ Back to waiting" : "Preview: It's your turn"}</button>
          ) : null}
          {!isBoardJourney ? <CanvasWidthBar width={pageWidth} presets={WIDTH_PRESETS} /> : null}
          {/* minWidth 0: without it the centering row's `auto` minimum lets the
              flex algorithm drive it below its children, and the two drag
              handles then squeeze the column out. */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", flex: 1, minWidth: 0 }}>
          {!isBoardJourney ? <CanvasEdge width={pageWidth} side={-1} /> : null}
          <CanvasColumn width={pageWidth}>
          <div className="nj-page" style={{ width: "100%", margin: "0 auto", background: "#FFFFFF", borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 10px rgba(20,19,31,0.08)" }}>
            {missingChips.length ? (
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, padding: "10px 14px", background: "#FBFAFF", borderBottom: "1px dashed #C9C2EE" }}>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", color: "#5142C7", textTransform: "uppercase" }}>Still needed</span>
                {missingChips.map((c) => (
                  <button key={c.label} onClick={() => set({ setupView: c.to, pagesOpen: true })} title={`Set this in Setup · ${c.to === "basics" ? "Basics" : "Schedule"}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, border: "1px dashed #A9A0DE", background: "#FFFFFF", color: "#5142C7", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{c.label} →</button>
                ))}
              </div>
            ) : null}
            {heroBlock ? (
              <div onClick={() => set({ sel: ui.sel === heroBlock.uid ? null : heroBlock.uid })} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }} onDrop={dropImage(heroBlock)} className="nj-block nj-hero" style={{ margin: "14px 14px 12px", position: "relative", cursor: "pointer", outline: `2px solid ${ui.sel === heroBlock.uid ? "#5142C7" : "transparent"}`, outlineOffset: 2, borderRadius: 10 }}>
                <div style={{ borderRadius: 10, background: heroImg ? `linear-gradient(rgba(20,19,31,0.45), rgba(20,19,31,0.45)), url(${heroImg}) center/cover` : (({ Gradient: "linear-gradient(135deg, #75ADFA, #5142C7)", "Solid brand": accent, Photo: "repeating-linear-gradient(45deg, #3B3660, #3B3660 8px, #464070 8px, #464070 16px)" } as any)[bval(heroBlock, "bg")] ?? "linear-gradient(135deg, #75ADFA, #5142C7)"), padding: ({ Compact: "14px 16px", Standard: "18px 16px", Tall: "34px 16px" } as any)[bval(heroBlock, "height")] ?? "18px 16px", color: "#FFFFFF" }}>
                  <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.01em" }}>{heroBlock.text}</div>
                  {bval(heroBlock, "sub") ? <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 5, lineHeight: 1.45 }}>{bval(heroBlock, "sub")}</div> : null}
                  {bval(heroBlock, "cta") ? <span style={{ display: "inline-block", marginTop: 10, background: "#FFFFFF", color: "#5142C7", borderRadius: 999, padding: "6px 14px", fontSize: 11, fontWeight: 700 }}>{bval(heroBlock, "cta")}</span> : null}
                  {!heroImg ? <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.6)", marginTop: 9, ...MONO }}>drop an image here for the background</div> : null}
                </div>
                {ui.sel === heroBlock.uid ? (
                  <span style={{ position: "absolute", top: -13, right: 0, display: "inline-flex", gap: 2, background: "#5142C7", borderRadius: 6, padding: 2 }}>
                    <span style={{ padding: "0 6px", height: 18, color: "#FFFFFF", fontSize: 8.5, fontWeight: 800, letterSpacing: "0.05em", display: "inline-flex", alignItems: "center" }}>PINNED TO TOP</span>
                    <button onClick={(e) => { e.stopPropagation(); patchContent((list) => list.filter((x) => x.type !== "hero")); set({ sel: null }); }} style={{ width: 20, height: 18, border: "none", background: "transparent", color: "#FFFFFF", fontSize: 10, cursor: "pointer" }}>✕</button>
                  </span>
                ) : null}
              </div>
            ) : null}
            <div onClick={() => set({ sel: ui.sel === "heading" ? null : "heading", selSection: null })} className="nj-heading" style={{ padding: "18px 20px 6px", cursor: "pointer", outline: `2px solid ${ui.sel === "heading" ? "#5142C7" : "transparent"}`, outlineOffset: -2, borderRadius: 4 }}>
              <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.01em" }}>{p.h}</div>
              {p.s ? <div style={{ fontSize: 12.5, color: "#697180", marginTop: 4 }}>{p.s}</div> : null}
            </div>
            {core()}
            {/* A One-Stop page IS its blocks — no core, and NOT the designer's block
                vocabulary either. Its canvas REPLACES the shared one rather than
                sitting beside it: both listen for the same drag payload, so two
                sets of insertion lines on one page would race for every drop and
                the loser would silently write into the wrong array. */}
            {isOneStopPage ? (
              <div style={{ padding: "0 14px 8px" }}>
                <OSCanvas
                  blocks={osBlocks}
                  rendered={osRendered}
                  locale={p.osSettings?.locale ?? "en-US"}
                  timeZone={p.osSettings?.timeZone ?? tzContext().zone}
                  subtitle={p.s}
                  sel={ui.selSection}
                  onSelect={(id) => set({ selSection: id, sel: id ? null : ui.sel })}
                  onChange={setOsBlocks}
                  onInsert={osInsertAt}
                />
              </div>
            ) : (
            <BlockCanvas
              blocks={contentBlocks.map((c) => ({ uid: String(c.uid), id: String(c.uid), block: c }))}
              sel={ui.sel}
              labelOf={(b) => String(BLOCK_DEF[b.block.type]?.label ?? b.block.type)}
              onSelect={(id) => set({ sel: id, selSection: id ? null : ui.selSection })}
              onMove={(from, to) => {
                const next = contentBlocks.slice();
                const [moved] = next.splice(from, 1);
                next.splice(to, 0, moved!);
                writeBlocks(next);
              }}
              onInsert={insertTypeAt}
              onRemove={(uid) => removeBlock(uid)}
              renderBlock={(b, i) => blockView(b.block, i)}
              emptyState={
                <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "18px 14px", textAlign: "center", fontSize: 11.5, color: "#8A92A0", lineHeight: 1.55 }}>
                  No blocks yet. Drag one from the palette to place it.
                </div>
              }
            />
            )}
            <div style={{ height: 6 }} />
            {stepNavFooter()}
          </div>
          <div style={{ margin: "10px auto 0", textAlign: "center", fontSize: 10.5, color: "#B5BAC6", ...MONO }}>
            {[
              "a preview — the published page uses your brand's styles",
            ].filter(Boolean).join(" · ")}
          </div>
          </CanvasColumn>
          {!isBoardJourney ? <CanvasEdge width={pageWidth} side={1} /> : null}
          </div>
        </div>

        {inspector}
        {/* The way back in. A tab rather than a re-opening of the whole pane on
            selection: an author who minimized it did so on purpose, and having it
            spring back at the next click is the behaviour that made them close
            it. */}
        {!ui.inspOpen ? (
          <button
            onClick={() => set({ inspOpen: true })}
            title="Open the editor"
            style={{ position: "absolute", top: 14, right: 0, zIndex: 19, height: 34, padding: "0 12px 0 10px", border: "1px solid #E6E8EC", borderRight: "none", borderRadius: "8px 0 0 8px", background: "#FFFFFF", color: "#5142C7", fontSize: 11.5, fontWeight: 700, cursor: "pointer", boxShadow: "-4px 0 12px rgba(20,19,31,0.08)", fontFamily: FONT }}
          >
            ‹ Editor
          </button>
        ) : null}
      </div>
    </>
  ) : null;

  /* ── Flow view ─────────────────────────────────────────────────────
   *  The ONE modeler (2026-08-25). This used to be a bespoke vertical strip
   *  with the single decision's branches in two fixed columns. It now mounts
   *  JourneyFlowBuilder — the canvas promoted out of screens/queues — so the
   *  Journey Designer and the queue flows page are the same editor, not two
   *  that resemble each other.
   *
   *  Three seams keep it honest:
   *    · vocab  — qfVocabFor(doc.type) picks this interaction type's triggers,
   *               decision conditions, end outcomes and wait timings.
   *    · pages  — journeyPages() supplies the page-type registry, a miniature
   *               that renders the document's real page, and a callback that
   *               switches to the Pages view (journey pages are built there,
   *               not in a modal).
   *    · flow   — the document IS the tree now (Doc.flow). njFlowOf only
   *               migrates a document stored in the old three-list shape, so
   *               there is no round-trip left to lose a third path in.
   */
  const flowVocab = qfVocabFor(D?.type);
  const flowServices = [
    ...queueCat.map((s: any) => ({ id: s.id, name: s.name, wait: s.wait })),
    ...apptCat.map((s: any) => ({ id: s.id, name: s.name, wait: s.wait, appt: true })),
  ];
  const flowPages = D
    ? journeyPages(D, (idx) => set({ view: "pages", cur: Math.max(0, idx), sel: null }), flowServices, {
        /* Same setup mirrors the Pages view reads, so a flow miniature can't
           show tickets, questions or a date the record doesn't have. */
        tickets: tixRows.map((t) => ({ name: t.name, price: t.kind === "free" || t.kind === "invite" ? "Free" : tgMoney(t.price) })),
        questions: attQuestions.map((q) => q.label),
        when: [setupDateLine, setupTimeLine].filter(Boolean).join(" · "),
        where: [setupVenue, setupAddress].filter(Boolean).join(" · "),
      })
    : null;
  const flowView = D && flowPages ? (
    <div style={{ flex: 1, display: "flex", minHeight: 0, padding: 12, background: "#F4F5F7" }}>
      <JourneyFlowBuilder
        flow={qfFlow}
        vocab={flowVocab}
        pages={flowPages}
        heroImage={(D as { heroImage?: string }).heroImage || undefined}
        onFlow={(next) => patchDoc((d) => ({ ...d, flow: next }))}
      />
    </div>
  ) : null;

  /* ── Setup views (stage 3) — jrni-ui controls inside the ported chrome,
   *  per the build-plan ruling: chrome stays as ported, form controls don't. */
  const SETUP_LABEL: Record<string, string> = {
    basics: "Basics", details: "Details", schedule: "Schedule", tickets: "Tickets", guests: "Guests", messaging: "Messaging", publish: "Publish",
    qsetup: "Setup", qhours: "Opening hours", qstaffing: "Staffing", qresources: "Resources", qservices: "Services", boardcfg: "Configuration", onestopcfg: "Configuration",
    registration: "Registration",
    ...APPT_SETUP_LABEL,
  };
  const setupFieldLabel = { fontSize: 12, fontWeight: 600, color: "#333333", display: "block" as const, marginBottom: 5 };


  /** What a Setup step requires, and whether each one is met — the single
   *  answer to "which fields turn this dot green". Reads the SAME readiness
   *  rows the dots and the Publish gate read (eventReadiness / queueReadiness /
   *  boardReadiness), so the three surfaces cannot disagree. An empty array
   *  means the step requires nothing and carries no dot.
   *
   *  Declared HERE, above the setup panels, because those panels now read it to
   *  mark their own fields — they are built eagerly, so a definition further
   *  down the render would be a temporal-dead-zone error rather than a lint. */
  const requiredForView = (view: string): { label: string; done: boolean }[] => {
    if (isApptJourney) return aChecks.filter((c) => c.tab === view).map((c) => ({ label: c.label, done: c.done }));
    if (isBoardJourney) return view === "boardcfg" ? bChecks.map((c) => ({ label: c.label, done: c.done })) : [];
    if (isOneStopJourney) return view === "onestopcfg" ? osChecks : [];
    if (isQueueJourney) return qChecks.filter((c) => c.tab === view).map((c) => ({ label: c.label, done: c.done }));
    return readiness.filter((c) => c.step === view).map((c) => ({ label: c.label, done: c.done }));
  };

  /** Is THIS field the reason the step's dot is red? Matched on the readiness
   *  row's label, which is the same string the field's own label carries — so a
   *  renamed field that stops matching goes quiet rather than marking the wrong
   *  control. */
  const reqMiss = (label: string): boolean =>
    requiredForView(ui.setupView ?? "").some((c) => c.label === label && !c.done);

  /** A field label that shows its own error. Replaces the summary block that
   *  used to sit at the top of the panel listing what was missing: a list one
   *  scroll above the fields is a second place to read the same fault, and it
   *  pulled the eye AWAY from where the fix happens (operator, 2026-09-02). */
  const reqLabel = (label: string, why?: string) => {
    const bad = reqMiss(label);
    return (
      <label style={{ ...setupFieldLabel, color: bad ? "#C0392B" : "#333333" }}>
        {label}
        <span title={why ?? "Required — this step's dot stays red until it's filled"} style={{ color: bad ? "#C0392B" : "#D01647", fontWeight: 700, whiteSpace: "nowrap" }}>{"\u00A0*"}</span>
        {bad ? <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "#C0392B" }}>Needs a value</span> : null}
      </label>
    );
  };

  /** Marks the whole field — label and control — when it is the missing one, so
   *  the eye lands on the input rather than only on its wording. A rule down the
   *  left edge rather than a border on the control itself: the controls are
   *  shared components, and re-bordering them from outside fights their own
   *  focus ring. */
  const reqField = (label: string, node: ReactNode) =>
    reqMiss(label)
      ? <div style={{ borderLeft: "3px solid #C0392B", paddingLeft: 11, marginLeft: -14, borderRadius: 2 }}>{node}</div>
      : <div>{node}</div>;

  const setupCard = { maxWidth: 560, display: "flex", flexDirection: "column" as const, gap: 16 };
  /** Required-field marker. A field wearing this is one the step's readiness
   *  row reads — fill every marked field in a step and its dot turns green.
   *  Same red as the queue/board labels that already carried an asterisk. */
  const req = (why?: string, label?: string) => {
    /* When a label is given, the marker also reports whether THIS field is the
       one holding the step back. Without one it is only the asterisk — which is
       right for a rule spanning two fields ("a ticket type OR a ticket group"),
       where reddening both would name the wrong number of faults. */
    const bad = !!label && reqMiss(label);
    return (
      <span title={why ?? "Required — this step's dot stays red until it's filled"} style={{ color: bad ? "#C0392B" : "#D01647", fontWeight: 700, whiteSpace: "nowrap" }}>{"\u00A0*"}</span>
    );
  };

  const tags = r0.tags ?? [];
  const addTag = () => {
    const v = tagDraft.trim();
    if (!v || tags.includes(v)) { setTagDraft(""); return; }
    const next = [...tags, v];
    patchRecord((x) => ({ ...x, tags: next }));
    if (eventOriginId) patchEventBasics(eventOriginId, { tags: next });
    setTagDraft("");
  };
  const setupBasics = (
    <div style={setupCard}>
      <div>{reqLabel("Event title")}<Input value={r0.title ?? ""} onChange={(e: any) => { const v = e.target.value; patchRecord((x) => ({ ...x, title: v })); if (!eventOriginId) patchDoc((d) => ({ ...d, name: v })); }} onBlur={() => eventOriginId && patchEventBasics(eventOriginId, { title: r0.title })} placeholder="Give your event a clear, scannable title" /></div>
      <div>{reqLabel("Description")}<textarea value={r0.description ?? ""} onChange={(e) => patchRecord((x) => ({ ...x, description: e.target.value }))} onBlur={() => eventOriginId && patchEventBasics(eventOriginId, { description: r0.description })} rows={4} style={{ ...INPUT, resize: "vertical" as const }} placeholder="What should people know before they register?" /></div>
      <div><label style={setupFieldLabel}>Hero image URL</label><Input value={r0.heroImage ?? ""} onChange={(e: any) => patchRecord((x) => ({ ...x, heroImage: e.target.value }))} onBlur={() => eventOriginId && patchEventBasics(eventOriginId, { heroImage: r0.heroImage })} placeholder="https://… — shown on the landing page, comms and the card grid" /></div>
      <div>
        <label style={setupFieldLabel}>Tags</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {tags.map((tag) => (
            <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, border: "1px solid #D1D4DC", fontSize: 11.5, color: "#333333" }}>{tag}
              <button onClick={() => { const next = tags.filter((x) => x !== tag); patchRecord((x) => ({ ...x, tags: next })); if (eventOriginId) patchEventBasics(eventOriginId, { tags: next }); }} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
            </span>
          ))}
        </div>
        <Input value={tagDraft} onChange={(e: any) => setTagDraft(e.target.value)} onKeyDown={(e: any) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder="Add tag — press Enter" />
      </div>
      <div>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>Card preview</div>
        <div
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setHeroDrop(true); }}
          onDragLeave={() => setHeroDrop(false)}
          onDrop={(e) => {
            e.preventDefault(); e.stopPropagation(); setHeroDrop(false);
            const files = Array.from<File>(e.dataTransfer?.files ?? []).filter((f) => /^image\//.test(f.type));
            const url = e.dataTransfer?.getData ? (e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain")) : "";
            const commit = (v: string) => { patchRecord((x) => ({ ...x, heroImage: v })); if (eventOriginId) patchEventBasics(eventOriginId, { heroImage: v }); showToast("Hero image set"); };
            if (!files.length && url) { commit(url); return; }
            if (!files.length) { showToast("Drop an image file, or paste a URL in the field above"); return; }
            const r = new FileReader();
            r.onload = () => commit(String(r.result));
            r.readAsDataURL(files[0]!);
          }}
          style={{ width: 220, border: `${heroDrop ? 2 : 1}px dashed ${heroDrop ? "#5142C7" : "#E6E8EC"}`, borderRadius: 10, overflow: "hidden", background: "#FFFFFF", cursor: "copy" }}>
          <div style={{ height: 90, background: r0.heroImage ? `url(${r0.heroImage}) center/cover` : "linear-gradient(135deg, #75ADFA, #5142C7)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {!r0.heroImage ? <span style={{ fontSize: 10.5, color: "#FFFFFF", opacity: 0.9, ...MONO }}>drop an image here</span> : null}
          </div>
          <div style={{ padding: 10 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: r0.title ? "#333333" : "#B5BAC6" }}>{r0.title || "Untitled event"}</div>
            <div style={{ fontSize: 10.5, color: "#697180", marginTop: 3 }}>{r0.start ? new Date(r0.start).toLocaleDateString() : "Date not set"}</div>
          </div>
        </div>
        <div style={{ fontSize: 10.5, color: "#B5BAC6", marginTop: 6 }}>Drag an image file or URL onto the card, or paste a URL in the field above.</div>
      </div>
      <div style={NOTE}>AI description drafting isn't available — it lived only in the creation wizard's Build with AI step, which was decommissioned. Write the description here.</div>
    </div>
  );

  const geoOpts = studioRequestOptions("geo") as { baseUrl: string; surface?: string };
  const verifyAddress = (q: string) => {
    if (q.trim().length < 4) { setAddrVerify("idle"); return; }
    setAddrVerify("checking");
    validateAddress({ address: q.trim() }, geoOpts).then((res: any) => setAddrVerify(res?.valid ? "valid" : "invalid")).catch(() => setAddrVerify("invalid"));
  };
  const onAddressType = (v: string) => {
    patchRecord((x) => ({ ...x, address: v }));
    setAddrVerify("idle");
    if (v.trim().length < 4) { setAddrSuggestions([]); setAddrOpen(false); return; }
    suggestAddresses({ q: v.trim(), limit: 5 }, geoOpts).then((res: any) => {
      setAddrSuggestions(res?.suggestions ?? []);
      setAddrOpen(true);
    }).catch(() => setAddrSuggestions([]));
  };
  const pickAddress = (label: string) => {
    patchRecord((x) => ({ ...x, address: label }));
    setAddrOpen(false);
    if (eventOriginId) patchEventDetails(eventOriginId, { locationAddress: label });
    setAddrVerify("valid");
  };
  const setupDetails = (
    <div style={setupCard}>
      {reqField("Format", (<>
        {reqLabel("Format")}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
          {[{ v: "in", l: "In person", i: <Icon.loc size={14} />, d: "A physical venue", m: { inPerson: true, virtual: false } },
            { v: "virtual", l: "Virtual", i: <Icon.video size={14} />, d: "Online only", m: { inPerson: false, virtual: true } },
            { v: "hybrid", l: "Hybrid", i: <Icon.globe size={14} />, d: "Venue + online", m: { inPerson: true, virtual: true } }].map((o) => {
            const active = !!r0.method && !!r0.method.inPerson === !!o.m.inPerson && !!r0.method.virtual === !!o.m.virtual;
            return <ChoiceTile key={o.v} leading={o.i} label={o.l} sublabel={o.d} selected={active} onSelect={() => { patchRecord((x) => ({ ...x, method: o.m })); if (eventOriginId) patchEventBasics(eventOriginId, { method: o.m }); }} />;
          })}
        </div>
      </>))}
      {reqField("Visibility", (<>
        {reqLabel("Visibility")}
        <Dropdown options={["Public · Anyone with link", "Invite · RSVP", "Public & Invite", "Private · Internal"].map((v) => ({ value: v, label: v }))} value={r0.visibility || null} onChange={(v: string) => { patchRecord((x) => ({ ...x, visibility: v })); if (eventOriginId) patchEventDetails(eventOriginId, { visibility: v }); }} placeholder="Choose visibility…" />
      </>))}
      {r0.method?.inPerson ? (<>
        <div>
          {reqLabel("Location", "Required for in-person and hybrid events — a saved location, or a venue name or address")}
          <div style={{ display: "inline-flex", border: "1px solid #D1D4DC", borderRadius: 8, overflow: "hidden", marginBottom: 8 }}>
            {(["saved", "custom"] as const).map((m) => (
              <button key={m} onClick={() => patchRecord((x) => ({ ...x, locationMode: m }))} style={{ padding: "5px 12px", border: "none", fontSize: 11.5, fontWeight: 700, cursor: "pointer", background: (r0.locationMode ?? "saved") === m ? "#5142C7" : "#FFFFFF", color: (r0.locationMode ?? "saved") === m ? "#FFFFFF" : "#333333", fontFamily: FONT }}>{m === "saved" ? "Saved location" : "New address"}</button>
            ))}
          </div>
          {(r0.locationMode ?? "saved") === "saved" ? (
            <Dropdown options={savedLocations.map((l) => ({ value: l.id, label: l.name }))} value={r0.locationId || null} onChange={(v: string) => { const loc = savedLocations.find((l) => l.id === v); patchRecord((x) => ({ ...x, locationId: v, venueName: loc?.name, address: loc?.address })); if (eventOriginId) patchEventDetails(eventOriginId, { locationId: v, locationName: loc?.name, locationAddress: loc?.address }); }} placeholder={savedLocations.length ? "Select a location…" : "No saved locations yet"} />
          ) : null}
        </div>
        {(r0.locationMode ?? "saved") === "custom" ? (<>
          <div><label style={setupFieldLabel}>Venue name</label><Input value={r0.venueName ?? ""} onChange={(e: any) => patchRecord((x) => ({ ...x, venueName: e.target.value }))} onBlur={() => eventOriginId && patchEventDetails(eventOriginId, { locationName: r0.venueName })} placeholder="e.g. Westside Branch · Community Room" /></div>
          <div style={{ position: "relative" }}>
            <label style={setupFieldLabel}>Address</label>
            <Input value={r0.address ?? ""} onChange={(e: any) => onAddressType(e.target.value)} onFocus={() => addrSuggestions.length && setAddrOpen(true)} onBlur={() => { window.setTimeout(() => setAddrOpen(false), 150); if (eventOriginId) patchEventDetails(eventOriginId, { locationAddress: r0.address }); verifyAddress(r0.address ?? ""); }} placeholder="Street, city, region, postal code" />
            {addrOpen && addrSuggestions.length ? (
              <div style={{ position: "absolute", zIndex: 5, top: "100%", left: 0, right: 0, background: "#FFFFFF", border: "1px solid #D1D4DC", borderRadius: 8, marginTop: 4, boxShadow: "0 4px 12px rgba(20,19,31,0.12)" }}>
                {addrSuggestions.map((s, i) => (
                  <div key={i} onMouseDown={() => pickAddress(s.label)} style={{ padding: "8px 11px", fontSize: 12, cursor: "pointer", borderBottom: i < addrSuggestions.length - 1 ? "1px solid #F4F5F7" : "none" }}>{s.label}</div>
                ))}
              </div>
            ) : null}
            {addrVerify === "checking" ? <div style={{ fontSize: 11, color: "#697180", marginTop: 4 }}>Verifying address…</div>
              : addrVerify === "valid" ? <div style={{ fontSize: 11, color: "#1F8A5B", marginTop: 4 }}>✓ Verified</div>
              : addrVerify === "invalid" ? <div style={{ fontSize: 11, color: "#C0392B", marginTop: 4 }}>Couldn't verify this address, but it's saved as entered.</div> : null}
          </div>
        </>) : null}
      </>) : null}
      {r0.method?.virtual ? (
        <div>{reqLabel("Meeting provider / join link", "Required for virtual and hybrid events")}<Input value={r0.virtualProvider ?? ""} onChange={(e: any) => patchRecord((x) => ({ ...x, virtualProvider: e.target.value }))} onBlur={() => eventOriginId && patchEventDetails(eventOriginId, { virtualProvider: r0.virtualProvider })} placeholder="https://…" /></div>
      ) : null}
      <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 14 }}>
        <div style={{ ...setupFieldLabel, marginBottom: 4 }}>Registration window</div>
        <div style={{ fontSize: 11.5, color: "#697180", marginBottom: 8 }}>When registration opens and closes.</div>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Opens</div>
            <Dropdown options={["Open on publish", "1d before", "1h before"].map((o) => ({ value: o, label: o }))} value={r0.registrationOpens ?? "Open on publish"} onChange={(v: string) => { patchRecord((x) => ({ ...x, registrationOpens: v })); if (eventOriginId) patchEventRegistration(eventOriginId, { registrationOpens: v }); }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Closes</div>
            <Dropdown options={["Close at start", "1h before", "1d before", "Keep open"].map((o) => ({ value: o, label: o }))} value={r0.registrationCloses ?? "Close at start"} onChange={(v: string) => { patchRecord((x) => ({ ...x, registrationCloses: v })); if (eventOriginId) patchEventRegistration(eventOriginId, { registrationCloses: v }); }} />
          </div>
        </div>
      </div>
      <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 14 }}>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>Consent &amp; marketing</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, marginBottom: 9 }}>Request marketing opt-in
          <Toggle on={!!r0.consent?.marketing} onChange={() => { const next = { ...r0.consent, marketing: !r0.consent?.marketing }; patchRecord((x) => ({ ...x, consent: next })); if (eventOriginId) patchEventRegistration(eventOriginId, { consent: next }); }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, marginBottom: 9 }}>Request contact-sharing consent
          <Toggle on={!!r0.consent?.contactSharing} onChange={() => { const next = { ...r0.consent, contactSharing: !r0.consent?.contactSharing }; patchRecord((x) => ({ ...x, consent: next })); if (eventOriginId) patchEventRegistration(eventOriginId, { consent: next }); }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Terms &amp; conditions copy</div>
          <textarea value={r0.termsCopy ?? ""} onChange={(e) => patchRecord((x) => ({ ...x, termsCopy: e.target.value }))} onBlur={() => eventOriginId && patchEventRegistration(eventOriginId, { termsCopy: r0.termsCopy })} rows={3} style={{ ...INPUT, resize: "vertical" as const }} placeholder="Shown beside the consent checkboxes at registration." />
        </div>
      </div>
      <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 14 }}>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>After registration</div>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Redirect URL</div>
        <Input value={r0.redirectUrl ?? ""} onChange={(e: any) => patchRecord((x) => ({ ...x, redirectUrl: e.target.value }))} onBlur={() => eventOriginId && patchEventRegistration(eventOriginId, { redirectUrl: r0.redirectUrl })} placeholder="https://… (leave blank to show the confirmation page)" />
      </div>
    </div>
  );

  /* ── Schedule helpers: event days + recurrence occurrences ── */
  const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const eventDays = (() => {
    if (!r0.start || !r0.end) return [] as Date[];
    const a = new Date(r0.start), b = new Date(r0.end);
    if (isNaN(+a) || isNaN(+b)) return [];
    const out: Date[] = [];
    const cur = new Date(a.getFullYear(), a.getMonth(), a.getDate());
    const last = new Date(b.getFullYear(), b.getMonth(), b.getDate());
    while (cur <= last && out.length < 60) { out.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    return out;
  })();
  const multiDay = eventDays.length > 1;
  const dayHours = r0.dayHours ?? {};
  const setDayHour = (key: string, part: "start" | "end", v: string) => {
    const next = { ...dayHours, [key]: { ...(dayHours[key] ?? {}), [part]: v } };
    patchRecord((x) => ({ ...x, dayHours: next }));
    if (eventOriginId) patchEventSchedule(eventOriginId, { dayHours: next });
  };
  const repeatIdx = r0.repeatIndex ?? 0;
  const isCustomRepeat = repeatIdx === REPEAT_LABELS_ALL.length - 1;
  const customDates = r0.customDates ?? [];
  /** Occurrence dates for the next 3 months, per the chosen recurrence. */
  const occurrences = (() => {
    if (!r0.start) return [] as string[];
    const s0 = new Date(r0.start);
    if (isNaN(+s0)) return [];
    if (isCustomRepeat) return customDates.slice();
    const horizon = new Date(s0.getFullYear(), s0.getMonth() + 3, s0.getDate());
    const out: string[] = [];
    const push = (d: Date) => { if (d <= horizon) out.push(dayKey(d)); };
    const nthWeekday = (d: Date) => Math.ceil(d.getDate() / 7);
    if (repeatIdx === 0) { push(s0); return out; }
    if (repeatIdx === 1) { const d = new Date(s0); while (d <= horizon) { push(new Date(d)); d.setDate(d.getDate() + 7); } return out; }
    const monthStep = repeatIdx === 2 || repeatIdx === 3 ? 1 : 3;
    const byWeekday = repeatIdx === 3 || repeatIdx === 5;
    const wd = s0.getDay(), nth = nthWeekday(s0);
    for (let m = 0; m < 12; m++) {
      const base = new Date(s0.getFullYear(), s0.getMonth() + m * monthStep, 1);
      if (base > horizon) break;
      if (!byWeekday) { const d = new Date(base.getFullYear(), base.getMonth(), s0.getDate()); if (d.getMonth() === base.getMonth()) push(d); }
      else { const d = new Date(base); let count = 0; while (d.getMonth() === base.getMonth()) { if (d.getDay() === wd) { count++; if (count === nth) { push(new Date(d)); break; } } d.setDate(d.getDate() + 1); } }
    }
    return out;
  })();
  const occSet = new Set(occurrences);
  const monthGrid = (offset: number) => {
    const anchor = r0.start ? new Date(r0.start) : new Date();
    const base = new Date(anchor.getFullYear(), anchor.getMonth() + offset, 1);
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const lead = first.getDay();
    const days = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    const cells: (Date | null)[] = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= days; d++) cells.push(new Date(base.getFullYear(), base.getMonth(), d));
    return { label: base.toLocaleDateString(undefined, { month: "long", year: "numeric" }), cells };
  };
  const setupSchedule = (
    <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
      <div style={{ ...setupCard, flex: "1 1 420px", minWidth: 320 }}>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}><DatePicker label={<>Start{req(undefined, "Start")}</>} value={r0.start} onChange={(v: string | null) => { const next = v ?? ""; patchRecord((x) => ({ ...x, start: next })); if (eventOriginId) patchEventSchedule(eventOriginId, { start: next }); }} leading={<Icon.calendar size={14} />} showTime={!r0.allDay} /></div>
          <div style={{ flex: 1 }}><DatePicker label={<>End{req(undefined, "End")}</>} value={r0.end} onChange={(v: string | null) => { const next = v ?? ""; patchRecord((x) => ({ ...x, end: next })); if (eventOriginId) patchEventSchedule(eventOriginId, { end: next }); }} leading={<Icon.calendar size={14} />} showTime={!r0.allDay} minDate={r0.start ? new Date(r0.start) : undefined} /></div>
        </div>
        <TimeZonePicker label="Time zone" value={r0.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone} onChange={(v: string) => { patchRecord((x) => ({ ...x, timezone: v })); if (eventOriginId) patchEventSchedule(eventOriginId, { timezone: v }); }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={setupFieldLabel}>All-day event</span>
          <Toggle on={!!r0.allDay} onChange={() => { const next = !r0.allDay; patchRecord((x) => ({ ...x, allDay: next })); if (eventOriginId) patchEventSchedule(eventOriginId, { allDay: next }); }} />
        </div>
        {multiDay && !r0.allDay ? (
          <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 14 }}>
            <div style={{ ...setupFieldLabel, marginBottom: 4 }}>Hours for each day</div>
            <div style={{ fontSize: 11.5, color: "#697180", marginBottom: 8 }}>This event spans {eventDays.length} days. Each day can run different hours — leave a day blank to use the event's own start and end.</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {eventDays.map((d) => {
                const k = dayKey(d);
                return (
                  <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid #E6E8EC", borderRadius: 8, padding: "8px 10px" }}>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#333333" }}>{d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
                    <input type="time" value={dayHours[k]?.start ?? ""} onChange={(e) => setDayHour(k, "start", e.target.value)} style={{ ...INPUT, width: 110 }} />
                    <span style={{ fontSize: 11, color: "#B5BAC6" }}>to</span>
                    <input type="time" value={dayHours[k]?.end ?? ""} onChange={(e) => setDayHour(k, "end", e.target.value)} style={{ ...INPUT, width: 110 }} />
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
        <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 14 }}>
          <label style={setupFieldLabel}>Repeats</label>
          <Dropdown options={REPEAT_LABELS_ALL.map((label, i) => ({ value: String(i), label }))} value={String(repeatIdx)} onChange={(v: string) => { const next = Number(v); patchRecord((x) => ({ ...x, repeatIndex: next })); if (eventOriginId) patchEventSchedule(eventOriginId, { repeatIndex: next }); }} />
          {isCustomRepeat ? (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Custom dates</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input type="date" value={newCustomDate} onChange={(e) => setNewCustomDate(e.target.value)} style={{ ...INPUT, flex: 1 }} />
                <button onClick={() => { const v = newCustomDate; if (!v || customDates.includes(v)) return; const next = [...customDates, v].sort(); patchRecord((x) => ({ ...x, customDates: next })); if (eventOriginId) patchEventSchedule(eventOriginId, { customDates: next }); setNewCustomDate(""); }} style={PRIMARY}>Add date</button>
              </div>
              {customDates.length ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {customDates.map((cd) => (
                    <span key={cd} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, border: "1px solid #D1D4DC", fontSize: 11.5 }}>{cd}
                      <button onClick={() => { const next = customDates.filter((x) => x !== cd); patchRecord((x) => ({ ...x, customDates: next })); if (eventOriginId) patchEventSchedule(eventOriginId, { customDates: next }); }} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
                    </span>
                  ))}
                </div>
              ) : <div style={{ fontSize: 11.5, color: "#B5BAC6" }}>No dates added yet.</div>}
            </div>
          ) : null}
        </div>
      </div>
      <div style={{ flex: "0 0 300px", minWidth: 280 }}>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>Next 3 months</div>
        {!r0.start ? <div style={{ fontSize: 12, color: "#B5BAC6" }}>Set a start date to preview occurrences.</div> : (<>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[0, 1, 2].map((off) => {
              const { label, cells } = monthGrid(off);
              return (
                <div key={off} style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: 12, background: "#FFFFFF" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{label}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, fontSize: 9.5, color: "#B5BAC6", marginBottom: 4 }}>
                    {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <span key={i} style={{ textAlign: "center" }}>{d}</span>)}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
                    {cells.map((d, i) => {
                      const on = d ? occSet.has(dayKey(d)) : false;
                      return (
                        <span key={i} style={{ height: 26, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, borderRadius: 6, fontWeight: on ? 800 : 400, background: on ? "#5142C7" : "transparent", color: on ? "#FFFFFF" : d ? "#333333" : "transparent" }}>{d ? d.getDate() : ""}</span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: "#697180", marginTop: 8 }}>{occurrences.length} {occurrences.length === 1 ? "occurrence" : "occurrences"} marked — {REPEAT_LABELS_ALL[repeatIdx]}.</div>
        </>)}
      </div>
    </div>
  );

  const selTypeIds = r0.ticketTypeIds ?? [];
  const selGroupIds = r0.ticketGroupIds ?? [];
  const typeCfgs = r0.ticketTypeConfigs ?? {};
  const totalCap = Number(r0.capacity) || 0;
  const assigned = selTypeIds.reduce((n, id) => n + (Number(typeCfgs[id]?.qty) || 0), 0);
  const remaining = totalCap - assigned;
  const setTypeQty = (id: string, qty: number) => {
    const next = { ...typeCfgs, [id]: { ...(typeCfgs[id] ?? {}), qty } };
    patchRecord((x) => ({ ...x, ticketTypeConfigs: next }));
    if (eventOriginId) patchEventTickets(eventOriginId, { ticketTypeConfigs: next });
  };
  const toggleTicketType = (id: string) => {
    const on = selTypeIds.includes(id);
    const next = on ? selTypeIds.filter((x) => x !== id) : [...selTypeIds, id];
    const nextCfgs = { ...typeCfgs };
    if (on) delete nextCfgs[id]; else nextCfgs[id] = { qty: 0 };
    patchRecord((x) => ({ ...x, ticketTypeIds: next, ticketTypeConfigs: nextCfgs }));
    if (eventOriginId) patchEventTickets(eventOriginId, { ticketTypeIds: next, ticketTypeConfigs: nextCfgs });
  };
  /* One group per event: attaching replaces, so the tickets page has a single
   * source to mirror. */
  const attachTicketGroup = (id: string | null) => {
    const next = id ? [id] : [];
    patchRecord((x) => ({ ...x, ticketGroupIds: next }));
    if (eventOriginId) attachEventTicketGroupIds(eventOriginId, next);
    /* The document's `attach.ticketGroupId` is a MIRROR of the record's first
     * selected group, not a second store — it exists so the tickets page's
     * preview and attachment chip render the same thing this picker chose.
     * Both entry points (here and the page's picker) write the pair. */
    setAttach({ ticketGroupId: next[0] ?? null });
  };
  const PickRow = ({ selected, onClick, title, sub, right }: { selected: boolean; onClick: () => void; title: string; sub?: string; right?: string }) => (
    <button onClick={onClick} style={{ display: "flex", alignItems: "flex-start", gap: 10, width: "100%", textAlign: "left", padding: "10px 12px", border: `${selected ? 2 : 1}px solid ${selected ? "#5142C7" : "#E6E8EC"}`, background: selected ? "#F6F2FE" : "#FFFFFF", borderRadius: selected ? "9px 9px 0 0" : 9, cursor: "pointer", fontFamily: FONT }}>
      <span style={{ width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${selected ? "#5142C7" : "#D1D4DC"}`, background: selected ? "#5142C7" : "#FFFFFF", color: "#FFFFFF", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1, fontSize: 11 }}>{selected ? "✓" : ""}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: "#333333" }}>{title}</span>
        {sub ? <span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 2 }}>{sub}</span> : null}
      </span>
      {right ? <span style={{ fontSize: 12, fontWeight: 800, color: "#D01647", flexShrink: 0 }}>{right}</span> : null}
    </button>
  );
  /* Attached group: the record's selection, falling back to the document's
   * attachment so this tab can never claim "no group" for an event whose
   * tickets page is already rendering one. No implicit first-in-library
   * fallback here — an empty pair means genuinely nothing attached. */
  const attachedGroup = (ticketGroups as any[]).find((g) => g.id === (selGroupIds[0] ?? at.ticketGroupId)) ?? null;
  const selTypes = selTypeIds.map((id) => (allTicketTypes as any[]).find((t) => t.id === id)).filter(Boolean) as any[];
  const secLabel = { ...SEC, color: "#333333", marginBottom: 0 };
  const linkBtn = { border: "none", background: "transparent", color: "#5142C7", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT, padding: 0 } as const;
  const dashedNote = { border: "1px dashed #D1D4DC", borderRadius: 9, padding: "11px 13px", fontSize: 11.5, color: "#697180", lineHeight: 1.5 } as const;
  const setupTickets = (
    <div style={setupCard}>
      <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>Select the ticket types or groups this event offers. This is the record — the tickets page shows a live reference to what's selected here.</div>

      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={secLabel}>Ticket types{req("At least one ticket type or one ticket group is required — either satisfies the step")}</span>
          <button onClick={() => nav.go("ticket-types")} style={linkBtn}>Manage types →</button>
        </div>
        {selTypes.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {selTypes.map((t) => {
              const qty = Number(typeCfgs[t.id]?.qty) || 0;
              return (
                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 9, padding: "10px 12px", background: "#FFFFFF" }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: "#333333" }}>{t.name}</span>
                    <span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 2 }}>
                      <b style={{ color: t.kind === "free" ? "#1F8A5B" : "#D01647", fontWeight: 800 }}>{t.kind === "free" ? "Free" : tgMoney(t.price)}</b>{t.desc ? ` · ${t.desc}` : ""}
                    </span>
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 7, flex: "none" }}>
                    <span style={{ fontSize: 11, color: "#697180" }}>Allocated</span>
                    <div style={{ width: 104 }}>
                      <Input type="number" value={qty || ""} onChange={(e: any) => setTypeQty(t.id, e.target.value ? Number(e.target.value) : 0)} placeholder="0" />
                    </div>
                  </span>
                  <button onClick={() => toggleTicketType(t.id)} title={`Remove ${t.name}`} style={{ border: "none", background: "transparent", color: "#697180", fontSize: 12, cursor: "pointer", flex: "none", fontFamily: FONT }}>✕</button>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={dashedNote}>{attachedGroup ? "No individual ticket types selected — this event uses a ticket group instead." : "No ticket types selected yet — pick them from the library below."}</div>
        )}
        <button onClick={() => set({ typesPicker: true })} style={{ width: "100%", marginTop: 8, border: "1px dashed #B69BF0", borderRadius: 9, background: "#FCFBFF", padding: "11px 13px", fontSize: 12, fontWeight: 700, color: "#5142C7", cursor: "pointer", fontFamily: FONT }}>+ Select from ticket types library</button>
      </div>

      <div style={{ height: 1, background: "#E6E8EC" }} />

      <div>
        <div style={{ ...secLabel, marginBottom: 8 }}>Capacity &amp; waitlist</div>
        {attachedGroup ? (
          <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.55 }}>Managed per tier inside the ticket group — no separate event-level capacity while a group is attached.</div>
        ) : (
          <>
            <label style={setupFieldLabel}>Total event capacity</label>
            <Input type="number" value={r0.capacity ?? ""} onChange={(e: any) => patchRecord((x) => ({ ...x, capacity: e.target.value ? Number(e.target.value) : null }))} onBlur={() => eventOriginId && patchEventTickets(eventOriginId, { capacity: r0.capacity })} placeholder="e.g. 250" />
            {totalCap > 0 ? (
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 8, padding: "9px 12px", borderRadius: 8, background: remaining < 0 ? "#FDECEA" : "#F4F5F7", border: `1px solid ${remaining < 0 ? "#F3D0CB" : "#E6E8EC"}` }}>
                <span style={{ fontSize: 11.5, color: "#697180" }}>Allocated <b style={{ color: "#333333" }}>{assigned}</b> of {totalCap}</span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: remaining < 0 ? "#C0392B" : remaining === 0 ? "#1F8A5B" : "#5142C7" }}>
                  {remaining < 0 ? `${Math.abs(remaining)} over the total` : `${remaining} remaining`}
                </span>
              </div>
            ) : null}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10, border: `1px solid ${r0.waitlist ? "#DCCCFC" : "#E6E8EC"}`, background: r0.waitlist ? "#F6F2FE" : "#FFFFFF", borderRadius: 9, padding: "11px 13px" }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: r0.waitlist ? "#5142C7" : "#333333" }}>Enable waitlist</span>
                <span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 2, lineHeight: 1.5 }}>{r0.waitlist ? "Once the total is reached, new registrants join the waitlist and are notified in signup order as seats open." : "Registration closes when the total is reached."}</span>
              </span>
              <Toggle on={!!r0.waitlist} onChange={() => { const next = !r0.waitlist; patchRecord((x) => ({ ...x, waitlist: next })); if (eventOriginId) patchEventTickets(eventOriginId, { waitlist: next }); }} />
            </div>
          </>
        )}
      </div>

      <div style={{ height: 1, background: "#E6E8EC" }} />

      {/* Moved off the Tickets page inspector (2026-08-31): an event's promo is
          event-scoped and belongs with the rest of the event's commerce. */}
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={secLabel}>Promo code</span>
          <button onClick={() => nav.go("promos")} style={linkBtn}>Manage promos →</button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {coupons.length === 0 ? <span style={{ fontSize: 11.5, color: "#697180" }}>No promo codes in the library yet.</span> : coupons.map((c: any) => {
            const on = (D?.attach ?? {}).promoId === c.id;
            return (
              <button key={c.id} onClick={() => setAttach({ promoId: on ? null : c.id })}
                style={{ padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 11.5, fontWeight: 700, border: `1.5px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#5142C7" : "#FFFFFF", color: on ? "#FFFFFF" : "#4B5563" }}>
                {c.code || c.name || "Untitled promo"}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={secLabel}>Ticket groups{req("At least one ticket type or one ticket group is required — either satisfies the step")}</span>
          <button onClick={() => nav.go("ticket-groups")} style={linkBtn}>Manage groups →</button>
        </div>
        {attachedGroup ? (
          <>
            <div style={{ border: "1px solid #DCCCFC", background: "#F6F2FE", borderRadius: 10, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 9.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#5142C7", marginBottom: 3 }}>Ticket group · attached</span>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 800, color: "#333333" }}>{attachedGroup.name || "Untitled group"}</span>
                  <span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 2 }}>{(attachedGroup.tiers ?? []).length} {(attachedGroup.tiers ?? []).length === 1 ? "tier" : "tiers"}{attachedGroup.description ? ` · ${attachedGroup.description}` : ""}</span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 10, flex: "none" }}>
                  <button onClick={() => set({ lib: "tickets" })} style={linkBtn}>Change</button>
                  <button onClick={() => attachTicketGroup(null)} style={{ ...linkBtn, color: "#697180" }}>Detach</button>
                </span>
              </div>
              {(attachedGroup.tiers ?? []).length ? (
                <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
                  {(attachedGroup.tiers ?? []).map((tier: any, i: number) => (
                    <div key={tier.id ?? i} style={{ background: "#FFFFFF", border: "1px solid #EDE5FE", borderRadius: 8, padding: "9px 11px" }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: "#333333" }}>{tier.name || "Untitled tier"}</div>
                      <div style={{ fontSize: 12.5, fontWeight: 800, color: "#5142C7", marginTop: 3 }}>{tier.kind === "free" || !tier.price ? "Free" : tgMoney(tier.price)}</div>
                      {tier.defaultAllocation ? <div style={{ fontSize: 10.5, color: "#697180", marginTop: 2 }}>{tier.defaultAllocation} allocated</div> : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            <div style={{ fontSize: 11, color: "#697180", lineHeight: 1.55, marginTop: 8 }}>Tier names, prices and allocation are edited in Ticket groups — this card is a reference, not a copy, so the tickets page in this journey shows the same thing.</div>
          </>
        ) : (
          <>
            <div style={dashedNote}>No group attached — using the individual ticket types above.</div>
            <button onClick={() => set({ lib: "tickets" })} style={{ ...GHOST, marginTop: 8 }}>Attach a ticket group →</button>
          </>
        )}
      </div>

      <div style={NOTE}>Nothing is created here — types and groups are library objects, referenced by id. You don't have to allocate the whole total now: leave tickets unassigned and come back to add more when you edit the event.</div>

      {/* Coupons and promotions — two different incentives, event-scoped, on the
          RECORD (operator, 2026-09-02). The promos library distinguishes them:
          a COUPON is a code the student enters at checkout, a PROMO applies
          automatically. They are separate fields because they behave
          differently at runtime, and one list would hide which is which.
          Under Tickets because both discount a price, and the prices are here.
          Neither is per method: an event has one way in. */}
      <div style={{ height: 1, background: "#E6E8EC" }} />
      {([
        { key: "coupons" as const, label: "Coupons", hint: "Codes a student enters at checkout. Saved on the event, so every journey that sells it takes the same codes.", rows: coupons, empty: "No coupons in the library yet — create one under Coupons and it will appear here." },
        { key: "promos" as const, label: "Promotions", hint: "Applied automatically, with no code to enter.", rows: autoPromos, empty: "No automatic promotions in the library yet." },
      ]).map((grp) => (
        <div key={grp.key}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={setupFieldLabel}>{grp.label}</span>
            <button onClick={() => goRoute("promos")} style={linkBtn}>Manage {grp.label.toLowerCase()} →</button>
          </div>
          <div style={{ ...NOTE, marginBottom: 8 }}>{grp.hint}</div>
          {grp.rows.length === 0 ? (
            <span style={{ fontSize: 11.5, color: "#697180" }}>{grp.empty}</span>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {grp.rows.map((c: any) => {
                const cur = (r0[grp.key] ?? []) as string[];
                const on = cur.includes(c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => {
                      const next = on ? cur.filter((x) => x !== c.id) : [...cur, c.id];
                      patchRecord((x) => ({ ...x, [grp.key]: next }));
                      if (eventOriginId) void patchEventTickets(eventOriginId, { [grp.key]: next });
                    }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 11.5, fontWeight: 700, border: `1.5px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#F6F2FE" : "#FFFFFF", color: on ? "#5142C7" : "#4B5563" }}
                  >
                    <span style={{ fontSize: 10 }}>{on ? "✓" : "+"}</span>
                    {/* A coupon is known by its CODE, a promo has none — it is
                        known by its name. Showing "code || name" for both would
                        print an empty string for every automatic promo. */}
                    {grp.key === "coupons" ? (c.code || c.name || "Untitled coupon") : (c.name || "Untitled promotion")}
                    {c.kind && c.value != null ? <span style={{ color: "#8A92A0", fontWeight: 500 }}>· {c.kind === "percent" ? `${c.value}%` : `${c.value} off`}</span> : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const guests = r0.guestList ?? [];
  const addGuest = (entry?: GuestEntry) => {
    const g = entry ?? guestDraft;
    if (!entry && (!guestDraft.firstName.trim() || !guestDraft.email.trim())) return;
    const next = [...guests, g];
    patchRecord((x) => ({ ...x, guestList: next }));
    if (eventOriginId) patchEventGuestList(eventOriginId, next);
    if (!entry) setGuestDraft({ firstName: "", email: "" });
  };
  const setupGuests = (
    <div style={setupCard}>
      <div>
        <Input value={guestSearch} onChange={(e: any) => setGuestSearch(e.target.value)} placeholder="Search by name, email or company…" />
        {guestSearching ? <div style={{ fontSize: 11, color: "#697180", marginTop: 4 }}>Searching…</div> : null}
        {guestResults.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
            {guestResults.map((r, i) => (
              <button key={i} onClick={() => { addGuest({ firstName: r.firstName, lastName: r.lastName, email: r.email }); setGuestSearch(""); setGuestResults([]); }} style={{ textAlign: "left", border: "1px solid #E6E8EC", borderRadius: 8, padding: "6px 10px", fontSize: 12, background: "#FFFFFF", cursor: "pointer", fontFamily: FONT }}>{r.firstName} {r.lastName ?? ""} — {r.email}</button>
            ))}
          </div>
        ) : null}
      </div>
      <div>
        <div style={{ ...setupFieldLabel, marginBottom: 6 }}>Add manually</div>
        <div style={{ display: "flex", gap: 8 }}>
          <Input value={guestDraft.firstName} onChange={(e: any) => setGuestDraft((g) => ({ ...g, firstName: e.target.value }))} placeholder="First name" />
          <Input value={guestDraft.email} onChange={(e: any) => setGuestDraft((g) => ({ ...g, email: e.target.value }))} placeholder="Email" />
          <button onClick={() => addGuest()} style={PRIMARY}>Add</button>
        </div>
      </div>
      {segments.length ? (
        <div>
          <div style={{ ...setupFieldLabel, marginBottom: 6 }}>Add a segment</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {segments.map((s) => (
              <button key={s.id} onClick={() => addGuest({ firstName: s.name, email: "", kind: "segment", count: s.count })} style={{ padding: "6px 12px", borderRadius: 999, border: "1px solid #D1D4DC", background: "#FFFFFF", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>{s.name} · {s.count}</button>
            ))}
          </div>
        </div>
      ) : null}
      {guests.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {guests.map((g, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #E6E8EC", borderRadius: 8, padding: "7px 10px", fontSize: 12 }}>
              <span>{g.kind === "segment" ? `Segment — ${g.firstName} (${g.count ?? 0})` : `${g.firstName} ${g.lastName ?? ""} — ${g.email}`}</span>
              <button onClick={() => { const next = guests.filter((_, j) => j !== i); patchRecord((x) => ({ ...x, guestList: next })); if (eventOriginId) patchEventGuestList(eventOriginId, next); }} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer" }}>✕</button>
            </div>
          ))}
        </div>
      ) : <div style={{ fontSize: 12, color: "#B5BAC6" }}>No guests added yet.</div>}
      <div style={NOTE}>CSV import isn't wired to this view yet — use Guests → Import for bulk upload.</div>
    </div>
  );

  /* ── Stage 5 — Publish view: one verb, ordered, honest on partial failure ── */
  const isLive = r0.status === "live" || r0.status === "Live";
  const approvalsOn = approvalsStore.enabled();
  const approvalSub = eventOriginId ? approvalsStore.submission("events", eventOriginId) : null;
  const approvalBlocked = !!eventOriginId && approvalsStore.isBlocked("events", eventOriginId);
  const approvalBlockReason = eventOriginId ? approvalsStore.blockReason("events", eventOriginId) : "";
  const approvalProcess = approvalsStore.processes("events")[0];
  const allReady = readiness.every((c) => c.done);
  const runPublish = async () => {
    if (!allReady || (approvalsOn && approvalBlocked) || publishBusy) return;
    setPublishBusy("publish");
    try {
      /* Create the record if this journey has none (operator, 2026-09-02).
         Publish used to require it to exist already, with a separate "Create the
         event" card doing that first — two steps for one intention, and the
         first one read as a save prompt. `runAttach` re-keys the document onto
         the new event, so `eventOriginId` is only trustworthy after it. */
      let id = eventOriginId;
      if (!id) {
        const r = await runAttach();
        if (!r) return;
        id = r.origin.id;
      }
      // Ordered: the record's own fields already save per-field as Setup is
      // edited (each onBlur above); the document (pages/blocks/flow) already
      // saves on every canvas edit via saveDoc/putJourneyDoc. Publish is the
      // one remaining step — flip the record live — and it is NOT assumed to
      // have succeeded until the call resolves.
      await publishEventRecord(id);
      patchRecord((x) => ({ ...x, status: "live" }));
      patchDoc((d) => ({ ...d, status: "Live", edited: false }));
      /* Stage 5's residual, closed: the record going live is not the same as
         the public page serving this journey. The pages/blocks are mapped onto
         the landing envelope and published as the second ordered step — and if
         it fails, the toast says the record is live but the page is not, rather
         than implying both landed. */
      const dNow = docs[ui.curDoc] ?? D!;
      try {
        /* `id`, not `eventOriginId` (2026-09-08). `runAttach` above mints the
           event and re-keys the document onto it; `eventOriginId` is the value
           from BEFORE that and is null on a first publish — so a
           create-and-publish sent the pages to no record while the record itself
           went live. The comment 12 lines up says exactly this and the call did
           not follow it. */
        const pg = await publishJourneyPages(id, dNow, dNow.publish?.landingPageId ?? null, true, {
          /* The live record, not the journey's setup bag: an attached event's
             schedule and venue live on the record, and the published page must
             carry the same values the designer showed. */
          start: r0.start, end: r0.end, allDay: !!r0.allDay, timezone: r0.timezone,
          venue: setupVenue, address: setupAddress,
        });
        patchDoc((d) => ({ ...d, status: "Live", edited: false, publish: { ...(d.publish ?? {}), landingPageId: pg.landingPageId } }));
        showToast(pg.dropped.length
          ? `Published — the public page now serves these pages. ${pg.dropped.join(" and ")} ${pg.dropped.length === 1 ? "has" : "have"} no public equivalent and wasn't sent.`
          : "Published — registration is open, comms have started, and the public page serves this journey's pages");
      } catch (pageErr) {
        showToast("Event is live, but the public page didn't update — press Publish again. The record is unchanged by the failure.");
      }
    } catch (e) {
      showToast("Couldn't publish — the event is unchanged. Retry in a moment.");
    } finally { setPublishBusy(null); }
  };
  const runTakeOffline = async () => {
    if (!eventOriginId || publishBusy) return;
    setPublishBusy("offline");
    try {
      await takeEventRecordOffline(eventOriginId);
      patchRecord((x) => ({ ...x, status: "draft" }));
      patchDoc((d) => ({ ...d, status: "Draft" }));
      showToast("Taken offline — registration is closed and the journey is editable");
    } catch (e) { showToast("Couldn't take the event offline. Retry in a moment."); }
    finally { setPublishBusy(null); }
  };
  const runCancel = async () => {
    if (!eventOriginId || publishBusy) return;
    setPublishBusy("cancel");
    try {
      await cancelEventRecord(eventOriginId);
      patchRecord((x) => ({ ...x, status: "cancelled" }));
      patchDoc((d) => ({ ...d, status: "Cancelled" }));
      showToast("Event cancelled — registered guests will be notified");
    } catch (e) { showToast("Couldn't cancel the event. Retry in a moment."); }
    finally { setPublishBusy(null); }
  };
  const submitForApproval = async () => {
    if (!eventOriginId || !approvalProcess || publishBusy) return;
    setPublishBusy("submit");
    try { await approvalsStore.submitItem("events", eventOriginId, approvalProcess.id, getUser().name || undefined); showToast("Submitted for approval"); }
    catch (e) { showToast("Couldn't submit for approval. Retry in a moment."); }
    finally { setPublishBusy(null); }
  };

  /* ── Live event operations (operator 2026-08-24: "events needs to do it how
     queuing does it today"). The queue's Publish view carries the live list and
     both messaging modes; an event's live surface lived only in the wizard's
     Live step, so it came here — same placement, same two verbs, real ops:
     registrations from the record's ticket rows, sends through messageGuest
     (POST /events/:id/live-messages), which is the op the wizard uses. */
  const liveTickets = selTypes.map((tt) => {
    const row = ((r0.tickets ?? []) as any[]).find((x) => String(x?.id) === String(tt.id)) ?? {};
    return {
      id: String(tt.id), name: tt.name || "Untitled type", color: tt.color || "#5142C7",
      capacity: Number(typeCfgs[tt.id]?.qty) || Number(row.capacity) || 0,
      sold: Number(row.sold) || 0, waitlist: Number(row.waitlist) || 0,
    };
  });
  const liveSold = liveTickets.reduce((n, x) => n + x.sold, 0);
  const liveCap = liveTickets.reduce((n, x) => n + x.capacity, 0) || totalCap;
  const liveWait = liveTickets.reduce((n, x) => n + x.waitlist, 0);
  const evMsgCount = evMsg?.tierId ? (liveTickets.find((x) => x.id === evMsg.tierId)?.sold ?? 0) : liveSold;
  const sendEventMessage = async () => {
    if (!eventOriginId || !evMsg || evMsgSending || !evMsgText.trim()) return;
    setEvMsgSending(true);
    try {
      const res: any = await messageGuest(eventOriginId, {
        audience: evMsg.tierId ? { type: "tier", tierId: evMsg.tierId } : { type: "all" },
        channel: evMsgChannel,
        subject: evMsgChannel === "email" ? (evMsgSubject.trim() || undefined) : undefined,
        body: evMsgText.trim(),
      } as Parameters<typeof messageGuest>[1]);
      const n = typeof res?.queued === "number" ? res.queued : evMsgCount;
      showToast(`Message queued for ${n} registrant${n === 1 ? "" : "s"} via ${evMsgChannel.toUpperCase()}`);
      setEvMsg(null); setEvMsgText(""); setEvMsgSubject("");
    } catch (e) { showToast(`Couldn't queue the message — ${e instanceof Error ? e.message : String(e)}`); }
    finally { setEvMsgSending(false); }
  };
  const eventLiveOps = isLive && eventOriginId ? (
    <>
      <div style={{ height: 1, background: "#E6E8EC" }} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        {([["Registered", liveSold.toLocaleString(), liveCap ? `of ${liveCap.toLocaleString()} · ${Math.round((liveSold / liveCap) * 100)}%` : "no capacity set"],
           ["Capacity", liveCap.toLocaleString(), liveCap ? `${Math.max(0, liveCap - liveSold).toLocaleString()} left` : "—"],
           ["Waitlist", liveWait.toLocaleString(), liveWait ? "waiting on a release" : "nobody waiting"]] as [string, string, string][]).map(([k, v, sub]) => (
          <div key={k} style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: "12px 14px", background: "#FFFFFF" }}>
            <div style={{ ...SEC, marginBottom: 6 }}>{k}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#333333", letterSpacing: "-0.02em" }}>{v}</div>
            <div style={{ fontSize: 11, color: "#697180", marginTop: 2 }}>{sub}</div>
          </div>
        ))}
      </div>
      <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#333333" }}>Registered by ticket type</div>
            <div style={{ fontSize: 11.5, color: "#697180", marginTop: 2 }}>Live from the event record. Message a single type, or everyone at once.</div>
          </div>
          <button onClick={() => { setEvMsg({ tierId: null }); setEvMsgText(""); }} disabled={!liveSold} style={{ ...PRIMARY, opacity: liveSold ? 1 : 0.5 }}>Message everyone</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {liveTickets.map((tk) => (
            <div key={tk.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", border: "1px solid #E6E8EC", borderRadius: 9 }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: tk.color, flex: "none" }} />
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "#333333", flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tk.name}</span>
              <span style={{ fontSize: 11.5, color: "#333333", flex: "none", ...MONO }}>{tk.sold}<span style={{ color: "#697180" }}>/{tk.capacity || "—"}</span></span>
              {tk.waitlist ? <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: "#FAF1DE", color: "#916D0F", flex: "none" }}>{tk.waitlist} waiting</span> : null}
              <button onClick={() => { setEvMsg({ tierId: tk.id }); setEvMsgText(""); }} disabled={!tk.sold} style={{ ...GHOST, opacity: tk.sold ? 1 : 0.5 }}>Message</button>
            </div>
          ))}
          {!liveTickets.length ? <div style={{ padding: "14px 12px", border: "1px dashed #D1D4DC", borderRadius: 9, fontSize: 12, color: "#697180", textAlign: "center" }}>No ticket types selected — registrations aren't split by type.</div> : null}
        </div>
      </div>
    </>
  ) : null;
  /** Appointment Setup — the rail lives in appt-setup.tsx; this hands it the
   *  live libraries and the two patchers. Services come from the SAME catalogue
   *  read the pages use, so Setup and the page cannot disagree about what the
   *  funnel offers. */
  const apptLibs: ApptLibs = {
    services: (cat?.services ?? [])
      .filter((sv: any) => sv?.modules?.appointments !== false)
      .map((sv: any) => ({
        id: sv.id, name: sv.name, category: sv.category, lob: sv.lob,
        durationMin: sv.durationMin, preMin: sv.preMin, postMin: sv.postMin,
        channels: sv.channels,
        questionGroupId: sv.questionGroups?.appointments ?? null,
        minAdvance: sv.minAdvance, maxAdvance: sv.maxAdvance, minCancel: sv.minCancel,
        disableBefore: sv.disableBefore, disableAfter: sv.disableAfter,
      })),
    categories: (cat?.categories ?? []).map((c: any) => ({ id: c.id, name: c.name })),
    locations: (savedLocations as any[]).map((l) => ({ id: l.id, name: l.name, address: l.address })),
    questionGroups: qGroups.map((g: any) => ({ id: g.id, name: g.name || "Untitled group", count: g.items?.length ?? 0 })),
    surveys: (surveys as any[]).map((sv: any) => ({ id: sv.id, name: sv.name || "Untitled survey" })),
    promos: coupons.map((c: any) => ({ id: c.id, label: c.code || c.name || "Untitled promo" })),
    flows: flows.all().map((f: any) => ({ id: f.id, name: f.name || "Untitled flow" })),
    availability: null,
  };
  const setupApptPanel = D ? (
    <ApptSetupPanel
      view={(ui.setupView ?? "aservices") as ApptView}
      doc={D}
      setup={D.apptSetup ?? {}}
      libs={apptLibs}
      onPatch={patchAppt}
      onPublish={(patch) => patchDoc((d) => ({ ...d, publish: { ...(d.publish ?? {}), ...patch } }))}
      goRoute={goRoute}
    />
  ) : null;

  const setupPublish = (
    <div style={setupCard}>
      <div>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>Readiness</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {readiness.map((c) => (
            <div key={c.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12.5, padding: "6px 2px" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: c.done ? "#1F8A5B" : "#C0392B" }}>{c.done ? "✓" : "○"}</span>{c.label}
              </span>
              {!c.done && c.step ? <button onClick={() => set({ setupView: c.step as typeof ui.setupView })} style={{ border: "none", background: "transparent", color: "#5142C7", fontWeight: 700, fontSize: 11.5, cursor: "pointer", fontFamily: FONT }}>Fix →</button> : null}
            </div>
          ))}
          {!readiness.length || rec === null ? <div style={{ fontSize: 12, color: "#B5BAC6" }}>{rec === null ? "Nothing set yet — fill in Setup to see what's outstanding." : "Loading the event record…"}</div> : null}
        </div>
      </div>
      {approvalsOn ? (
        <div>
          <div style={{ ...setupFieldLabel, marginBottom: 8 }}>Approval</div>
          {approvalSub ? (
            <div style={{ fontSize: 12.5, marginBottom: 8 }}>Status: <b>{approvalSub.status}</b></div>
          ) : <div style={{ fontSize: 12.5, color: "#697180", marginBottom: 8 }}>Not yet submitted.</div>}
          {approvalBlocked ? <div style={WARN}>{approvalBlockReason}</div> : null}
          {!approvalSub || approvalSub.status === "rejected" ? (
            <button onClick={submitForApproval} disabled={publishBusy === "submit"} style={{ ...PRIMARY, opacity: publishBusy === "submit" ? 0.6 : 1 }}>{publishBusy === "submit" ? "Submitting…" : "Submit for approval"}</button>
          ) : null}
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 10 }}>
        {!isLive ? (
          <button
            onClick={runPublish}
            disabled={!allReady || (approvalsOn && approvalBlocked) || publishBusy !== null}
            /* No longer gated on the event RECORD existing (operator,
               2026-09-02) — Publish creates it. The tooltip now says so instead
               of refusing, and only the two real blockers disable the button. */
            title={!allReady ? "Fix the readiness items above first"
              : (approvalsOn && approvalBlocked) ? approvalBlockReason
              : !eventOriginId ? "Creates the event from Setup, then takes it live"
              : undefined}
            style={{ ...PRIMARY, opacity: (!allReady || (approvalsOn && approvalBlocked) || publishBusy !== null) ? 0.5 : 1 }}
          >{publishBusy === "publish" ? "Publishing…" : !eventOriginId ? "Create & publish event" : "Publish event"}</button>
        ) : (<>
          <button onClick={runTakeOffline} disabled={publishBusy !== null} style={{ height: 36, padding: "0 16px", borderRadius: 8, border: "1px solid #D1D4DC", background: "#FFFFFF", color: "#333333", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{publishBusy === "offline" ? "Taking offline…" : "Take offline to edit"}</button>
          <button onClick={runCancel} disabled={publishBusy !== null} style={{ height: 36, padding: "0 16px", borderRadius: 8, border: "1px solid #F3D0CB", background: "#FFFFFF", color: "#C0392B", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{publishBusy === "cancel" ? "Cancelling…" : "Cancel event"}</button>
        </>)}
      </div>
      {isLive ? <div style={NOTE}>This event is live. Registration is open and comms have started — take it offline to make further changes.</div> : null}
      {eventLiveOps}
      {!eventOriginId ? (
        /* No button (operator, 2026-09-02): Publish creates the event itself.
           The card stays as an explanation of what Publish will do, because
           "everything in Setup becomes the record's opening values" is worth
           knowing BEFORE pressing it and is stated nowhere else. */
        <div style={{ border: "1px dashed #DCCCFC", background: "#FCFBFF", borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#333333", marginBottom: 4 }}>Not an event yet</div>
          <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.55 }}>Everything in Setup is held on this journey and saved as you work. Publishing creates the event from those values, keeps every page exactly as designed, and takes it live in one step.</div>
        </div>
      ) : null}
    </div>
  );

  /** Setup · Registration (2026-08-31) — the event-record fields that used to be
   *  edited from the Register page's inspector. Same controls, same ops, one
   *  owner: reference where it acts, edit where it lives. */
  const setupRegistration = (
    <div style={setupCard}>
      <div>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>Who can register, and what you ask them</div>
        <div style={NOTE}>These are fields on the event record — the Register page shows their effect and links back here. Booking questions are attached separately, in the Registration section below.</div>
      </div>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5, marginBottom: 12 }}>
          <span>Require approval before a place is confirmed</span>
          <Toggle on={!!r0.approvalMode} onChange={() => { const next = !r0.approvalMode; patchRecord((x) => ({ ...x, approvalMode: next })); if (eventOriginId) patchEventRegistration(eventOriginId, { approvalMode: next }); }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <span style={setupFieldLabel}>Max guests per registration</span>
          <Input type="number" value={r0.maxGuests ?? ""} onChange={(e: any) => { const v = e.target.value ? Number(e.target.value) : null; patchRecord((x) => ({ ...x, maxGuests: v })); }} onBlur={() => eventOriginId && patchEventRegistration(eventOriginId, { maxGuests: r0.maxGuests })} placeholder="e.g. 2" />
          <div style={{ ...NOTE, marginTop: 5 }}>Only applies when the Register page has +1 guests switched on.</div>
        </div>
        <span style={setupFieldLabel}>Standard attendee fields</span>
        <div style={{ ...NOTE, marginBottom: 8 }}>First and last name are always collected.</div>
        {([["email", "Email"], ["phone", "Phone number"]] as const).map(([k, label]) => {
          const on = (r0.standardFields ?? ["email"]).includes(k);
          return (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5, marginBottom: 10 }}>{label}
              <Toggle on={on} onChange={() => { const cur = r0.standardFields ?? ["email"]; const next = on ? cur.filter((x) => x !== k) : cur.concat(k); patchRecord((x) => ({ ...x, standardFields: next })); if (eventOriginId) patchEventRegistration(eventOriginId, { standardFields: next }); }} />
            </div>
          );
        })}
      </div>
      <div style={{ height: 1, background: "#E6E8EC" }} />
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={setupFieldLabel}>Booking questions</span>
          <button onClick={() => goRoute("questions")} style={linkBtn}>Manage groups →</button>
        </div>
        <div style={{ ...NOTE, marginBottom: 8 }}>One group for the whole event. Unlike a queue or a booking funnel, an event asks the same questions of everyone — there is no service to vary by.</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {qGroups.length === 0 ? <span style={{ fontSize: 11.5, color: "#697180" }}>No question groups yet.</span> : qGroups.map((g: any) => {
            const on = (D?.attach ?? {}).groupId === g.id;
            return (
              <button key={g.id} onClick={() => setAttach({ groupId: on ? null : g.id })}
                style={{ padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 11.5, fontWeight: 700, border: `1.5px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#5142C7" : "#FFFFFF", color: on ? "#FFFFFF" : "#4B5563" }}>
                {g.name || "Untitled group"} <span style={{ opacity: 0.7 }}>· {g.items?.length ?? 0}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const setupMessaging = (
    <div style={setupCard}>
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
          <label style={setupFieldLabel}>Message flow</label>
          <button onClick={() => nav.go("messaging")} style={{ border: "none", background: "transparent", color: "#5142C7", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Manage flows →</button>
        </div>
        <div style={{ fontSize: 11.5, color: "#697180", marginBottom: 8 }}>Pick an existing flow from the message library — it runs for this event. Designing a new one happens in the library, not here.</div>
        {flows.loading ? <div style={{ fontSize: 12, color: "#B5BAC6" }}>Loading flows…</div>
          : allFlows.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {allFlows.map((f: any) => {
              const st = flowStats(f);
              const on = r0.messagingFlowId === f.id;
              return (
                <PickRow key={f.id} selected={on}
                  onClick={() => { const next = on ? undefined : f.id; patchRecord((x) => ({ ...x, messagingFlowId: next })); if (eventOriginId) patchEventMessaging(eventOriginId, { flowId: next ?? null }); }}
                  title={f.name || "Untitled flow"}
                  sub={`${st.steps} ${st.steps === 1 ? "step" : "steps"} · ${st.messages} ${st.messages === 1 ? "message" : "messages"}${f.desc ? ` · ${f.desc}` : ""}`}
                  right={f.eventKind || undefined} />
              );
            })}
          </div>
        ) : <div style={{ fontSize: 12, color: "#B5BAC6" }}>No flows in the library yet — create one under Messaging.</div>}
      </div>
      <div style={NOTE}>Selecting a flow references it by id; the flow itself stays in the library, so edits there apply everywhere it's used. Per-event customisation lives on the Flow canvas.</div>
      <div style={{ height: 1, background: "#E6E8EC" }} />
      {/* Moved off the CSAT page inspector (2026-08-31). An event's survey is
          event-scoped, so it belongs with the event's other sends.
          2026-09-02: it now writes to the EVENT RECORD (`surveys`) instead of the
          journey document's `attach.surveyId`. It was in the right screen and the
          wrong store — two journeys against one event could each hold a different
          survey, and the event itself knew about neither. Same ruling that put
          surveys on the service definition. */}
      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={setupFieldLabel}>Post-event survey</span>
          <button onClick={() => goRoute("surveys")} style={linkBtn}>Manage surveys →</button>
        </div>
        <div style={{ ...NOTE, marginBottom: 8 }}>Sent after the event and shown on the How did we do? page. Saved on the event, so every journey that runs it sends the same one.</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {(surveys as any[]).length === 0 ? <span style={{ fontSize: 11.5, color: "#697180" }}>No surveys in the library yet — create one under Surveys and it will appear here.</span> : (surveys as any[]).map((sv: any) => {
            /* One survey, held in the record's `surveys` list. The field is an
               array because the contract allows several; the UI offers one
               because "the post-event survey" is how an organiser thinks of it,
               and a second would just race the first onto the same page. */
            const on = (r0.surveys ?? [])[0] === sv.id;
            return (
              <button key={sv.id} onClick={() => {
                const next = on ? [] : [sv.id];
                patchRecord((x) => ({ ...x, surveys: next }));
                if (eventOriginId) void patchEventMessaging(eventOriginId, { surveys: next });
              }}
                style={{ padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 11.5, fontWeight: 700, border: `1.5px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#5142C7" : "#FFFFFF", color: on ? "#FFFFFF" : "#4B5563" }}>
                {sv.name || "Untitled survey"}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════════════
     Queue Setup views — the queue editor's steps, ported in full
     (2026-08-24). Type & journey · Opening hours · Staffing · Resources ·
     Services · Messaging · Publish. Every field writes through
     patchQueue → updateQueue, the op the wizard autosaved with.
     ══════════════════════════════════════════════════════════════════ */
  const qHours = q0.hours ?? [];
  const qPatchHours = (i: number, patch: Partial<{ days: number[]; start: string; end: string }>) =>
    patchQueue((q) => ({ ...q, hours: (q.hours ?? []).map((h, hi) => (hi === i ? { ...h, ...patch } : h)) }));
  const qToggleDay = (i: number, d: number) =>
    patchQueue((q) => ({ ...q, hours: (q.hours ?? []).map((h, hi) => (hi === i ? { ...h, days: h.days.includes(d) ? h.days.filter((x) => x !== d) : [...h.days, d].sort((a, b) => a - b) } : h)) }));
  const clonePages = (pgs: NJPage[]): NJPage[] => pgs.map((pg) => ({ ...pg, uid: njUid("pg"), content: pg.content.map((c) => ({ ...c, uid: njUid("bl") })) }));
  /** Fresh ids for a copied flow, page steps keyed to the cloned pages. Walks
   *  the whole tree, so a copied journey's branches come with it. */
  const remapSteps = (steps: QFStep[], pages: NJPage[]): QFStep[] => qfMapAll(steps, (st) =>
    st.kind === "page"
      ? { ...st, id: String(pages[Number(st.page)]?.uid ?? njUid("st")) }
      : { ...st, id: njUid("st") });
  /** Per-type starter flows (jf-model's qfTypeDefaultFlow), in
   *  journey-document terms: the same pages, messages, waits and decision. */
  /* `buildQueueStarter` / `applyQueueStarter` / `seedFromQueueDoc` removed
     (2026-09-02). They existed to seed or replace a queue's journey from the
     Setup tab's starter tiles; the tiles are gone because a queue journey arrives
     with its pages already made. Copy-a-journey survives them, inverted, on the
     journeys list. */
  const qFlowStats = (() => {
    let messages = 0, decisions = 0, waits = 0;
    qfFlatten(qfFlow.steps).forEach((st) => {
      if (st.kind === "comms" || st.kind === "survey") messages += 1;
      else if (st.kind === "wait") waits += 1;
      else if (st.kind === "decision") decisions += 1;
    });
    return { messages, decisions, waits };
  })();
  const qHeroFile = (f: File | undefined) => {
    if (!f || !/^image\//.test(f.type)) return;
    const rd = new FileReader();
    rd.onload = () => patchQueue((q) => ({ ...q, heroImage: String(rd.result) }));
    rd.readAsDataURL(f);
  };

  const setupQSetup = (
    <div style={setupCard}>
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 12 }}>
        <div>
          <label style={setupFieldLabel}>Queue name <span style={{ color: "#D01647" }}>*</span></label>
          <Input value={q0.name ?? ""} onChange={(e: any) => patchQueue((q) => ({ ...q, name: e.target.value }))} placeholder="e.g. Advising walk-ins" />
        </div>
        <div>
          <label style={setupFieldLabel}>Location <span style={{ color: "#D01647" }}>*</span></label>
          <Dropdown options={(savedLocations as any[]).map((l) => ({ value: l.id, label: l.name }))} value={q0.locationId ?? null}
            onChange={(v: any) => patchQueue((q) => ({ ...q, locationId: v, locationName: (savedLocations as any[]).find((l) => l.id === v)?.name }))}
            placeholder={(savedLocations as any[]).length ? "Pick a location" : "No locations yet"} disabled={!!queueOriginId} style={{ gap: 0 }} />
          {queueOriginId ? <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>A queue belongs to its location's branch company, so it can't move after it exists.</div> : null}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={setupFieldLabel}>Ticket prefix</label>
          <Input value={q0.ticketPrefix ?? ""} onChange={(e: any) => patchQueue((q) => ({ ...q, ticketPrefix: String(e.target.value).toUpperCase().replace(/\s/g, "").slice(0, 4) }))} placeholder="e.g. ADV" />
          <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>Prefix + the customer's join number is their ticket{q0.ticketPrefix ? ` — tickets like ${q0.ticketPrefix}-104` : " — leave blank for numbers only"}.</div>
        </div>
        <div>
          <label style={setupFieldLabel}>Wait target (minutes)</label>
          <NumberField value={Number(q0.slaTargetMins ?? 10)} min={1} onChange={(n: number) => patchQueue((q) => ({ ...q, slaTargetMins: n }))} ariaLabel="Wait target in minutes" style={INPUT as any} />
        </div>
      </div>
      <div>
        <label style={setupFieldLabel}>Hero image</label>
        <div style={{ fontSize: 11, color: "#697180", margin: "0 0 8px", lineHeight: 1.5 }}>Tops every page of this queue's journey. Empty falls back to the brand gradient.</div>
        <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
          <div style={{ width: 200, aspectRatio: "16 / 9", borderRadius: 8, background: q0.heroImage ? `url(${q0.heroImage}) center/cover` : "linear-gradient(135deg, #75ADFA, #5142C7)", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFFFFF", fontSize: 20, fontWeight: 800, flex: "none" }}>
            {!q0.heroImage ? ((q0.name ?? "").trim() ? (q0.name ?? "").trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase() : "Q") : null}
            {q0.heroImage ? <button onClick={() => patchQueue((q) => ({ ...q, heroImage: "" }))} title="Remove the hero image" style={{ position: "absolute", top: 6, right: 6, width: 22, height: 22, borderRadius: 999, border: "none", background: "rgba(0,0,0,0.55)", color: "#FFFFFF", fontSize: 10, cursor: "pointer" }}>✕</button> : null}
          </div>
          <label onDragOver={(e) => { e.preventDefault(); setQHeroDrag(true); }} onDragLeave={() => setQHeroDrag(false)}
            onDrop={(e) => { e.preventDefault(); setQHeroDrag(false); qHeroFile(e.dataTransfer?.files?.[0]); }}
            style={{ flex: 1, border: `1px dashed ${qHeroDrag ? "#5142C7" : "#D1D4DC"}`, background: qHeroDrag ? "#F6F2FE" : "transparent", borderRadius: 8, padding: 16, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5, fontSize: 12, color: "#697180", cursor: "pointer", textAlign: "center" }}>
            <span style={{ fontSize: 16 }}>↥</span>
            <span>{q0.heroImage ? "Replace the image — drop it here or " : "Drop an image here or "}<b style={{ color: "#5142C7" }}>browse</b></span>
            <span style={{ fontSize: 10.5 }}>PNG or JPG · 16:9 works best</span>
            <input type="file" accept="image/*" onChange={(e) => { qHeroFile(e.target.files?.[0]); (e.target as HTMLInputElement).value = ""; }} style={{ display: "none" }} />
          </label>
        </div>
      </div>
      {/* The "Type of queue & starting point" picker is gone (operator,
          2026-09-02: "I do not need the Type of Queue & Starting point content at
          all. The queue pages are already created automatically").
          It offered four starters, a copy-another-queue option and Blank, each
          of which REPLACED the journey's pages and flow. Creating a queue journey
          already seeds them, so the picker's only remaining use was to throw away
          what the template had just made — an action that looks like a choice and
          is really a reset. The pages are edited in Pages and the flow in Flow.
          "Copy another queue's journey" lived here too and is DROPPED (operator,
          2026-09-02) rather than re-sited. It only ever seeded a journey by
          replacing its pages and flow, which is the same reset the starter tiles
          performed — a queue's pages arrive with it, so there is nothing a copy
          adds that is not a discard first. `clonePages`/`remapSteps` stay: the
          journeys list's Duplicate uses them.
          `queueTypes`/`qType` stay: `typeId` still drives the resource-tracking
          preset and the Publish summary's Type row. */}
    </div>
  );

  const setupQHours = (
    <div style={setupCard}>
      <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>When can customers join this queue? Add a set per pattern — weekdays and weekends usually differ. <span style={{ color: "#D01647" }}>At least one set, with days chosen, is required.</span></div>
      {qHours.map((h, i) => (
        <div key={i} style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
            {Q_DAYS.map((d, di) => {
              const on = h.days.includes(di);
              return (
                <button key={d} onClick={() => qToggleDay(i, di)} style={{ width: 42, height: 32, borderRadius: 8, border: `1.5px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#5142C7" : "#FFFFFF", color: on ? "#FFFFFF" : "#333333", fontSize: 11.5, fontWeight: 700, fontFamily: FONT, cursor: "pointer" }}>{d}</button>
              );
            })}
            <span style={{ flex: 1 }} />
            <button onClick={() => patchQueue((q) => ({ ...q, hours: (q.hours ?? []).filter((_, hi) => hi !== i) }))} style={GHOST}>Remove</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, maxWidth: 400 }}>
            <Dropdown options={TIME_OPTS.map((v) => ({ value: v, label: hmLabel12(v) }))} value={h.start} onChange={(v: any) => qPatchHours(i, { start: v })} style={{ gap: 0, width: 140 }} />
            <span style={{ fontSize: 12.5, color: "#697180" }}>to</span>
            <Dropdown options={TIME_OPTS.filter((v) => v > h.start).map((v) => ({ value: v, label: hmLabel12(v) }))} value={h.end} onChange={(v: any) => qPatchHours(i, { end: v })} style={{ gap: 0, width: 140 }} />
          </div>
          {h.days.length === 0 ? <div style={{ fontSize: 11, color: "#C0392B" }}>Pick at least one day for this set.</div> : null}
        </div>
      ))}
      <div><button onClick={() => patchQueue((q) => ({ ...q, hours: [...(q.hours ?? []), { days: [], start: "09:00", end: "17:00" }] }))} style={GHOST}>+ Add another set</button></div>
      <div style={{ fontSize: 12, color: "#333333", padding: "9px 11px", background: "#F6F2FE", border: "1px solid #DCCCFC", borderRadius: 8 }}><b>Open:</b> {qHoursLabel(qHours as any)}</div>
    </div>
  );

  const qStaff = q0.assignedStaff ?? [];
  /** Staff through the same picker, grouped by line of business — `department`
   *  where the record carries one, else the member's first skill, which is the
   *  nearest thing to a line of business the staff record has. */
  const staffLobId = (m: any) => String(m.department ?? m.category ?? (m.skills ?? [])[0] ?? "").trim();
  const staffPickerItems: PickerItem[] = (staffMembers as any[]).map((m) => ({
    id: m.id,
    name: m.name ?? m.id,
    group: staffLobId(m) || null,
    meta: [m.title ?? null, (m.skills ?? []).slice(0, 2).join(", ") || null],
    keywords: [(m.skills ?? []).join(" "), (m.languages ?? []).join(" "), m.department ?? ""].join(" "),
  }));
  const staffPickerGroups: PickerGroup[] = [...new Set((staffMembers as any[]).map(staffLobId).filter(Boolean))]
    .map((id, i) => ({ id: String(id), name: String(id), hue: ["#2D60EB", "#0AA396", "#D61A55", "#916D0F", "#7C3AED", "#1F8A5B"][i % 6]! }));
  const setupQStaffing = (
    <div style={setupCard}>
      <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>Who serves this queue. Add whole lines of business or single people — staff can be added or removed while it is live.</div>
      <CatalogPicker
        items={staffPickerItems}
        groups={staffPickerGroups}
        value={(q0.staffSelection ?? qStaff.map((s) => ({ type: "item" as const, id: s.id }))) as PickerSel[]}
        onChange={(next) => patchQueue((q) => ({
          ...q,
          staffSelection: next,
          /* `assignedStaff` stays the resolved truth the console reads, so a
             live line-of-business rule reaches the floor as real people. */
          assignedStaff: resolveSelection(next, staffPickerItems).map((it) => {
            const m = (staffMembers as any[]).find((x) => x.id === it.id);
            return { id: it.id, name: it.name, role: m?.title ?? null };
          }),
        }))}
        sourceLabel="Your staff"
        selectedLabel="Serving this queue"
        noun="staff member"
        searchPlaceholder="Search people, skills or lines of business…"
        emptySource="No staff in the directory yet."
        emptySelection="Pick who serves this queue — the order here is the order the console offers them." />
    </div>
  );

  const qRes = q0.resources ?? [];
  /** Resources through the same picker, grouped by resource type ("Room",
   *  "Desk", "Equipment"), which is the resource record's own department. */
  const resPickerItems: PickerItem[] = ((resourceRecs as any[]) ?? []).map((r) => ({
    id: r.name, name: r.name, group: (r.type || r.kind || "") || null,
    meta: [r.type || null, r.location || null], keywords: [r.description ?? "", (r.groups ?? []).join(" ")].join(" "),
  })).filter((r) => r.name);
  const resPickerGroups: PickerGroup[] = [...new Set(((resourceRecs as any[]) ?? []).map((r) => (r.type || r.kind || "")).filter(Boolean))]
    .map((id, i) => ({ id: String(id), name: String(id), hue: ["#0AA396", "#2D60EB", "#916D0F", "#D61A55", "#7C3AED"][i % 5]! }));
  const setupQResources = (
    <div style={setupCard}>
      <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>Rooms, desks or equipment this queue consumes. Availability is tracked per resource while the queue runs.</div>
      <CatalogPicker
        items={resPickerItems}
        groups={resPickerGroups}
        value={(q0.resourceSelection ?? (qRes as string[]).map((r) => ({ type: "item" as const, id: r }))) as PickerSel[]}
        onChange={(next) => patchQueue((q) => ({
          ...q,
          resourceSelection: next,
          resources: resolveSelection(next, resPickerItems).map((it) => it.name),
        }))}
        sourceLabel="Your resources"
        selectedLabel="Used by this queue"
        noun="resource"
        searchPlaceholder="Search resources or types…"
        emptySource="Create resources under Resources and they'll show up here."
        emptySelection="Pick the rooms, desks or equipment this queue consumes." />
      {qType && !(qType.presets as any)?.resourceTracking ? <div style={{ fontSize: 11.5, color: "#697180" }}>{qType.label} queues don't track resources by default — anything added here is still enforced.</div> : null}
    </div>
  );

  /* The queue's own selection helpers (qSelHas / qSelAdd / qSelRemove /
     qSelMove / qSelDropOn / qSvcRow / qSelLabel / uncategorisedSvcs) are gone
     with the hand-rolled picker they served — CatalogPicker owns add, remove,
     reorder and labelling now, and qPickerCommit is the only writer. */
  /** The per-service matrix, shared by the queue Services panel and reused in
   *  shape by the appointment rail. What each offered service asks and sends —
   *  the surface the withdrawn page pickers were standing in for. */
  const perServiceMatrix = (method: "queueing" | "appointments", svcIds: string[]) => {
    const catSvcs = ((cat?.services ?? []) as any[]);
    const rows = svcIds.map((id) => {
      const sv = catSvcs.filter((x: any) => x.id === id || x.name === id)[0];
      const gid = sv?.questionGroups?.[method] ?? null;
      const g = gid ? qGroups.find((x: any) => x.id === gid) : null;
      return { id: String(id), name: sv?.name ?? String(id), svcId: sv?.id ?? null, group: g ?? null, gid };
    });
    return (
      <div>
        <div style={{ ...setupFieldLabel, marginBottom: 8 }}>What each service asks and sends</div>
        {rows.length === 0 ? (
          <div style={{ border: "1px dashed #D1D4DC", borderRadius: 9, padding: "16px 12px", textAlign: "center", fontSize: 11.5, color: "#697180" }}>No services selected yet.</div>
        ) : (
          <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.4fr 1fr", gap: 10, padding: "8px 12px", background: "#F7F7FA", fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#697180" }}>
              <span>Service</span><span>Booking questions</span><span>Survey · promo · flow</span>
            </div>
            {rows.map((r) => (
              <div key={r.id} style={{ display: "grid", gridTemplateColumns: "1.2fr 1.4fr 1fr", gap: 10, padding: "10px 12px", borderTop: "1px solid #EEF0F3", alignItems: "baseline", fontSize: 11.5 }}>
                <span style={{ fontWeight: 700 }}>{r.name}</span>
                <span>
                  {r.group
                    ? <>{r.group.name} <span style={{ color: "#697180" }}>· {r.group.items?.length ?? 0} questions</span><br /><a href="#" onClick={(e) => { e.preventDefault(); goRoute("questions.group.edit", { id: r.group.id }); }} style={{ fontSize: 11 }}>Edit group →</a></>
                    : <span style={{ color: "#916D0F" }}>None set{r.svcId ? <> — <a href="#" onClick={(e) => { e.preventDefault(); goRoute("ops.catalogue", { service: r.svcId! }); }}>set one on the service</a></> : null}</span>}
                </span>
                <span style={{ color: "#8A92A0", fontSize: 10.5, lineHeight: 1.5 }}>Pending the catalogue fields</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ── The one picker (2026-09-02, operator) ──────────────────────────────
     Every "what does this journey offer" list on every setup step now runs
     through CatalogPicker: departments with an Add-all that stays live, search
     across name / department / keywords, and an ordered selection. These
     adapters are the only per-kind code left — items in, selection out. */
  /** Queue services. The queue's own `{type:"category"|"service"}` shape is
   *  translated to the picker's `{type:"group"|"item"}` here rather than
   *  migrating the stored shape, so an older queue round-trips untouched. */
  const qPickerItems: PickerItem[] = svcCatServices.map((s) => {
    /* Queue-only names are no longer OFFERED (operator: stop offering them,
       migrate on next save) — but they stay in `items` so an existing queue's
       selection still RESOLVES and shows its real name. Dropping them from the
       array entirely made every legacy pick read "No longer in the catalogue"
       and reported the queue as offering nothing. */
    const legacy = s.id.startsWith(QNAME_ID);
    return {
      id: s.id,
      name: s.name,
      group: legacy ? null : (s.category ?? null),
      meta: legacy ? ["Not in your catalogue — migrates when you next save"] : [s.listedDuration],
      keywords: (s as any).description ?? "",
      offerable: !legacy,
    };
  });
  const qPickerGroups: PickerGroup[] = svcCatCategories.filter((c) => c.id !== QUEUE_ONLY_CAT);
  const qPickerValue: PickerSel[] = (q0.serviceSelection ?? []).map((s) => ({ type: s.type === "category" ? "group" : "item", id: s.id } as PickerSel));
  /** Write back, migrating queue-only entries as we go: a `name:` id whose name
   *  now exists in the catalogue becomes that service; one that does not is
   *  dropped rather than carried forward for ever. */
  const qPickerCommit = (next: PickerSel[]) => patchQueue((q) => ({
    ...q,
    /* Return type annotated (2026-09-08). Without it TypeScript infers the
       union from the FIRST arm — `{type:"category"}` — and the two `"service"`
       arms below then fail to assign. Naming the element type up front makes all
       three arms members of the same union rather than candidates to widen it. */
    serviceSelection: next.flatMap((s): { type: "category" | "service"; id: string }[] => {
      if (s.type === "group") return [{ type: "category" as const, id: s.id }];
      if (s.id.startsWith(QNAME_ID)) {
        const hit = svcCatServices.find((x) => !x.id.startsWith(QNAME_ID) && x.name === s.id.slice(QNAME_ID.length));
        return hit ? [{ type: "service" as const, id: hit.id }] : [];
      }
      return [{ type: "service" as const, id: s.id }];
    }),
  }));

  const setupQServices = (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 900 }}>
      {/* The per-service matrix (2026-08-31): the surface the withdrawn page
          pickers were standing in for. Placed FIRST so an operator changing the
          selection below sees immediately what each service brings with it. */}
      {perServiceMatrix("queueing", (q0.services ?? []).map(String))}
      <div style={{ height: 1, background: "#E6E8EC" }} />
      <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>What can customers queue for? Add whole departments or single services — the order here is the order on the Services page. <span style={{ color: "#D01647" }}>At least one is required.</span></div>
      <CatalogPicker
        items={qPickerItems}
        groups={qPickerGroups}
        value={qPickerValue}
        onChange={qPickerCommit}
        sourceLabel="Your catalogue"
        selectedLabel="In this queue"
        noun="service"
        searchPlaceholder="Search services or departments…"
        emptySource="Your services catalogue is empty — create services first."
        emptySelection="Add services from your catalogue — they appear here in customer order." />
    </div>
  );

  /* Setup · Messaging removed (operator, 2026-09-02: "Messaging is also not
     necessary, as messages are now added and defined in the flow itself").
     The panel picked a library message flow and then linked to the Flow canvas
     for its steps — two places to answer one question, and the canvas is the one
     that can actually change what a customer receives. `messagingFlowId` stays
     on the record and is still written by the flow; only the duplicate picker
     is gone. */
  const reloadQueues = () => listQueues().then((p: any) => setQueues(p?.data ?? [])).catch(() => {});
  const qLifecycle = async (status: "open" | "paused" | "closed", verb: string) => {
    if (!queueOriginId) { showToast("This journey isn't attached to a queue yet."); return; }
    setPublishBusy(status === "open" ? "publish" : "offline");
    try {
      await patchQueueConfig(queueOriginId, {
        name: q0.name, ticketPrefix: q0.ticketPrefix, slaTargetMins: q0.slaTargetMins, needed: q0.needed,
        hours: q0.hours, assignedStaff: q0.assignedStaff, services: q0.services,
        serviceSelection: q0.serviceSelection, resources: q0.resources,
        heroImage: q0.heroImage || null, journeySource: q0.journeySource, messagingFlowId: q0.messagingFlowId,
      });
      await setQueueLifecycle(queueOriginId, status);
      patchQueue((q) => ({ ...q, status }));
      await reloadQueues();
      showToast(verb);
    } catch (e) {
      showToast(`Couldn't ${status === "open" ? "publish" : "update"} the queue — ${e instanceof Error ? e.message : String(e)}`);
    } finally { setPublishBusy(null); }
  };
  const qJoinUrl = () => publicUrl(q0.joinCode || `q/${queueOriginId ?? ""}`, { prefix: "join" });
  const qActive: QueueEntry[] = qBoard ? [
    ...(qBoard.entries ?? []).filter((e) => e.status === "serving"),
    ...(qBoard.entries ?? []).filter((e) => e.status === "called"),
    ...(qBoard.entries ?? []).filter((e) => e.status === "waiting").sort((a, b) => a.position - b.position),
  ] : [];
  const setupQPublish = (
    <div style={setupCard}>
      <div style={{ border: `1px solid ${qReady ? "#A7DACB" : "#F3D0CB"}`, background: qReady ? "#F1FAF6" : "#FDECEA", borderRadius: 10, padding: "12px 14px" }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: qReady ? "#1F8A5B" : "#C0392B", marginBottom: 8 }}>
          {qReady ? (q0.status === "open" ? "Everything required is set — edits apply to the running queue immediately" : "Everything required is set — this queue can open") : `${qChecks.filter((c) => !c.done).length} required field${qChecks.filter((c) => !c.done).length === 1 ? "" : "s"} still to complete`}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {qChecks.map((c) => (
            <div key={c.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
              <span style={{ color: c.done ? "#1F8A5B" : "#C0392B", flex: "none" }}>{c.done ? "✓" : "✕"}</span>
              <span style={{ flex: 1, color: c.done ? "#333333" : "#C0392B", fontWeight: c.done ? 500 : 600 }}>{c.label}</span>
              {!c.done ? <button onClick={() => {
                /* Services lost their Setup pill (operator 2026-08-24) — they're
                   picked on the services PAGE now, so that's where Fix goes. */
                if (c.tab === "qservices") {
                  const idx = Math.max(0, (D?.pages ?? []).findIndex((pg) => pg.id === "services"));
                  set({ setupView: null, view: "pages", cur: idx, sel: null });
                } else set({ setupView: c.tab as any });
              }} style={{ border: "none", background: "transparent", color: "#5142C7", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Fix →</button> : null}
            </div>
          ))}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#333333", marginBottom: 10 }}>{q0.name || "Untitled queue"}</div>
        {([
          ["Type", qType?.label ?? q0.typeId ?? "—"],
          ["Location", q0.locationName ?? (savedLocations as any[]).find((l) => l.id === q0.locationId)?.name ?? "—"],
          ["Ticket prefix", q0.ticketPrefix ? `${q0.ticketPrefix} — tickets like ${q0.ticketPrefix}-104` : "—"],
          ["Wait target", `${q0.slaTargetMins ?? 10} minutes`],
          ["Opening hours", qHoursLabel(qHours as any)],
          ["Staff", qStaff.length ? qStaff.map((s) => s.name).join(", ") : "No one assigned yet"],
          /* What the selection RESOLVES TO today, not just that a department is
             live (operator ruling, 2026-09-02): a live rule is the right default
             but Publish is where you check what is actually being offered. */
          ["Services", describeSelection(qPickerValue, qPickerItems, qPickerGroups, "service")],
          ["Resources", qRes.length ? qRes.join(", ") : "—"],
          ["Journey", `${D?.pages.length ?? 0} page${(D?.pages.length ?? 0) === 1 ? "" : "s"}`],
          ["Message flow", allFlows.find((f: any) => f.id === q0.messagingFlowId)?.name ?? "None selected"],
        ] as [string, string][]).map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 12.5, borderBottom: "1px solid #E6E8EC", padding: "8px 0" }}>
            <span style={{ color: "#697180", flex: "none" }}>{k}</span><span style={{ fontWeight: 600, color: "#333333", textAlign: "right" }}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {q0.status === "open" ? (
          <>
            <button onClick={() => qLifecycle("paused", "Queue paused — no new joins, the line keeps its place")} disabled={!!publishBusy} style={GHOST}>Pause joins</button>
            <button onClick={() => qLifecycle("closed", "Queue closed")} disabled={!!publishBusy} style={GHOST}>Close the queue</button>
            <span style={{ flex: 1 }} />
            <button onClick={() => nav.go("queues.console", { q: queueOriginId ?? "" })} style={PRIMARY}>Open the console →</button>
          </>
        ) : (
          <>
            {/* ONE button (operator, 2026-09-02). Publish creates the record if
                it does not exist yet and then opens it — the same fold the board
                publish has always used. Two buttons made record-creation look
                like a separate decision, and it is not: a journey nobody can join
                is not published, whatever the record says. */}
            <button
              onClick={async () => {
                if (queueOriginId) { qLifecycle("open", "Queue published — it's live and taking customers"); return; }
                setPublishBusy("publish");
                try {
                  const r = await runAttach();
                  /* Only open it if the create succeeded. `runAttach` toasts its
                     own failure, so a refused create must not be followed by a
                     lifecycle call against a record that does not exist. */
                  if (r) qLifecycle("open", "Queue published — it's live and taking customers");
                } finally { setPublishBusy(null); }
              }}
              disabled={!qReady || !!publishBusy || attachBusy}
              style={{ ...PRIMARY, opacity: !qReady || publishBusy || attachBusy ? 0.55 : 1 }}
            >
              {publishBusy === "publish" ? "Publishing…" : q0.status === "paused" ? "Resume the queue" : "Publish the queue"}
            </button>
          </>
        )}
      </div>
      {q0.status === "open" && queueOriginId ? (
        <>
          <div style={{ height: 1, background: "#E6E8EC" }} />
          <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: 14, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#333333", marginBottom: 3 }}>The customer's journey link</div>
              <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5, marginBottom: 8 }}>Print it, share it, or put the QR on the desk — it opens this journey's first page.</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ flex: 1, minWidth: 200, fontSize: 11.5, padding: "8px 10px", border: "1px solid #E6E8EC", borderRadius: 8, background: "#FBFAFF", ...MONO, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{qJoinUrl()}</span>
                <button onClick={() => { void copyText(qJoinUrl()).then((ok) => showToast(ok ? "Journey link copied" : "Couldn't copy — select the link and copy it manually")); }} style={GHOST}>Copy link</button>
                <button onClick={() => nav.go("queue.join", { q: queueOriginId })} style={GHOST}>Preview →</button>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center", flex: "none" }}>
              <div style={{ padding: 8, border: "1px solid #E6E8EC", borderRadius: 8, background: "#FFFFFF" }}><QrCode value={qJoinUrl()} size={112} /></div>
              <button onClick={() => { const ok = downloadQrPng(qJoinUrl(), `${(q0.name || "queue").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-qr`); showToast(ok ? "QR downloaded — a print-ready PNG is in your downloads" : "Couldn't generate the QR code"); }} style={GHOST}>Download PNG</button>
            </div>
          </div>
          <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "#333333" }}>In the queue now — {qActive.length}</div>
                <div style={{ fontSize: 11.5, color: "#697180", marginTop: 2 }}>Live from the queue board, refreshed every 10 seconds.</div>
              </div>
              <button onClick={() => { setMsgTargets(qActive); setMsgText(""); }} disabled={!qActive.length} style={{ ...PRIMARY, opacity: qActive.length ? 1 : 0.5 }}>Message everyone</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {qActive.map((e) => (
                <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", border: "1px solid #E6E8EC", borderRadius: 9 }}>
                  {e.ticket ? <span style={{ fontSize: 11.5, fontWeight: 700, color: "#333333", flex: "none", ...MONO }}>{e.ticket}</span> : null}
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: "#333333", flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.name}{e.party > 1 ? <span style={{ color: "#697180", fontWeight: 500 }}> +{e.party - 1}</span> : null}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: e.status === "serving" ? "#E4F5EF" : e.status === "called" ? "#ECF1FE" : "#F4F5F7", color: e.status === "serving" ? "#1F8A5B" : e.status === "called" ? "#2D60EB" : "#697180", flex: "none" }}>{e.status === "serving" ? "Serving" : e.status === "called" ? "Called" : `#${e.position}`}</span>
                  <span style={{ fontSize: 11, color: "#697180", flex: "none", ...MONO }}>{e.waitMins}m</span>
                  <button onClick={() => { setMsgTargets([e]); setMsgText(""); }} style={GHOST}>Message</button>
                </div>
              ))}
              {!qActive.length ? <div style={{ padding: "14px 12px", border: "1px dashed #D1D4DC", borderRadius: 9, fontSize: 12, color: "#697180", textAlign: "center" }}>Nobody in the line right now.</div> : null}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );

  /* ══════════════════════════════════════════════════════════════════
     Display board — Setup · Configuration (the board builder's options)
     ══════════════════════════════════════════════════════════════════ */
  const bDark = b0.theme === "dark";
  const bC = boardColors(bDark);
  const bUsedTypes = new Set(b0.blocks.map((x) => x.type));
  const bSelected = b0.blocks.find((x) => x.uid === ui.boardSel) ?? null;
  const bSelectedDef = bSelected ? BOARD_BLOCKS.find((d) => d.id === bSelected.type) ?? null : null;
  const bAddBlock = (type: string, beforeUid?: string | null) => {
    const def = BOARD_BLOCKS.find((d) => d.id === type);
    if (!def || (!def.multi && bUsedTypes.has(type))) return;
    const nb: BoardBlockRec = { uid: boardBlockUid(), type, cfg: { ...(def.defaults ?? {}) } };
    patchBoard((b) => {
      const i = beforeUid ? b.blocks.findIndex((x) => x.uid === beforeUid) : -1;
      return { ...b, blocks: i < 0 ? [...b.blocks, nb] : [...b.blocks.slice(0, i), nb, ...b.blocks.slice(i)] };
    });
    set({ boardSel: nb.uid });
  };
  const bRemoveBlock = (uid: string) => { patchBoard((b) => ({ ...b, blocks: b.blocks.filter((x) => x.uid !== uid) })); if (ui.boardSel === uid) set({ boardSel: null }); };
  const bMoveBlock = (uid: string, dir: number) => patchBoard((b) => {
    const i = b.blocks.findIndex((x) => x.uid === uid); const j = i + dir;
    if (i < 0 || j < 0 || j >= b.blocks.length) return b;
    const next = b.blocks.slice(); const [it] = next.splice(i, 1); next.splice(j, 0, it!);
    return { ...b, blocks: next };
  });
  /* ── One-Stop · Configuration ──────────────────────────────────────────
   *  The Setup step a One-Stop journey was missing. A Display Board has one for
   *  the same reason: the page itself is composed on the canvas, but the things
   *  that are true of the WHOLE page — its name, its URL, the language and zone
   *  it renders in, and which of the tenant's journeys it offers — are not
   *  blocks and have nowhere on a canvas to live.
   *
   *  The journey picker is the part worth explaining. It does not store a list.
   *  Ticking a journey writes its origin id into the SCOPE of the matching live
   *  block — a queue journey into `queues`, an appointment journey into
   *  `appointments`, an event journey into `events` — creating that block if the
   *  page has none. So the page keeps exactly one source of truth (the blocks),
   *  and the picker is a faster way to reach it than selecting each block and
   *  filling its scope by hand. Untick and the id comes out; the block stays,
   *  because removing a block an author placed is not what unticking a journey
   *  asked for.
   */

  const setupOneStopCfg = (
    <div style={{ display: "flex", gap: 16, maxWidth: 1120, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>
          What is true of the whole page: what it is called, where it lives, and which of your journeys
          a student can reach from it. The page itself is composed on the canvas.
        </div>

        <div>
          {reqLabel("Page name")}
          <Input value={D?.name ?? ""} onChange={(e: any) => patchDoc((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. Student Center One-Stop" />
          <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>Shown in the journeys list, and used as the page heading unless a block overrides it.</div>
        </div>

        <div>
          <label style={setupFieldLabel}>Web address</label>
          <div style={{ display: "flex", alignItems: "center", gap: 0, border: "1px solid #D1D4DC", borderRadius: 8, overflow: "hidden", background: onestopOriginId ? "#FAFAFD" : "#FFFFFF" }}>
            <span style={{ padding: "8px 0 8px 10px", fontSize: 12, color: "#8A92A0", flex: "none", ...MONO }}>onestop/</span>
            <input
              value={osCfg.slug ?? slugFromTitle(D?.name || "")}
              readOnly={!!onestopOriginId}
              onChange={(e) => patchOsSettings({ slug: slugFromTitle(e.target.value) })}
              style={{ flex: 1, minWidth: 0, border: "none", outline: "none", padding: "8px 10px 8px 0", fontSize: 12.5, fontFamily: FONT, color: onestopOriginId ? "#697180" : "#333333", background: "transparent" }}
            />
          </div>
          <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>
            {onestopOriginId
              ? "Fixed once the page exists — it is the URL students have, so changing it would break every link to it."
              : "Taken from the name if you leave it. It becomes fixed when the page is created."}
          </div>
        </div>

        <div>
          <label style={setupFieldLabel}>Navigation label</label>
          <Input value={osCfg.navLabel ?? ""} onChange={(e: any) => patchOsSettings({ navLabel: e.target.value || null })} placeholder="Leave empty to use the page name" />
          <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>How this page is named in the portal's own navigation, where it sits beside your other One-Stop pages.</div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={setupFieldLabel}>Language</label>
            <Dropdown
              options={["en-US", "es-US", "fr-CA", "de-DE", "it-IT"].map((v) => ({ value: v, label: v }))}
              value={osCfg.locale ?? "en-US"}
              onChange={(v: string) => patchOsSettings({ locale: v, supportedLocales: [...new Set([v, ...(osCfg.supportedLocales ?? [])])] })}
            />
            <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>The language your text is written in. Students see their own where a translation exists.</div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {reqLabel("Time zone")}
            <TimeZonePicker value={osCfg.timeZone ?? tzContext().zone} onChange={(v: string) => patchOsSettings({ timeZone: v })} />
            <div style={{ fontSize: 10.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>Every opening time and date on the page is shown in this zone.</div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid #E6E8EC", paddingTop: 16 }}>
          {reqLabel("What this page offers")}
          <div style={{ fontSize: 11, color: "#697180", margin: "0 0 12px", lineHeight: 1.5 }}>
            Three lists, one per block. Tick what this page should offer — each list feeds the matching
            block on the page, and adds that block if the page has none yet.
          </div>
          {osJourneyRows.length === 0 ? (
            <div style={{ border: "1px dashed #D1D4DC", borderRadius: 8, padding: "16px 12px", textAlign: "center", fontSize: 11.5, color: "#697180", lineHeight: 1.55 }}>
              Nothing to offer yet. Create a bookable service, a queue or an event and it will appear here.
            </div>
          ) : (
            /* Three CatalogPickers, one per block (operator, 2026-09-02). Same
               component as every other picker in the designer, with grouping
               OFF: a One Stop page's three lists are each one flat kind by
               design, and the ruling was that this is the exception to the
               department breakdown. Each is ordered — the order here is the
               order the blocks sit on the page. */
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, alignItems: "start" }}>
              {["Bookable services", "Queues", "Events"].map((group) => {
                const rows = osJourneyRows.filter((r) => r.group === group);
                const kind = rows[0]?.kind ?? (group === "Queues" ? "queue" : group === "Events" ? "event" : "service");
                const picked = osScopeIds(kind);
                return (
                  <CatalogPicker
                    key={group}
                    dense
                    grouped={false}
                    allowGroups={false}
                    /* `r.label` is not a field on these rows — they carry `name`
                 (2026-09-08). The `??` chain read as a tolerant fallback while
                 the first term could never be anything but undefined. */
              items={rows.map((r) => ({ id: r.id, name: r.name ?? r.id, meta: [r.sub ?? null], keywords: r.sub ?? "" }))}
                    value={picked.map((id) => ({ type: "item" as const, id }))}
                    onChange={(next) => {
                      const want = next.filter((s) => s.type === "item").map((s) => s.id);
                      /* osToggleJourney owns block creation and removal, so the
                         diff is applied through it rather than rebuilding the
                         block list here. */
                      picked.filter((id) => !want.includes(id)).forEach((id) => osToggleJourney(kind, id));
                      want.filter((id) => !picked.includes(id)).forEach((id) => osToggleJourney(kind, id));
                    }}
                    sourceLabel={group}
                    selectedLabel="On this page"
                    noun={group === "Queues" ? "queue" : group === "Events" ? "event" : "service"}
                    searchPlaceholder="Search…"
                    emptySource="None yet."
                    emptySelection="Nothing from this list yet." />
                );
              })}
            </div>
          )}
          <div style={{ fontSize: 10.5, color: "#697180", marginTop: 10, lineHeight: 1.5 }}>
            The page stores what to show, never a copy of it — rename a queue or add a time to a service
            and this page follows without being republished.
          </div>
        </div>
      </div>

      <div style={{ width: 260, flex: "none", border: "1px solid #E6E8EC", borderRadius: 10, padding: 14, background: "#FFFFFF" }}>
        <div style={{ ...SEC, marginBottom: 9 }}>On this page</div>
        {osBlocks.length === 0 ? (
          <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.55 }}>Nothing on this page yet — that is the right place to start. Tick what it should offer below, and a block appears for each one.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {osBlocks.map((b) => (
              <div key={b.uid} style={{ display: "flex", alignItems: "baseline", gap: 7, fontSize: 11.5 }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: "#5142C7", flex: "none" }} />
                <span style={{ flex: 1, minWidth: 0, color: "#333333" }}>{osLabel(b.type)}</span>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => set({ setupView: null, view: "pages" })} style={{ ...GHOST, width: "100%", marginTop: 12 }}>Open the page canvas →</button>
        {/* Start over (operator, 2026-09-02: "start with nothing selected, let me
            make all selections from the beginning").
            A NEW One-Stop journey has started empty since this morning, but a page
            created before that still carries the seven blocks the old seed made —
            including three types the palette no longer offers, so they can be seen
            and removed but not put back. This empties the page in one act instead
            of asking for seven deletions.
            No confirmation dialog: it is one undo away, and the toast says so.
            Guarding a reversible action with a modal trains people to dismiss
            modals. */}
        {osBlocks.length > 0 ? (
          <button
            onClick={() => {
              const n = osBlocks.length;
              setOsBlocks([]);
              set({ selSection: null });
              showToast(`Cleared ${n} block${n === 1 ? "" : "s"} — undo brings them back`);
            }}
            style={{ width: "100%", marginTop: 8, height: 30, border: "1px solid #F3D0CB", borderRadius: 8, background: "#FFFFFF", color: "#C0392B", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
          >
            Clear the page and start over
          </button>
        ) : null}
      </div>
    </div>
  );

  const bPatchCfg = (uid: string, patch: Record<string, unknown>) => patchBoard((b) => ({ ...b, blocks: b.blocks.map((x) => (x.uid === uid ? { ...x, cfg: { ...x.cfg, ...patch } } : x)) }));
  const bRadio = (on: boolean) => ({ flex: 1, minHeight: 32, borderRadius: 8, border: `1.5px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#F6F2FE" : "#FFFFFF", color: on ? "#5142C7" : "#333333", fontSize: 11.5, fontWeight: 600, fontFamily: FONT, cursor: "pointer" } as const);
  const setupBoardCfg = (
    <div style={{ display: "flex", gap: 16, maxWidth: 900, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 12.5, color: "#697180", lineHeight: 1.55 }}>Everything the lobby screen shows: which queues, what content, how queuers are named, and the welcome line underneath.</div>
        <div>
          <label style={setupFieldLabel}>Board name <span style={{ color: "#D01647" }}>*</span></label>
          <Input value={b0.name ?? ""} onChange={(e: any) => patchBoard((b) => ({ ...b, name: e.target.value }))} placeholder="e.g. Student Center — lobby TV" />
        </div>
        <div>
          <label style={setupFieldLabel}>Queues shown <span style={{ color: "#D01647" }}>*</span></label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(queues as any[]).map((qq) => {
              const on = b0.queueIds.includes(qq.id);
              return (
                <button key={qq.id} onClick={() => patchBoard((b) => ({ ...b, queueIds: on ? b.queueIds.filter((x) => x !== qq.id) : [...b.queueIds, qq.id] }))}
                  style={{ minHeight: 30, padding: "0 11px", borderRadius: 999, border: `1.5px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#F6F2FE" : "#FFFFFF", color: on ? "#5142C7" : "#333333", fontSize: 11.5, fontWeight: 600, fontFamily: FONT, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
                  {on ? "✓" : "+"} {qq.name}
                </button>
              );
            })}
            {!(queues as any[]).length ? <span style={{ fontSize: 12, color: "#B5BAC6" }}>No queues yet — create one first.</span> : null}
          </div>
        </div>
        <div>
          {reqLabel("Fixed content", "At least one block is required")}
          <div style={{ fontSize: 11, color: "#697180", margin: "0 0 8px", lineHeight: 1.5 }}>These blocks compose each queue's card, top to bottom. Click one in the list to configure it.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
            {BOARD_BLOCKS.map((bb) => {
              const used = !bb.multi && bUsedTypes.has(bb.id);
              return (
                <button key={bb.id} onClick={() => bAddBlock(bb.id)} disabled={used} title={bb.blurb}
                  style={{ textAlign: "left", padding: "9px 10px", border: `1px solid ${used ? "#5142C7" : "#E6E8EC"}`, background: used ? "#F6F2FE" : "#FFFFFF", borderRadius: 8, cursor: used ? "default" : "pointer", fontFamily: FONT }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: used ? "#5142C7" : "#333333" }}>{bb.l}{used ? " ✓" : ""}</div>
                  <div style={{ fontSize: 10.5, color: "#697180", marginTop: 2, lineHeight: 1.4 }}>{bb.blurb}</div>
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {b0.blocks.map((bb, i) => {
              const def = BOARD_BLOCKS.find((d) => d.id === bb.type);
              const on = ui.boardSel === bb.uid;
              return (
                <div key={bb.uid} onClick={() => set({ boardSel: on ? null : bb.uid })}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `1px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#FCFBFF" : "#FFFFFF", borderRadius: 8, cursor: "pointer" }}>
                  <span style={{ fontSize: 10.5, color: "#B5BAC6", flex: "none", ...MONO }}>{i + 1}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 700, color: "#333333" }}>{def?.l ?? bb.type}</span>
                  <button onClick={(e) => { e.stopPropagation(); bMoveBlock(bb.uid, -1); }} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", fontSize: 11 }}>▲</button>
                  <button onClick={(e) => { e.stopPropagation(); bMoveBlock(bb.uid, 1); }} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", fontSize: 11 }}>▼</button>
                  <button onClick={(e) => { e.stopPropagation(); bRemoveBlock(bb.uid); }} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", fontSize: 11 }}>✕</button>
                </div>
              );
            })}
            {!b0.blocks.length ? <div style={{ padding: "14px 12px", border: "1px dashed #D1D4DC", borderRadius: 9, fontSize: 12, color: "#697180", textAlign: "center" }}>No content yet — add a block above.</div> : null}
          </div>
        </div>
        <div>
          <label style={setupFieldLabel}>Theme</label>
          <div style={{ display: "flex", gap: 8, maxWidth: 300 }}>
            <button onClick={() => patchBoard((b) => ({ ...b, theme: "dark" }))} style={bRadio(bDark)}>Dark</button>
            <button onClick={() => patchBoard((b) => ({ ...b, theme: "light" }))} style={bRadio(!bDark)}>Light</button>
          </div>
        </div>
        <div>
          <label style={setupFieldLabel}>Queuer display</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, maxWidth: 360 }}>
            {([["full", "Full name"], ["first-initial", "First + initial"], ["first", "First only"], ["ticket", "Ticket only"]] as [BoardNameFormat, string][]).map(([id, lbl]) => (
              <button key={id} onClick={() => patchBoard((b) => ({ ...b, nameFormat: id }))} style={{ ...bRadio(b0.nameFormat === id), minHeight: 30 }}>{lbl}</button>
            ))}
          </div>
          {b0.nameFormat === "ticket" ? <div style={{ fontSize: 11, color: "#697180", marginTop: 5, lineHeight: 1.5 }}>Tickets only — no names on screen. Queuers without a ticket fall back to a shortened name.</div> : null}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, padding: "10px 12px", border: "1px solid #E6E8EC", borderRadius: 9, maxWidth: 360 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: "#333333" }}>Show wait estimates</span>
            <Toggle on={!!b0.showWaitEstimates} onChange={() => patchBoard((b) => ({ ...b, showWaitEstimates: !b.showWaitEstimates }))} />
          </div>
        </div>
        <div>
          <label style={setupFieldLabel}>Welcome message</label>
          <textarea value={b0.message} onChange={(e) => patchBoard((b) => ({ ...b, message: e.target.value }))} rows={2} style={{ ...INPUT, resize: "vertical" as const, minHeight: 54 }} placeholder="Shown under the board, across the full width" />
        </div>
        <div style={NOTE}>The board is a library object: this configuration saves through the boards library, so the lobby display and this journey always show the same thing.</div>
      </div>
      {bSelected && bSelectedDef ? (
        <div style={{ width: 240, flex: "none", background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ ...SEC, marginBottom: 0, flex: 1 }}>Block · {bSelectedDef.l}</span>
            <button onClick={() => set({ boardSel: null })} style={{ border: "none", background: "transparent", color: "#697180", cursor: "pointer", fontSize: 11 }}>✕</button>
          </div>
          {"label" in bSelected.cfg || ["now-serving", "called", "up-next"].includes(bSelected.type) ? (
            <div><label style={setupFieldLabel}>Heading</label><Input value={String(bSelected.cfg.label ?? "")} onChange={(e: any) => bPatchCfg(bSelected.uid, { label: e.target.value })} /></div>
          ) : null}
          {bSelected.type === "called" || bSelected.type === "up-next" ? (
            <div><label style={setupFieldLabel}>People shown</label><NumberField value={Number(bSelected.cfg.max ?? (bSelected.type === "called" ? 3 : 6))} min={1} onChange={(n: number) => bPatchCfg(bSelected.uid, { max: n })} ariaLabel="People shown" style={INPUT as any} /></div>
          ) : null}
          {bSelected.type === "hours" ? (
            <div><label style={setupFieldLabel}>Prefix</label><Input value={String(bSelected.cfg.prefix ?? "Open")} onChange={(e: any) => bPatchCfg(bSelected.uid, { prefix: e.target.value })} /></div>
          ) : null}
          {bSelected.type === "text" ? (
            <div><label style={setupFieldLabel}>Text</label><textarea value={String(bSelected.cfg.text ?? "")} onChange={(e) => bPatchCfg(bSelected.uid, { text: e.target.value })} rows={3} style={{ ...INPUT, resize: "vertical" as const, minHeight: 62 }} /></div>
          ) : null}
          {bSelected.type === "stats" ? <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5 }}>Shows the waiting count{b0.showWaitEstimates ? " and the estimated wait" : ""} — both come from the live queue.</div> : null}
          {bSelected.type === "services" ? <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5 }}>Per-service waiting counts, live from each queue's own service list.</div> : null}
        </div>
      ) : null}
    </div>
  );

  const setupBoardPublish = (
    <div style={setupCard}>
      <div style={{ border: `1px solid ${bReady ? "#A7DACB" : "#F3D0CB"}`, background: bReady ? "#F1FAF6" : "#FDECEA", borderRadius: 10, padding: "12px 14px" }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: bReady ? "#1F8A5B" : "#C0392B", marginBottom: 8 }}>{bReady ? "This board is ready for the lobby screen" : "Finish the configuration first"}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {bChecks.map((c) => (
            <div key={c.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
              <span style={{ color: c.done ? "#1F8A5B" : "#C0392B", flex: "none" }}>{c.done ? "✓" : "✕"}</span>
              <span style={{ flex: 1, color: c.done ? "#333333" : "#C0392B", fontWeight: c.done ? 500 : 600 }}>{c.label}</span>
              {!c.done ? <button onClick={() => set({ setupView: "boardcfg" })} style={{ border: "none", background: "transparent", color: "#5142C7", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Fix →</button> : null}
            </div>
          ))}
        </div>
      </div>
      {boardOriginId ? (
        <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#333333", marginBottom: 3 }}>Display link</div>
          <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.5, marginBottom: 8 }}>Open it full-screen on the lobby screen. The link is token-authorised — copying mints a fresh token.</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ flex: 1, minWidth: 200, fontSize: 11.5, padding: "8px 10px", border: "1px solid #E6E8EC", borderRadius: 8, background: "#FBFAFF", ...MONO, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{boardPublicUrl(boardOriginId)}</span>
            <button onClick={async () => {
              try {
                const minted: any = await mintBoardDisplayUrl(boardOriginId);
                const url = `${boardPublicUrl(boardOriginId)}${boardPublicUrl(boardOriginId).includes("?") ? "&" : "?"}t=${encodeURIComponent(minted.token)}`;
                showToast((await copyText(url)) ? "Display link copied" : url);
              } catch { showToast("Couldn't create a display link — publishing one needs the queues publish permission"); }
            }} style={GHOST}>Copy display link</button>
            <button onClick={async () => {
              try { const minted: any = await mintBoardDisplayUrl(boardOriginId); nav.go("queue.display", { b: boardOriginId, t: minted.token }); }
              catch { showToast("Couldn't open the display — publishing a link needs the queues publish permission"); }
            }} style={PRIMARY}>Open the display →</button>
          </div>
        </div>
      ) : (
        <div style={{ border: "1px dashed #B9A8F0", background: "#FCFBFF", borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#333333", marginBottom: 4 }}>Not in the boards library yet</div>
          <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.55, marginBottom: 10 }}>Saving it creates the board and its display link. Its configuration is already held on this journey, so nothing is lost either way.</div>
          <button disabled={!bReady || !!publishBusy || attachBusy} onClick={async () => {
            setPublishBusy("publish");
            try {
              /* Goes through the shared attach path so the journey is re-keyed
                 onto the board it just created — otherwise the board exists in
                 the library while its journey still reads as unattached. */
              const r = await runAttach();
              if (r) { const list: any = await listMessageBoards(); setBoards(list?.boards ?? list?.data ?? []); }
            } finally { setPublishBusy(null); }
          }} style={{ ...PRIMARY, opacity: !bReady || publishBusy || attachBusy ? 0.55 : 1 }}>{publishBusy ? "Saving…" : "Save to the boards library"}</button>
        </div>
      )}
      <div>
        {([
          ["Queues shown", b0.queueIds.map((id) => (queues as any[]).find((qq) => qq.id === id)?.name ?? id).join(", ") || "—"],
          ["Theme", b0.theme === "dark" ? "Dark" : "Light"],
          ["Content", b0.blocks.map((x) => BOARD_BLOCKS.find((d) => d.id === x.type)?.l ?? x.type).join(" · ") || "—"],
          ["Queuer display", `${({ full: "Full name", "first-initial": "First + initial", first: "First only", ticket: "Ticket only" } as any)[b0.nameFormat]}${b0.showWaitEstimates ? " · wait estimates on" : ""}`],
          ["Welcome message", b0.message || "—"],
        ] as [string, string][]).map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 12.5, borderBottom: "1px solid #E6E8EC", padding: "8px 0" }}>
            <span style={{ color: "#697180", flex: "none" }}>{k}</span><span style={{ fontWeight: 600, color: "#333333", textAlign: "right" }}>{v}</span>
          </div>
        ))}
      </div>
      {boardOriginId ? (
        <button onClick={async () => {
          if (!window.confirm("Delete this display board? The lobby link stops working immediately.")) return;
          try { await deleteBoardConfig(boardOriginId); const r: any = await listMessageBoards(); setBoards(r?.boards ?? r?.data ?? []); set({ route: "home" }); showToast("Board deleted"); }
          catch (e) { showToast(`Couldn't delete the board — ${e instanceof Error ? e.message : String(e)}`); }
        }} style={{ ...GHOST, color: "#C0392B", alignSelf: "flex-start" }}>Delete this board</button>
      ) : null}
    </div>
  );

  /** Board canvas — the lobby display itself, fed by the real queue config
   *  plus sample customers (never data: a preview can't call people forward). */
  const BOARD_SAMPLE = ["Maria Gonzales", "Marcus Lee", "Priya Shah", "Jordan Ellis", "Sofia Marino", "Devon Clark", "Amara Diallo", "Theo Brandt"];
  const sampleBoardQueues = () => b0.queueIds.map((id, i) => {
    const qq = (queues as any[]).find((x) => x.id === id);
    if (!qq) return null;
    const pfx = qq.ticketPrefix || String(qq.name ?? "Q").charAt(0).toUpperCase();
    const person = (n: number, position: number) => ({ name: BOARD_SAMPLE[(i * 3 + n) % BOARD_SAMPLE.length]!, ticket: `${pfx}-${104 + i * 20 + n}`, position });
    return {
      id: qq.id, name: qq.name, hoursLabel: qHoursLabel(qq.hours),
      nowServing: person(0, 0), called: [person(1, 1), person(2, 2)],
      upNext: [person(3, 1), person(4, 2), person(5, 3)],
      services: ((qq.services ?? []).length ? qq.services : ["Walk-in"]).slice(0, 4).map((s: string, si: number) => ({ name: s, waiting: 1 + ((i + si) % 3) })),
      waiting: 4 + i * 2, estWaitMins: 12 + i * 4,
    };
  }).filter(Boolean) as any[];
  /** Orientation of the board canvas (2026-09-02, operator). A board is always
   *  full screen, so the canvas is a real SCREEN BOX at true pixel size —
   *  1280×720 landscape, 720×1280 portrait — laid out at that size and then
   *  scaled as a whole to fit the pane. Sizing the box to the pane instead (an
   *  aspect-ratio frame with 1fr rows) squeezed the queue cards below their
   *  natural height and every card's content overflowed across its neighbours;
   *  laying out at true size and scaling keeps type at the proportions the
   *  lobby screen actually renders.
   *
   *  The measuring hooks live at the top of the component with the other state,
   *  NOT here: this point is past the screen's early returns, and a hook behind
   *  a return is React error #310 on the first render that takes the short path. */
  const bLand = ui.boardOrientation === "landscape";
  const B_W = bLand ? 1280 : 720;
  const B_H = bLand ? 720 : 1280;
  /* Fit the whole screen box into the pane, with a LEGIBILITY FLOOR. Pure
     fit-both put the board at 32% in a short window, where the 17px card title
     renders at about 5px and the ticket lines go sub-pixel — complete but
     unreadable, which is not a preview. Below the floor the board fits its WIDTH
     instead and the pane scrolls, so the type stays legible and the rest of the
     screen is one scroll away rather than shrunk into illegibility. */
  const B_FLOOR = 0.5;
  const bFitBoth = boardFit.w > 0 && boardFit.h > 0 ? Math.min(boardFit.w / B_W, boardFit.h / B_H) : 1;
  const bFitsPane = bFitBoth >= B_FLOOR;
  const bScale = bFitsPane ? bFitBoth : (boardFit.w > 0 ? Math.max(B_FLOOR, boardFit.w / B_W) : 1);
  const bCols = bColCount;
  const bPageQueues = sampleBoardQueues().slice(bPager.page * bPager.perPage, bPager.page * bPager.perPage + bPager.perPage);
  /** The board screen itself at true pixel size, scaled as a whole. Shared by
   *  the canvas and the Preview overlay so the two cannot drift — Preview used
   *  to fall through to the page-card renderer, which a board has no content
   *  for, and showed an empty white card (operator, 2026-09-02). */
  const boardScreen = (scale: number) => (
    <div style={{ width: B_W * scale, height: B_H * scale, borderRadius: 14 * Math.min(1, scale), overflow: "hidden", boxShadow: "0 2px 14px rgba(20,19,31,0.14)", flex: "none" }}>
      <div style={{ width: B_W, height: B_H, transform: `scale(${scale})`, transformOrigin: "top left", background: bC.bg, color: bC.fg, fontFamily: bC.font, padding: bLand ? "30px 34px 24px" : "34px 26px 26px", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
        {/* Portrait stacks the masthead: at 9:16 the org name, board name and
            clock on one baseline crush each other, and the clock is the one
            thing a waiting customer looks for. */}
        <div style={{ display: "flex", alignItems: bLand ? "baseline" : "flex-start", flexDirection: bLand ? "row" : "column", gap: bLand ? 14 : 3, marginBottom: bLand ? 24 : 22, flexShrink: 0 }}>
          <span style={{ fontSize: bLand ? 26 : 30, fontWeight: 800, letterSpacing: -0.3 }}>{tenant?.name ?? "Your organisation"}</span>
          <span style={{ fontSize: bLand ? 15 : 17, color: bC.soft }}>{b0.name || "Untitled board"}</span>
          <span style={{ marginLeft: bLand ? "auto" : 0, marginTop: bLand ? 0 : 4, fontSize: bLand ? 15 : 20, fontWeight: bLand ? 400 : 700, color: bC.soft }}>{new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
        </div>
        {sampleBoardQueues().length ? (
          /* One PAGE of queues, sized from the real grid and real card heights
             (useBoardPager) so nothing is cropped, with the remainder cycling.
             `gridRef` measures the space available; `cardRef` on the first card
             measures what a card actually costs. Rows take their natural
             height — forcing them to share the space made cards overlap. */
          <div ref={bPager.gridRef} style={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: `repeat(${bCols}, minmax(0, 1fr))`, gridAutoRows: "min-content", alignContent: "start", gap: bLand ? 20 : 16, overflow: "hidden" }}>
            {bPageQueues.map((qq, i) => (
              <div key={qq.id} ref={i === 0 ? bPager.cardRef : undefined} style={{ display: "grid", minWidth: 0 }}>
                <BoardQueueCard q={qq} blocks={b0.blocks as BoardBlockRec[]} C={bC} dark={bDark} nameFormat={b0.nameFormat as BoardNameFormat} showWait={!!b0.showWaitEstimates} />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ margin: "auto", fontSize: 18, color: bC.soft, textAlign: "center", lineHeight: 1.6 }}>No queues picked yet.<br />Choose which queues this board shows in Setup · Configuration.</div>
        )}
        {b0.message ? <div style={{ marginTop: bLand ? 20 : 18, textAlign: "center", fontSize: bLand ? 17 : 19, color: bC.soft, flexShrink: 0 }}>{b0.message}</div> : null}
        {/* Page dots, on the screen itself — a waiting customer needs to know
            another page of queues is coming, not wonder where theirs went. */}
        {bPager.pages > 1 ? (
          <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 7, flexShrink: 0 }}>
            {Array.from({ length: bPager.pages }).map((_, i) => (
              <span key={i} style={{ width: 8, height: 8, borderRadius: 999, background: i === bPager.page ? bC.fg : bC.border }} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
  /* No advisory strip and no "Configure this board" button (operator,
     2026-09-02): the Setup · Configuration chip directly above is the same
     destination, and a full-width purple row explaining where configuration
     lives cost the canvas a band of height on every board, every visit. */
  const boardPageView = (
    <div style={{ flex: 1, minHeight: 0, overflow: "hidden", background: "#F1F2F6", padding: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div ref={boardFitRef} style={{ flex: 1, minHeight: 0, width: "100%", overflow: bFitsPane ? "hidden" : "auto", display: "flex", alignItems: bFitsPane ? "center" : "flex-start", justifyContent: "center" }}>
        {boardScreen(bScale)}
      </div>
      <div style={{ textAlign: "center", fontSize: 10.5, color: "#B5BAC6", flexShrink: 0, ...MONO }}>{bLand ? `16:9 · ${B_W}×${B_H} — a wall-mounted screen` : `9:16 · ${B_W}×${B_H} — a screen turned on its side`} · {Math.round(bScale * 100)}%{bFitsPane ? "" : " · scroll for the rest"} · {bCols === 1 ? "one column" : `${bCols} columns`}{bPager.pages > 1 ? ` · ${sampleBoardQueues().length} queues over ${bPager.pages} pages, cycling every 10s — page ${bPager.page + 1}` : ""} · sample customers with each queue's real services and hours</div>
    </div>
  );

  const setupPanel = ui.setupView ? (
    <div style={{ flex: 1, overflow: "auto", padding: "28px 32px", background: "#F1F2F6" }}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>{SETUP_LABEL[ui.setupView]}</div>
      {/* No "where does this save" subtitle (operator, 2026-09-02). On a journey
          whose record does not exist yet it read "Held on this journey until you
          create the queue", which told an author nothing they could act on and
          repeated itself on every step of every setup. */}
      {/* No "nothing on this step is required" band either: a white box whose
          only content is the absence of requirements is a line to read on the
          way to the fields, every visit. */}
      {/* Journey-level navigation defaults. On the type's FIRST setup tab,
          because that is where the journey's own identity is set; the commit
          page can override the label, nothing overrides the switch. */}
      {ui.setupView === "basics" ? setupBasics : ui.setupView === "details" ? setupDetails
        : ui.setupView === "schedule" ? setupSchedule : ui.setupView === "tickets" ? setupTickets
        : ui.setupView === "guests" ? setupGuests : ui.setupView === "messaging" ? setupMessaging
        : ui.setupView === "qsetup" ? setupQSetup : ui.setupView === "qhours" ? setupQHours
        : ui.setupView === "qstaffing" ? setupQStaffing : ui.setupView === "qresources" ? setupQResources
        : ui.setupView === "qservices" ? setupQServices
        : ui.setupView === "registration" ? setupRegistration
        : ui.setupView === "boardcfg" ? setupBoardCfg
        : ui.setupView === "onestopcfg" ? setupOneStopCfg
        : isApptJourney ? setupApptPanel
        : isBoardJourney ? setupBoardPublish : isQueueJourney ? setupQPublish : setupPublish}
      {/* BELOW the tab's own fields (operator, 2026-09-02). It used to sit above
          them, so Queue Setup opened on a panel about button wording with the
          required Queue name and Location beneath it — the tab's least-used
          control in front of its most-used ones.
          It stays on this tab rather than moving to the Flow canvas: the wording
          is a journey-wide DEFAULT, and the flow edits one step at a time. The
          per-page override lives in the page inspector, where the button is
          visible while you type. */}
      {D && hasFlow && (ui.setupView === "basics" || ui.setupView === "qsetup" || ui.setupView === "aservices") ? (
        /* No card around these (operator, 2026-09-02): they are setup fields
           like the ones above them and read as one column, not as a panel
           bolted onto the end of the step. */
        <div style={{ maxWidth: 520, marginTop: 22 }}>
          <div style={{ ...setupFieldLabel, marginBottom: 3 }}>Moving through the journey</div>
          <div style={{ fontSize: 11.5, color: "#697180", marginBottom: 12, lineHeight: 1.5 }}>Every page carries Back and Next, derived from its place in the flow. The page that creates the booking is the exception — its button says what it does.</div>
          <div style={{ ...setupFieldLabel, marginBottom: 4 }}>Booking button label</div>
          <input value={String(D.commitLabel ?? "")} placeholder={COMMIT_LABEL_BY_TYPE[D.type ?? ""] ?? "Confirm"} onChange={(e) => patchDoc((d) => ({ ...d, commitLabel: e.target.value }))} style={{ ...INPUT, fontSize: 12, maxWidth: 260 }} />
          <div style={{ fontSize: 11, color: "#B5BAC6", marginTop: 4 }}>Default for this journey. A page can override it in its own editor.</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, fontSize: 12, marginTop: 18 }}>
            <span style={{ minWidth: 0 }}>
              <span style={setupFieldLabel}>Advance automatically when something is picked</span>
              <span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 2, lineHeight: 1.5 }}>Off by default — Next is the way forward, and a page that jumps on its own takes the choice back.</span>
            </span>
            <Switch on={D.autoAdvance === true} onClick={() => patchDoc((d) => ({ ...d, autoAdvance: !(d.autoAdvance === true) }))} />
          </div>
        </div>
      ) : null}
    </div>
  ) : null;

  /** SETUP row — one tab set per journey type. Always visible for a journey
   *  that owns configuration (events, queues, display boards). */
  const setupTabs: { k: string; label: string; dot: boolean | null; go?: () => void; active?: boolean }[] =
    isBoardJourney
      ? [{ k: "boardcfg", label: "Configuration", dot: bReady }]
      : isOneStopJourney
        ? [{ k: "onestopcfg", label: "Configuration", dot: osReady }]
      : isQueueJourney
        ? [
            /* Two checks now, not three — the seeded-pages assertion retired with
             the starter picker (2026-09-02). */
          { k: "qsetup", label: "Setup", dot: qChecks[0]!.done && qChecks[1]!.done },
            { k: "qhours", label: "Opening hours", dot: qChecks[2]!.done },
            { k: "qstaffing", label: "Staffing", dot: null },
            { k: "qresources", label: "Resources", dot: null },
            /* Services carries a real readiness row (queueReadiness's fifth
               check) but had no tab — reachable only by clicking the row on
               Publish, so its red dot was invisible until then. */
            { k: "qservices", label: "Services", dot: qChecks[3]!.done },
            ]
        : isEventJourney
          ? [
              { k: "basics", label: "Basics", dot: setupDone.basics },
              { k: "details", label: "Details", dot: setupDone.details },
              { k: "schedule", label: "Schedule", dot: setupDone.schedule },
              { k: "tickets", label: "Tickets", dot: setupDone.tickets },
              { k: "registration", label: "Registration", dot: null },
              { k: "guests", label: "Guests", dot: null },
              /* No Messaging step (operator, 2026-09-02): an event's messages
                 are set up in the flow now, and a second place to configure
                 them is a second answer to the same question. */
            ]
          : isApptJourney
            ? [
                { k: "aservices", label: "Services", dot: aChecks[0]!.done },
                { k: "aplaces", label: "Places & methods", dot: aChecks[1]!.done },
                { k: "awindow", label: "Booking window", dot: null },
                { k: "aperservice", label: "Per-service settings", dot: null },
                { k: "adetails", label: "Details & consent", dot: aChecks[2]!.done },
                { k: "amessaging", label: "Messaging", dot: null },
              ]
            : [];
  const publishReady = isApptJourney ? aReady : isBoardJourney ? bReady : isQueueJourney ? qReady : allReady;
  const liveNow = isBoardJourney ? !!boardOriginId : isQueueJourney ? q0.status === "open" : isLive;
  const setupStrip = setupTabs.length ? (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#FFFFFF", borderBottom: "1px solid #E6E8EC", overflowX: "auto", flexShrink: 0 }}>
      <span style={{ ...SEC, marginBottom: 0, marginRight: 4, flex: "none" }}>Setup</span>
      {setupTabs.map((tb) => {
        const active = tb.active ?? ui.setupView === tb.k;
        return (
          <button key={tb.k} onClick={() => (tb.go ? tb.go() : set({ setupView: tb.k as any }))} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 11px", borderRadius: 999, border: `1px solid ${active ? "#5142C7" : "#E6E8EC"}`, background: active ? "#F6F2FE" : "#FFFFFF", cursor: "pointer", flex: "none" }}>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: active ? "#5142C7" : "#333333", whiteSpace: "nowrap" }}>{tb.label}</span>
            {tb.dot !== null ? <span style={{ width: 6, height: 6, borderRadius: 999, background: tb.dot ? "#1F8A5B" : "#C0392B", flex: "none" }} /> : null}
          </button>
        );
      })}
      {/* No "Back to pages" — a page pill IS the way back, it sits one row below,
          and it says which page it goes to. A second control meaning only "not
          this" was noise (operator, 2026-09-02). */}
      <span style={{ flex: 1 }} />
      {liveNow ? <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", background: "#E4F5EF", color: "#1F8A5B", borderRadius: 999, padding: "3px 10px", flex: "none" }}>LIVE</span> : null}
      {/* Flush before Publish (2026-09-02). Publish reads `q0`/`r0`, which are
          live, so the button itself is safe — but a pending write must land
          before the record is created from those values, or the record is made
          from the document's older copy. */}
      {/* STICKY to the strip's right edge (2026-09-03). This strip scrolls, and
          on an appointment journey its seven tabs plus the note came to ~1167px
          in a 708px bar — so Publish sat 459px off the end and an author had to
          discover a horizontal scroll to reach it. The tabs may scroll; the verb
          that ships the thing may not. */}
      <button onClick={() => { flushSetup(); set({ setupView: "publish" }); }} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 13px", borderRadius: 999, border: `1px solid ${ui.setupView === "publish" ? "#5142C7" : "#D1D4DC"}`, background: ui.setupView === "publish" ? "#F6F2FE" : "#FFFFFF", cursor: "pointer", flex: "none", position: "sticky", right: 0, zIndex: 2, boxShadow: "-8px 0 10px -6px rgba(255,255,255,1), -14px 0 12px -10px rgba(20,19,31,0.10)" }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: ui.setupView === "publish" ? "#5142C7" : "#333333" }}>Publish</span>
        {!publishReady ? <span style={{ width: 6, height: 6, borderRadius: 999, background: "#C0392B", flex: "none" }} /> : null}
      </button>
      {/* The aside is the first thing to go when the strip is tight — it explains
          the strip, and the strip's own "Setup" label already does that. */}
      <span style={{ fontSize: 10.5, color: "#B5BAC6", flex: "0 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...MONO }}>{isApptJourney ? "funnel + catalogue settings, not page content" : isBoardJourney ? "board config, not page content" : isQueueJourney ? "queue data, not page content" : "event data, not page content"}</span>
    </div>
  ) : null;

  const attachNoun = ATTACH_NOUN[D?.type ?? ""] ?? "record";
  /* A quiet status line, not an action bar (operator, 2026-09-02).
     It used to carry a "Create the queue" button and copy about values being
     "held on the document" — which read as a warning that saving needed a second
     step. It never did: the journey autosaves, and the strip was about the
     RECORD, which is a different thing and a later one.
     Publish now creates the record itself, so there is no step to prompt for.
     What remains is worth one sentence, because it is not visible anywhere else:
     the queue does not exist for staff or customers yet. */
  const attachStrip = D && needsAttach(D) ? (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 16px", background: "#FBFBFC", borderBottom: "1px solid #EEF0F3", flexShrink: 0 }}>
      <span style={{ fontSize: 11, color: "#697180", flex: 1, lineHeight: 1.5 }}>
        Draft — saved as you work. Publishing creates the {attachNoun} and puts it in front of {attachNoun === "event" ? "attendees" : "customers"}.
      </span>
    </div>
  ) : null;

  const designer = D && p ? (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* overflowX auto is the FLOOR, not the layout: the title block below
          shrinks first and hands its width back, but if a long journey name and
          every control still exceed the bar, the actions must remain reachable.
          They were clipped away entirely at a 924px window with the shell nav
          expanded — Undo, Redo, Preview and Publish all sat past the right edge
          with `overflow: hidden` and no way to scroll to them. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderBottom: "1px solid #E6E8EC", background: "#FFFFFF", flexShrink: 0, overflowX: "auto" }}>
        <span onClick={() => { flushSetup(); set(ui.fromExp
          ? { route: "experience", curExp: ui.fromExp, fromExp: "", addMenu: null }
          : { route: "home", addMenu: null }); }} title={ui.fromExp ? "Back to the experience" : "Back to journeys"} style={{ width: HDR_H, height: HDR_H, borderRadius: 8, border: "1px solid #D1D4DC", background: "#FFFFFF", color: "#697180", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, cursor: "pointer", flex: "none" }}>←</span>
        <span style={glyphBox(p.tint, p.color, 32, 15)}>▤</span>
        {/* Shrinks FIRST and ellipsises. It carried `flex: "none"` with a
            `minWidth: 0` that could therefore never engage, so the bar's
            trailing actions were pushed past the clip instead of the name being
            truncated — which is the wrong thing to protect: a journey's name is
            already in the list you came from, Publish is not. */}
        <div style={{ minWidth: 60, flex: "1 1 auto", overflow: "hidden" }}>
          <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Journey Designer</div>
          <div style={{ fontSize: 11, color: "#697180", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{docDisplayName}</div>
        </div>
        {/* Pages / Flow — only for journey types that HAVE a flow. A Display
            Board and a One-Stop page are one page each: nobody walks through
            them, they arrive and choose. Offering a Flow tab there is a toggle
            between a page and an empty canvas, which reads as something missing
            rather than something absent by design. */}
        {hasFlow ? (
          <div style={{ display: "inline-flex", border: "1px solid #D1D4DC", borderRadius: 8, overflow: "hidden", marginLeft: 8, flex: "none" }}>
            {[{ k: "pages", label: "Pages" }, { k: "flow", label: "Flow" }].map((t) => (
              <button key={t.k} onClick={() => { flushSetup(); set({ view: t.k as any, addMenu: null, sel: null, setupView: null }); }} style={{ height: HDR_H, padding: "0 14px", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", background: ui.view === t.k && !ui.setupView ? "#5142C7" : "#FFFFFF", color: ui.view === t.k && !ui.setupView ? "#FFFFFF" : "#333333", fontFamily: FONT }}>{t.label}</button>
            ))}
          </div>
        ) : null}
        {/* Desktop / Mobile — not for a Display Board. That canvas is a lobby TV:
            it has one size, it is never held in a hand, and offering a phone
            width invites an author to check a layout that will never exist
            (operator, 2026-09-02). */}
        {ui.view === "pages" && !ui.setupView && !isBoardJourney ? (
          <div style={{ display: "inline-flex", border: "1px solid #D1D4DC", borderRadius: 8, overflow: "hidden", marginLeft: 8, flex: "none" }}>
            {[{ k: "desktop", label: "Desktop" }, { k: "mobile", label: "Mobile" }].map((t) => (
              <button key={t.k} onClick={() => setViewport(t.k as "desktop" | "mobile")} style={{ height: HDR_H, padding: "0 13px", border: "none", fontSize: 11.5, fontWeight: 700, cursor: "pointer", background: curViewport === t.k ? "#14131F" : "#FFFFFF", color: curViewport === t.k ? "#FFFFFF" : "#333333", fontFamily: FONT }}>{t.label}</button>
            ))}
          </div>
        ) : null}
        {/* Landscape / Portrait — the Display Board's equivalent, and the only
            width question a lobby TV has (operator, 2026-09-02). Phone width was
            never a layout a board would be seen at; how the screen is BOLTED TO
            THE WALL is. */}
        {ui.view === "pages" && !ui.setupView && isBoardJourney ? (
          <div style={{ display: "inline-flex", border: "1px solid #D1D4DC", borderRadius: 8, overflow: "hidden", marginLeft: 8, flex: "none" }}>
            {[{ k: "landscape", label: "Landscape" }, { k: "portrait", label: "Portrait" }].map((t) => (
              <button key={t.k} onClick={() => set({ boardOrientation: t.k as "landscape" | "portrait" })} title={t.k === "landscape" ? "A TV on a wall bracket — 16:9" : "A TV turned on its side — 9:16"} style={{ height: HDR_H, padding: "0 13px", border: "none", fontSize: 11.5, fontWeight: 700, cursor: "pointer", background: ui.boardOrientation === t.k ? "#14131F" : "#FFFFFF", color: ui.boardOrientation === t.k ? "#FFFFFF" : "#333333", fontFamily: FONT }}>{t.label}</button>
            ))}
          </div>
        ) : null}
        {/* The ‹ Page 1 of 5 › pager is GONE (2026-09-03). It duplicated the
            Pages strip immediately below, which lists every page as a named chip
            — so it answered "which page am I on" a second time, worse, while
            costing ~200px of a 708px bar. With it there the bar overflowed by
            459px and Undo, Redo, Preview and Publish were all clipped past the
            right edge with no way to reach them. A pager that hides Publish is
            not worth the pager. The flow view keeps its summary line, which has
            no equivalent below and shrinks. */}
        {ui.view === "flow" ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minWidth: 0 }}>
            {(() => {
              const all = qfFlatten(qfFlow.steps);
              const pgCount = all.filter((st) => st.kind === "page").length;
              const rest = all.length - pgCount;
              return <span style={{ fontSize: 11.5, color: "#697180", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Trigger → {pgCount} pages{rest > 0 ? ` + ${rest} more steps` : ""} · click a step to edit it</span>;
            })()}
          </div>
        ) : <span style={{ flex: 1, minWidth: 0 }} />}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flex: "none" }}>
          <SavedBadge state="saved" />
          <EditorHistoryButtons history={history} current={D} onRestore={restoreDoc} size="sm"
            hint="Undo covers pages, blocks and the flow — Setup writes to the record and isn't undoable here."
            testidPrefix="nj" />
        </span>
        <button onClick={() => set({ previewOpen: true, sel: null, previewViewport: curViewport })} style={{ ...GHOST, height: HDR_H, flex: "none" }}>Preview</button>
        {/* The way out is the boxed ← at the head of this bar (2026-09-02,
            operator): the same back affordance as the Confirmation Page
            Designer. It was a filled primary button on the right, which read as
            the header's main action when it is the opposite — leaving. Saving is
            automatic and the SavedBadge above states it permanently. */}
      </div>
      {attachStrip}
      {setupStrip}
      {pagesStrip}
      {ui.setupView ? setupPanel : (ui.view === "pages" ? (p.id === "board" ? boardPageView : pagesView) : flowView)}
    </div>
  ) : null;

  /* ── EXPERIENCE ─────────────────────────────────────────────────── */
  const anchor = E?.anchor ? expJourney(E.anchor) : null;
  const experience = E ? (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderBottom: "1px solid #E6E8EC", background: "#FFFFFF", flexShrink: 0 }}>
        <span style={glyphBox("#E4E1F7", "#262265", 32, 15)}>◈</span>
        <div style={{ minWidth: 0, flex: "none" }}>
          <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap" }}>Experience Designer</div>
          <input value={E.name} onChange={(e) => patchExp((x) => ({ ...x, name: e.target.value }))} style={{ border: "none", outline: "none", fontSize: 11, color: "#697180", fontFamily: FONT, padding: 0, background: "transparent", width: 320 }} />
        </div>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", background: "#E4E1F7", color: "#262265", borderRadius: 999, padding: "3px 10px", flex: "none" }}>EXPERIENCE · PHASE 5</span>
        <span style={{ flex: 1 }} />
        {/* Same correction as the designer header (2026-09-02): Close and Done
            ran identical code and only Done claimed a save, so the pair implied
            a choice that did not exist. One way out; the experience's own edits
            autosave like everything else. */}
        <button onClick={() => set({ route: "home" })} style={PRIMARY}>Back to journeys</button>
      </div>
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <div style={{ flex: 1, background: "#F1F2F6", overflow: "auto", padding: "26px 28px", minWidth: 0 }}>
          <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={SEC}>Anchor — the event itself</div>
            {anchor ? (
              <div onClick={() => openDoc(E.anchor!, ui.curExp)} style={{ width: 460, background: "#FFFFFF", border: "2px solid #262265", borderRadius: 12, padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
                <span style={glyphBox(anchor.tint, anchor.color, 34, 15)}>◆</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 700 }}>{anchor.name}</span>
                  <span style={{ display: "block", fontSize: 11, color: "#697180", marginTop: 2 }}>{anchor.pages} pages · registration here checks the attendee in to everything below</span>
                </span>
                <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: anchor.tint, color: anchor.color, flex: "none" }}>{anchor.ty}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#5142C7", flex: "none" }}>Open →</span>
              </div>
            ) : (
              <button onClick={() => set({ expPicker: "anchor" })} style={{ width: 460, padding: 16, border: "1px dashed #B9A8F0", borderRadius: 12, background: "#FCFBFF", color: "#5142C7", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>+ Pick the anchor journey — the event this experience wraps</button>
            )}
            <span style={{ width: 2, height: 18, background: "#C7CBD6" }} />
            <div style={{ width: 560, background: "#FFFFFF", border: "2px solid #262265", borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 4 }}>
                <span style={glyphBox("#E4E1F7", "#262265", 28, 13)}>◈</span>
                <span style={{ flex: 1, minWidth: 0 }}><span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>Experience hub — after check-in</span><span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>the attendee's home screen while they're here</span></span>
                <button onClick={() => set({ expPicker: "attach" })} style={{ height: 28, padding: "0 12px", border: "1px dashed #B9A8F0", borderRadius: 999, background: "#FCFBFF", color: "#5142C7", fontSize: 11.5, fontWeight: 700, cursor: "pointer", flex: "none", fontFamily: FONT }}>+ Add to hub</button>
              </div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#B5BAC6", margin: "10px 0 7px" }}>What the customer can do here</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {E.attached.map((a, i) => {
                  const j = expJourney(a.doc);
                  return (
                    <div key={a.doc + i} onClick={() => (exps[a.doc] ? set({ route: "experience", curExp: a.doc, expPicker: null, fromExp: "" }) : openDoc(a.doc, ui.curExp))} style={{ display: "flex", alignItems: "flex-start", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "10px 11px", cursor: "pointer" }}>
                      <span style={glyphBox(j.tint, j.color, 28, 13)}>{TYPE_GLYPH[j.ty] ?? "▤"}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                          <span style={{ fontSize: 12.5, fontWeight: 700 }}>{HUB_VERB[j.ty] ?? "Open — "}{j.name}</span>
                          <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: j.tint, color: j.color }}>{j.ty}</span>
                          <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: "0.05em", background: "#262265", color: "#FFFFFF", borderRadius: 3, padding: "1px 5px" }}>REFERENCE</span>
                        </span>
                        <span style={{ display: "block", fontSize: 10.5, color: "#697180", lineHeight: 1.5, marginTop: 3 }}>{a.scope} · {j.pages} pages</span>
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 9, flex: "none", paddingTop: 2 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#5142C7" }}>Open →</span>
                        <button onClick={(e) => { e.stopPropagation(); patchExp((x) => ({ ...x, attached: x.attached.filter((_, n) => n !== i) })); showToast("Detached — the journey itself is untouched (it was a reference)."); }} style={{ border: "none", background: "transparent", color: "#697180", fontSize: 11, cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
                      </span>
                    </div>
                  );
                })}
                {E.attached.length === 0 ? <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "18px 12px", textAlign: "center", fontSize: 11.5, color: "#697180" }}>Nothing in the hub yet — add the queues, appointments, events or experiences that run inside this one.</div> : null}
              </div>
              <div style={{ fontSize: 10, color: "#B5BAC6", marginTop: 10, ...MONO }}>one page in the anchor's flow — page type ◈ Experience hub, from the registry</div>
            </div>
          </div>
        </div>
        <div style={{ width: 264, background: "#FFFFFF", borderLeft: "1px solid #E6E8EC", padding: 14, overflow: "auto", flexShrink: 0 }}>
          <div style={SEC}>Shared context</div>
          {[["One registration.", "Registering for the anchor event satisfies Contact details in every attached journey — the invariant is met by the experience, not re-asked."],
            ["Scoped availability.", "Attached journeys run only inside the anchor's time window and location — the queue closes when the event does."],
            ["One identity.", "The check-in QR is the same identity everywhere — joining a line or booking a 1:1 needs no second sign-in."]].map(([b, t]) => (
            <div key={b} style={{ fontSize: 11.5, lineHeight: 1.55, color: "#333333", marginBottom: 10 }}><b>{b}</b> {t}</div>
          ))}
          <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "4px 0 12px" }} />
          <div style={{ ...NOTE, fontSize: 10.5 }}>Edits to a journey show here automatically. Detaching never deletes it.</div>
          <div style={{ fontSize: 10, color: "#B5BAC6", marginTop: 10, ...MONO }}>experience = anchor + attached[] + hub · phase 5</div>
        </div>
      </div>
    </div>
  ) : null;

  /* ── Modals ─────────────────────────────────────────────────────── */
  const eat = (e: any) => e.stopPropagation();
  const scopeFor = (ty: string) => ({ Queue: "Runs only during event hours", Appointment: "Slots only inside the event window", Event: "Runs inside this experience", Experience: "Nested experience — its own hub inside this one" } as any)[ty] ?? "Available while checked in";
  const pickerRows = E ? [
    ...docKeys.filter((k) => k !== E.anchor && !E.attached.some((a) => a.doc === k)).map((k) => { const j = expJourney(k); return { ...j, sub: docs[k]!.sub, pagesLabel: `${j.pages} pages` }; }),
    ...Object.keys(exps).filter((k) => k !== ui.curExp && !E.attached.some((a) => a.doc === k)).map((k) => { const x = exps[k]!; return { key: k, name: x.name, ty: "Experience", tint: TYPE_TINT.Experience![0], color: TYPE_TINT.Experience![1], sub: `${x.attached.length} journeys inside · one check-in`, pages: x.attached.length, pagesLabel: `${x.attached.length} journeys` }; }),
  ].filter((r) => ui.expPickerTab === "all" || r.ty === ui.expPickerTab) : [];

  /* ── Preview — the pages with the authoring chrome taken away ──────
     The same render the canvas uses (hero, heading, core, blocks), inside a
     scrim, with pointer events off so nothing is selectable and no inspector
     is reachable. Its own device toggle and page stepper.

     Closing it (2026-08-31): the overlay used to paint IN PLACE, inside the
     screen's own stacking context, at z-index 40. The app topbar and rail sit in
     a sibling context above that, so they covered the preview header — the Close
     button was under the chrome and unclickable, and with no scrim click and the
     focus outside the document, an operator had no way out. It now mounts at
     document.body above the chrome, and there are three ways to leave: the Close
     button, a click on the scrim, and Esc. */
  const closePreview = () => set({ previewOpen: false });
  const previewOverlay = ui.previewOpen && D && p ? (
    <OverlayPortal>
      <div role="dialog" aria-modal="true" aria-label={`Preview — ${D.name}`} onClick={closePreview} style={{ position: "fixed", inset: 0, zIndex: 260, background: "rgba(20,19,31,0.72)", display: "flex", flexDirection: "column", padding: "16px clamp(10px, 4vw, 48px) 20px", fontFamily: FONT }}>
      <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", flexShrink: 0, marginBottom: 12 }}>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", background: "rgba(255,255,255,0.16)", color: "#FFFFFF", borderRadius: 999, padding: "3px 10px" }}>PREVIEW</span>
        <span style={{ color: "#FFFFFF", fontSize: 13.5, fontWeight: 700, minWidth: 0 }}>{D.name}</span>
        <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 11.5 }}>{p.label} · page {curIdx + 1} of {D.pages.length}</span>
        <span style={{ flex: 1 }} />
        <button onClick={() => set({ cur: Math.max(0, curIdx - 1) })} disabled={curIdx === 0} style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.12)", color: "#FFFFFF", cursor: curIdx === 0 ? "default" : "pointer", opacity: curIdx === 0 ? 0.4 : 1 }}>‹</button>
        <button onClick={() => set({ cur: Math.min(D.pages.length - 1, curIdx + 1) })} disabled={curIdx >= D.pages.length - 1} style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.12)", color: "#FFFFFF", cursor: curIdx >= D.pages.length - 1 ? "default" : "pointer", opacity: curIdx >= D.pages.length - 1 ? 0.4 : 1 }}>›</button>
        <span style={{ display: "inline-flex", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, overflow: "hidden" }}>
          {/* A Display Board's only width question is how the TV is mounted, so
              the toggle reads Landscape / Portrait there and Desktop / Mobile
              everywhere else (operator, 2026-09-02). */}
          {(isBoardJourney
            ? [{ k: "landscape", label: "Landscape" }, { k: "portrait", label: "Portrait" }]
            : [{ k: "desktop", label: "Desktop" }, { k: "mobile", label: "Mobile" }]
          ).map((t) => {
            const on = isBoardJourney ? ui.boardOrientation === t.k : ui.previewViewport === t.k;
            /* Preview-scoped (2026-09-02, operator). This briefly wrote
               `page.viewport`, which made a look at the phone layout an edit to
               the page: the canvas behind the scrim changed width, and the
               change persisted after the preview closed. It now writes only the
               preview's own viewport, seeded from the page on open — or, for a
               board, the designer-local orientation. */
            return <button key={t.k} onClick={() => set(isBoardJourney ? { boardOrientation: t.k as "landscape" | "portrait" } : { previewViewport: t.k as "desktop" | "mobile" })} style={{ padding: "6px 13px", border: "none", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT, background: on ? "#FFFFFF" : "transparent", color: on ? "#14131F" : "#FFFFFF" }}>{t.label}</button>;
          })}
        </span>
        <button onClick={closePreview} aria-label="Close preview" style={{ height: 30, padding: "0 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.12)", color: "#FFFFFF", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Close preview</button>
      </div>
      <div onClick={closePreview} style={{ flex: 1, minHeight: 0, overflow: isBoardJourney ? "hidden" : "auto" }}>
        {/* A Display Board previews as the SCREEN, not as a page card: the same
            true-size box the canvas draws, scaled to the scrim. Before this it
            fell through to the page renderer, which a board has no blocks for,
            and Preview showed an empty white card (operator, 2026-09-02). */}
        {isBoardJourney ? (
          <div ref={previewFitRef} onClick={(e) => e.stopPropagation()} style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" as const }}>
            {boardScreen(previewFit.w > 0 && previewFit.h > 0 ? Math.min(previewFit.w / B_W, previewFit.h / B_H) : 1)}
          </div>
        ) : (
        <>
        {/* The page card takes no pointer events (nothing in a preview is
            interactive), so a click ON it would otherwise reach the scrim and
            close the preview. This wrapper absorbs those clicks; only the scrim
            around the page closes. */}
        <div onClick={(e) => e.stopPropagation()} style={{ width: "fit-content", maxWidth: "100%", margin: "0 auto" }}>
        {/* Desktop is a real width, not "100%" (2026-09-02, operator). `100%` of
            a `fit-content` parent resolves against the parent's own shrink-to-fit
            width, so Desktop collapsed to roughly the mobile card — the toggle
            read Desktop while the preview showed the narrow experience. 1280 is
            the design system's desktop preview width; `maxWidth: 100%` keeps it
            inside the scrim on a small display. */}
        <div className="nj-page" style={{ width: ui.previewViewport === "desktop" ? 1280 : 440, maxWidth: "100%", margin: "0 auto", background: "#FFFFFF", borderRadius: 16, overflow: "hidden", boxShadow: "0 12px 40px rgba(0,0,0,0.35)", pointerEvents: "none" as const }}>
          {heroBlock ? (
            <div className="nj-block nj-hero" style={{ margin: "14px 14px 12px" }}>
              <div style={{ borderRadius: 10, background: heroImg ? `linear-gradient(rgba(20,19,31,0.45), rgba(20,19,31,0.45)), url(${heroImg}) center/cover` : (({ Gradient: "linear-gradient(135deg, #75ADFA, #5142C7)", "Solid brand": "#5142C7", Photo: "#1F2430" } as any)[bval(heroBlock, "bg")] ?? "linear-gradient(135deg, #75ADFA, #5142C7)"), color: "#FFFFFF", padding: "22px 18px", textAlign: (bval(heroBlock, "align") === "Left" ? "left" : "center") as any }}>
                <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.01em" }}>{heroBlock.text}</div>
                {bval(heroBlock, "sub") ? <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 5, lineHeight: 1.45 }}>{bval(heroBlock, "sub")}</div> : null}
                {bval(heroBlock, "cta") ? <span style={{ display: "inline-block", marginTop: 10, background: "#FFFFFF", color: "#5142C7", borderRadius: 999, padding: "6px 14px", fontSize: 11, fontWeight: 700 }}>{bval(heroBlock, "cta")}</span> : null}
              </div>
            </div>
          ) : null}
          <div className="nj-heading" style={{ padding: "18px 20px 6px" }}>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.01em" }}>{p.h}</div>
            {p.s ? <div style={{ fontSize: 12.5, color: "#697180", marginTop: 4 }}>{p.s}</div> : null}
          </div>
          {/* A One-Stop page's content is `osBlocks`, drawn by the student's own
              renderer — NOT the designer's copy blocks. Without this branch the
              preview rendered `core()` (null, the page has no core) and
              `contentBlocks` (empty, the blocks live elsewhere), so it showed a
              heading and nothing else. Same fault the canvas had on 2026-09-01,
              in the one place that had not been fixed. */}
          {isOneStopPage ? (
            /* The SAME component the canvas mounts, with authoring switched off
               — not a second copy of the renderer. A preview that renders the
               page through a different path is a preview that can disagree with
               the thing it previews, which is how the canvas and this overlay
               drifted in the first place. */
            <div style={{ padding: "0 14px 10px" }}>
              <OSCanvas
                blocks={osBlocks}
                rendered={osRendered}
                locale={p.osSettings?.locale ?? "en-US"}
                timeZone={p.osSettings?.timeZone ?? tzContext().zone}
                subtitle={p.s}
                readOnly
                sel={null}
                onSelect={() => {}}
                onChange={() => {}}
                onInsert={() => {}}
              />
            </div>
          ) : (<>
            {core()}
            {/* NOT `.map(blockView)`: map passes (item, index, ARRAY), and
                blockView's third parameter is `opts?: { inCell?: boolean }` — so
                every block was rendered with the whole array as its options bag
                (2026-09-08). Arity spelled out. */}
            {contentBlocks.map((c, i) => blockView(c, i))}
          </>)}
          <div style={{ height: 6 }} />
          {stepNavFooter()}
        </div>
        </div>
        <div style={{ textAlign: "center", fontSize: 10.5, color: "rgba(255,255,255,0.5)", marginTop: 10, ...MONO }}>preview renders this journey's own pages — no authoring chrome, nothing here is editable · Esc or click outside to close</div>
        </>
        )}
      </div>
      </div>
    </OverlayPortal>
  ) : null;

  const modals = (
    <>
      {previewOverlay}
      {ui.tplOpen ? (
        <div onClick={() => set({ tplOpen: false })} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={eat} style={{ width: 820, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, padding: "24px 26px", boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 4 }}>New journey</div>
            <div style={{ fontSize: 12.5, color: "#697180", marginBottom: 18 }}>Pick a starting template — every page and step is editable after.</div>
            {/* Four columns for the four v1 types. At six the cards were narrow
                enough to wrap a two-word name ("Queue journey") onto two lines. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              {templates.map((t) => (
                <div key={t.id} onClick={() => pickTemplate(t.id)} style={{ display: "flex", flexDirection: "column", border: "1px solid #DCCCFC", borderRadius: 10, padding: "14px 12px", cursor: "pointer", background: "#FCFBFF" }}>
                  <span style={glyphBox(t.tint, t.color, 30, 14)}>{t.glyph}</span>
                  <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 8 }}>{t.name}</div>
                  {/* flex: 1 keeps the badges on one baseline across cards whose
                      descriptions run to different line counts. */}
                  <div style={{ flex: 1, fontSize: 11, color: "#697180", lineHeight: 1.5, marginTop: 3 }}>{t.sub}</div>
                  <span style={{ alignSelf: "flex-start", marginTop: 10, fontSize: 9, fontWeight: 800, letterSpacing: "0.05em", background: "#F4F5F7", color: "#697180", borderRadius: 4, padding: "2px 7px" }}>{t.tagLabel}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {ui.typePicker ? (
        <div onClick={() => set({ typePicker: null })} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 25, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={eat} style={{ width: 620, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, padding: "24px 26px", boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 4 }}>Add a page</div>
            <div style={{ fontSize: 12.5, color: "#697180", marginBottom: 18 }}>Pick what this page is for. A journey can mix page types freely.</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, maxHeight: "58vh", overflow: "auto" }}>
              {/* A One-Stop page is NOT a page type you add to another journey
                  (operator, 2026-09-02). It is a whole journey type of its own,
                  minted by the One-Stop entry in the new-journey picker: it owns
                  the document's slug, its own block registry and its own publish
                  validator, and it is the ONLY page its journey has. Offered
                  here, it invited an author to drop it into an appointment,
                  queue or event flow, where none of that applies. A One-Stop
                  journey never reaches this picker anyway — it has no "+ Add
                  page" — so the type simply has no place in this list. */}
              {REG.filter((t) => t.id !== "onestop").map((t) => (
                <div key={t.id} onClick={() => addPage(t.id, ui.typePicker?.at ?? null)} style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: "13px 12px", cursor: "pointer" }}>
                  <span style={glyphBox(t.tint, t.color, 30, 14)}>{t.glyph}</span>
                  <div style={{ fontSize: 13, fontWeight: 700, marginTop: 8 }}>{t.label}</div>
                  <div style={{ fontSize: 10.5, color: "#697180", lineHeight: 1.5, marginTop: 3 }}>{t.desc}</div>
                  <div style={{ fontSize: 9.5, fontWeight: 700, color: "#B5BAC6", marginTop: 7, textTransform: "uppercase", letterSpacing: "0.05em" }}>Core: {t.core ?? "none — blocks only"}</div>
                  {t.phase ? <span style={{ display: "inline-block", marginTop: 6, fontSize: 8.5, fontWeight: 800, letterSpacing: "0.05em", background: "#E0F2F1", color: "#0AA396", borderRadius: 4, padding: "2px 7px", whiteSpace: "nowrap" }}>NEW · {t.phase}</span> : null}
                </div>
              ))}
            </div>
            <div style={{ ...NOTE, fontSize: 11, marginTop: 14 }}>All three verticals' page types are in the registry (phases 2–4) plus the experience hub (phase 5) — none of them touched the shell to land.</div>
          </div>
        </div>
      ) : null}

      {ui.expPicker ? (
        <div onClick={() => set({ expPicker: null })} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.35)", zIndex: 25, display: "flex", justifyContent: "flex-end" }}>
          <div onClick={eat} style={{ width: 420, maxWidth: "94vw", height: "100%", background: "#FFFFFF", boxShadow: "-12px 0 40px rgba(20,19,31,0.22)", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid #E6E8EC", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 15.5, fontWeight: 800, flex: 1 }}>{ui.expPicker === "anchor" ? "Pick the anchor journey" : "Add to the Experience hub"}</span>
                <button onClick={() => set({ expPicker: null })} style={{ width: 26, height: 26, border: "none", borderRadius: 6, background: "#F4F5F7", color: "#697180", fontSize: 12, cursor: "pointer" }}>✕</button>
              </div>
              <div style={{ fontSize: 12, color: "#697180", marginTop: 5, lineHeight: 1.5 }}>Anything on the journeys list can go in the hub — queues, appointments, events, and other experiences. It stays an independent document; the hub only references it.</div>
              <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                {[{ k: "all", label: "All" }, { k: "Queue", label: "Queues" }, { k: "Appointment", label: "Appointments" }, { k: "Event", label: "Events" }, { k: "Experience", label: "Experiences" }].map((tb) => {
                  const on = ui.expPickerTab === tb.k;
                  return <button key={tb.k} onClick={() => set({ expPickerTab: tb.k })} style={{ height: 26, padding: "0 11px", border: `1px solid ${on ? "#5142C7" : "#D1D4DC"}`, borderRadius: 999, background: on ? "#F6F2FE" : "#FFFFFF", color: on ? "#5142C7" : "#333333", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>{tb.label}</button>;
                })}
              </div>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: "14px 20px 20px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {pickerRows.map((r) => (
                  <div key={r.key} onClick={() => {
                    if (ui.expPicker === "anchor") { patchExp((y) => ({ ...y, anchor: r.key })); showToast("Anchor set — registration there checks attendees in to the whole experience."); }
                    else { patchExp((y) => ({ ...y, attached: [...y.attached, { doc: r.key, scope: scopeFor(r.ty) }] })); showToast("Added to the hub — edits to it show here automatically."); }
                    set({ expPicker: null });
                  }} style={{ display: "flex", alignItems: "flex-start", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "11px 12px", cursor: "pointer" }}>
                    <span style={glyphBox(r.tint, r.color, 28, 13)}>{TYPE_GLYPH[r.ty] ?? "▤"}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{r.name}</span>
                      <span style={{ display: "block", fontSize: 10.5, color: "#697180", marginTop: 2, lineHeight: 1.5 }}>{r.sub}</span>
                    </span>
                    <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flex: "none" }}>
                      <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: r.tint, color: r.color }}>{r.ty}</span>
                      <span style={{ fontSize: 10.5, color: "#B5BAC6" }}>{r.pagesLabel}</span>
                    </span>
                  </div>
                ))}
                {pickerRows.length === 0 ? <div style={{ border: "1px dashed #D1D4DC", borderRadius: 10, padding: "22px 12px", textAlign: "center", fontSize: 11.5, color: "#697180" }}>Everything of this type is already in the hub.</div> : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {ui.renameKey ? (
        <div onClick={() => set({ renameKey: "" })} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 34, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={eat} style={{ width: 380, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, padding: 20, display: "flex", flexDirection: "column", gap: 12, boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
            <div style={{ fontSize: 15.5, fontWeight: 800 }}>Rename journey</div>
            <input autoFocus value={ui.renameVal} onChange={(e) => set({ renameVal: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") renameRow(ui.renameKey, ui.renameVal); if (e.key === "Escape") set({ renameKey: "" }); }} style={INPUT} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => set({ renameKey: "" })} style={GHOST}>Cancel</button>
              <button onClick={() => renameRow(ui.renameKey, ui.renameVal)} style={PRIMARY}>Save</button>
            </div>
          </div>
        </div>
      ) : null}

      

      {msgTargets ? (
        <div onClick={() => setMsgTargets(null)} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={eat} style={{ width: 440, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, padding: 20, display: "flex", flexDirection: "column", gap: 12, boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
            <div>
              <div style={{ fontSize: 15.5, fontWeight: 800 }}>{msgTargets.length === 1 ? `Message ${msgTargets[0]!.name}` : `Message everyone in the line (${msgTargets.length})`}</div>
              <div style={{ fontSize: 11.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>{msgTargets.length === 1 ? `Sent to their chosen channel${msgTargets[0]!.ticket ? ` — ticket ${msgTargets[0]!.ticket}` : ""}.` : "One message to every waiting, called and serving customer, on each of their chosen channels."}</div>
            </div>
            <textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} rows={3} autoFocus placeholder="We're running about 10 minutes behind — thanks for waiting." style={{ ...INPUT, resize: "vertical" as const, minHeight: 70 }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setMsgTargets(null)} style={GHOST}>Cancel</button>
              <button disabled={msgSending || !msgText.trim()} onClick={async () => {
                if (!queueOriginId) return;
                setMsgSending(true);
                try {
                  if (msgTargets.length > 1 && msgTargets.length === qActive.length) await broadcastQueueMessage(queueOriginId, msgText.trim());
                  else for (const e of msgTargets) await sendQueueMessage(queueOriginId, e.id, msgText.trim());
                  showToast(msgTargets.length === 1 ? `Message sent to ${msgTargets[0]!.name}` : `Message sent to ${msgTargets.length} queuers`);
                  setMsgTargets(null); setMsgText("");
                  if (queueOriginId) getQueueBoard(queueOriginId).then((b: any) => setQBoard(b ?? null)).catch(() => {});
                } catch (e) { showToast(`Couldn't send — ${e instanceof Error ? e.message : String(e)}`); }
                finally { setMsgSending(false); }
              }} style={{ ...PRIMARY, opacity: msgSending || !msgText.trim() ? 0.55 : 1 }}>{msgSending ? "Sending…" : "Send"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {evMsg ? (
        <div onClick={() => setEvMsg(null)} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={eat} style={{ width: 460, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, padding: 20, display: "flex", flexDirection: "column", gap: 12, boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
            <div>
              <div style={{ fontSize: 15.5, fontWeight: 800 }}>{evMsg.tierId ? `Message ${liveTickets.find((x) => x.id === evMsg.tierId)?.name ?? "this ticket type"}` : "Message everyone registered"}</div>
              <div style={{ fontSize: 11.5, color: "#697180", marginTop: 4, lineHeight: 1.5 }}>{evMsgCount} registrant{evMsgCount === 1 ? "" : "s"}. Queues through the event's live-messages API — the toast reports the server's receipt, not an assumption.</div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {(["email", "sms"] as const).map((c) => (
                <button key={c} onClick={() => setEvMsgChannel(c)} style={{ padding: "5px 13px", borderRadius: 999, border: `1px solid ${evMsgChannel === c ? "#5142C7" : "#E6E8EC"}`, background: evMsgChannel === c ? "#F6F2FE" : "#FFFFFF", color: evMsgChannel === c ? "#5142C7" : "#333333", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT, textTransform: "uppercase" }}>{c}</button>
              ))}
            </div>
            {evMsgChannel === "email" ? <input value={evMsgSubject} onChange={(e) => setEvMsgSubject(e.target.value)} placeholder="Subject" style={INPUT} /> : null}
            <textarea value={evMsgText} onChange={(e) => setEvMsgText(e.target.value)} rows={3} autoFocus placeholder={evMsgChannel === "sms" ? "SMS · keep it under 160 characters." : "Doors open at 5:30 — bring your student ID."} style={{ ...INPUT, resize: "vertical" as const, minHeight: 70 }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setEvMsg(null)} style={GHOST}>Cancel</button>
              <button disabled={evMsgSending || !evMsgText.trim()} onClick={() => { void sendEventMessage(); }} style={{ ...PRIMARY, opacity: evMsgSending || !evMsgText.trim() ? 0.55 : 1 }}>{evMsgSending ? "Queuing…" : `Send to ${evMsgCount}`}</button>
            </div>
          </div>
        </div>
      ) : null}

      {ui.typesPicker ? (
        <div onClick={() => set({ typesPicker: false })} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={eat} style={{ width: 540, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, overflow: "hidden", boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 22px", borderBottom: "1px solid #E6E8EC" }}>
              <span style={glyphBox("#FDEBF3", "#D61A55", 32, 14)}>◆</span>
              <div><div style={{ fontSize: 14.5, fontWeight: 800 }}>Ticket types</div><div style={{ fontSize: 11, color: "#697180" }}>Ticket types library — pick which ones this event offers</div></div>
              <span style={{ flex: 1 }} />
              <button onClick={() => set({ typesPicker: false })} style={{ width: 28, height: 28, border: "none", background: "transparent", fontSize: 15, color: "#697180", cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: "14px 22px", maxHeight: "56vh", overflow: "auto" }}>
              {/* The one picker (2026-09-02): ticket types grouped by their own
                  category, searchable, and ordered — the order here is the order
                  the Tickets page lists them in. */}
              <CatalogPicker
                dense
                items={(allTicketTypes as any[]).filter((t) => t.status !== "archived").map((t) => ({
                  id: t.id, name: t.name, group: (t.category || t.groupName || "") || null,
                  meta: [t.kind === "free" ? "Free" : tgMoney(t.price), t.desc || null],
                  keywords: [t.desc ?? "", t.category ?? ""].join(" "),
                }))}
                groups={[...new Set((allTicketTypes as any[]).map((t) => (t.category || t.groupName || "")).filter(Boolean))].map((id, i) => ({ id: String(id), name: String(id), hue: ["#D61A55", "#5142C7", "#0AA396", "#916D0F"][i % 4]! }))}
                value={selTypeIds.map((id) => ({ type: "item" as const, id }))}
                onChange={(next) => {
                  const ids = resolveSelection(next, (allTicketTypes as any[]).map((t) => ({ id: t.id, name: t.name, group: (t.category || t.groupName || "") || null }))).map((it) => it.id);
                  const nextCfgs: Record<string, { qty: number }> = {};
                  ids.forEach((id) => { nextCfgs[id] = (typeCfgs as any)[id] ?? { qty: 0 }; });
                  patchRecord((x) => ({ ...x, ticketTypeIds: ids, ticketTypeConfigs: nextCfgs }));
                  if (eventOriginId) patchEventTickets(eventOriginId, { ticketTypeIds: ids, ticketTypeConfigs: nextCfgs });
                }}
                sourceLabel="Ticket types library"
                selectedLabel="This event offers"
                noun="ticket type"
                searchPlaceholder="Search ticket types…"
                emptySource="Nothing in the ticket types library yet."
                emptySelection="Pick the ticket types this event offers — allocation is set per event on the Tickets tab." />
              {!(allTicketTypes as any[]).filter((t) => t.status !== "archived").length ? (
                <div style={{ marginTop: 10, textAlign: "center" }}><button onClick={() => { set({ typesPicker: false }); nav.go("ticket-types"); }} style={GHOST}>Open the ticket types library →</button></div>
              ) : null}
              <div style={{ ...NOTE, fontSize: 11, marginTop: 10 }}>The event references each type by id — allocation is set per event on the Tickets tab, everything else stays in the library.</div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "14px 22px", borderTop: "1px solid #E6E8EC" }}>
              <button onClick={() => { set({ typesPicker: false }); nav.go("ticket-types"); }} style={{ ...GHOST, border: "none", background: "transparent", color: "#5142C7", fontWeight: 700 }}>Open the full library →</button>
              <button onClick={() => set({ typesPicker: false })} style={PRIMARY}>Done</button>
            </div>
          </div>
        </div>
      ) : null}

      {ui.lib ? (() => {
        const m = LIB_META[ui.lib]!;
        const list = libRows(ui.lib);
        return (
          <div onClick={() => set({ lib: null })} style={{ position: "fixed", inset: 0, background: "rgba(31,36,48,0.45)", zIndex: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div onClick={eat} style={{ width: 540, maxWidth: "92vw", background: "#FFFFFF", borderRadius: 14, overflow: "hidden", boxShadow: "0 18px 50px rgba(20,19,31,0.3)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 22px", borderBottom: "1px solid #E6E8EC" }}>
                <span style={glyphBox(m.tint, m.color, 32, 14)}>{m.glyph}</span>
                <div><div style={{ fontSize: 14.5, fontWeight: 800 }}>{m.title}</div><div style={{ fontSize: 11, color: "#697180" }}>{m.lib} — pick what this journey references</div></div>
                <span style={{ flex: 1 }} />
                <button onClick={() => set({ lib: null })} style={{ width: 28, height: 28, border: "none", background: "transparent", fontSize: 15, color: "#697180", cursor: "pointer" }}>✕</button>
              </div>
              <div style={{ padding: "14px 22px", maxHeight: "56vh", overflow: "auto" }}>
                {list.length ? list.map((r) => (
                  <div key={r.id} onClick={() => { r.pick(); set({ lib: null }); }} style={{ display: "flex", alignItems: "center", gap: 10, border: `1px solid ${r.on ? "#5142C7" : "#E6E8EC"}`, background: r.on ? "#FCFBFF" : "#FFFFFF", borderRadius: 8, padding: "10px 12px", marginBottom: 6, cursor: "pointer" }}>
                    <span style={{ width: 15, height: 15, borderRadius: 999, border: `1px solid ${r.on ? "#5142C7" : "#D1D4DC"}`, background: r.on ? "#5142C7" : "#FFFFFF", color: "#FFFFFF", fontSize: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{r.on ? "✓" : ""}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 12.5, fontWeight: 700 }}>{r.name}</span>
                      <span style={{ display: "block", fontSize: 10.5, color: "#697180" }}>{r.meta}</span>
                    </span>
                    <a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); r.edit(); }} style={{ fontSize: 11, fontWeight: 700, flex: "none" }}>Edit →</a>
                  </div>
                )) : (
                  <div style={{ border: "1px dashed #D1D4DC", borderRadius: 8, padding: "22px 12px", textAlign: "center", fontSize: 11.5, color: "#697180" }}>
                    Nothing in the {m.lib.toLowerCase()} yet.
                    <div style={{ marginTop: 10 }}><button onClick={() => goRoute(m.route)} style={GHOST}>Open the {m.lib.toLowerCase()} →</button></div>
                  </div>
                )}
                <div style={{ ...NOTE, fontSize: 11, marginTop: 10 }}>This journey points at the record — edit it in its own screen and the change shows here.</div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "14px 22px", borderTop: "1px solid #E6E8EC" }}>
                <button onClick={() => goRoute(m.route)} style={{ ...GHOST, border: "none", background: "transparent", color: "#5142C7", fontWeight: 700 }}>Open the full library →</button>
                <button onClick={() => set({ lib: null })} style={PRIMARY}>Done</button>
              </div>
            </div>
          </div>
        );
      })() : null}

      {toast ? <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 40, background: "#1F2430", color: "#FFFFFF", fontSize: 12.5, fontWeight: 600, borderRadius: 10, padding: "11px 20px", boxShadow: "0 8px 24px rgba(20,19,31,0.3)" }}>{toast}</div> : null}
    </>
  );


  const onestop = null;
  void onestop;

  return shell(<>
    {ui.route === "home" || !D ? home : ui.route === "designer" ? designer : experience ?? home}
    {modals}
  </>);
}
